import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { AlertCircle, AlertTriangle, Sparkles, RefreshCw, Plus, CheckSquare, Check, X, Menu, Sun, Moon, Settings, ArrowLeft, FileText, Save, Loader2, Send, ChevronDown, CheckCircle2, HelpCircle, BookOpen, Download } from 'lucide-react';
import { HilfeLink } from './HilfeLink';
import { DynamicListField } from './DynamicListField';
import { useDynamicList } from '../hooks/useDynamicList';
import { CustomerSelector } from './CustomerSelector';
import { TemplateSelector } from './TemplateSelector';
import type { Template } from './TemplateSelector';
import ApplyTemplateDialog from './ApplyTemplateDialog';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import SaveAsTemplateModal from './SaveAsTemplateModal';
import { Section1Cards } from './Section1Cards';
import { Section2Cards } from './Section2Cards';
import { Section4Cards } from './Section4Cards';
import { DocumentArea } from './customer-detail/DocumentArea';
import { VnButton } from './customer-detail/VnButton';
import { hatBerichtPdf, hatVerwendungsnachweis, naechsterEditorSchritt, darfNeuErstellen } from '../lib/dokumentPruefung';
import { fetchFileBlob, deleteFile, downloadVorgangZip, sendVnForSigning, prepareSigningReport, prepareSigningVn } from '../lib/vorgangApi';
import type { Customer } from './CustomerManagement';
import { WzSearch } from './WzSearch';
import RichTextEditor from './RichTextEditor';
import { PromptPopup } from './PromptPopup';
import { GeneratingTipsPopup } from './GeneratingTipsPopup';
import { DateTextInput } from './DateTextInput';
import { getDefaultPromptForCard, buildContextPreview, validateInputs } from '../lib/promptBuilder';
import { resolveBeratungsthema } from '../lib/beratungsthema';
import { collectReportData, exportPdf, getPdfBytes, exportDocx, exportTxt, buildReportFilename, buildZeitangabenHtml } from '../lib/export';
import { api } from '../lib/api';
import { API_BASE } from '../lib/apiBase';
import { generateV2, suggestBulk, formatGenerationError, refineItem, cancelGeneration } from '../lib/generationClient';
import { generationLimitsApi, type GenAction } from '../lib/generationLimitsApi';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from './admin/ConfirmDialog';
import ReportAnalysisModal from './report-analysis/ReportAnalysisModal';
import { type SigningPreview } from './SigningPreviewDialog';
import SigningEmailReview from './SigningEmailReview';
import { Section2Analysis } from './sections/Section2Analysis';
import { Section3Execution } from './sections/Section3Execution';
import { Section4Weaknesses } from './sections/Section4Weaknesses';
import { migrateSchwachstelle, type Schwachstelle } from '../lib/section4Schwachstelle';
import { Section5ActionPlan } from './sections/Section5ActionPlan';
import { migrateMassnahme, type Massnahme } from '../lib/section5Massnahme';
import { generateId } from '../lib/generateId';
import { Section6Principles } from './sections/Section6Principles';
import { Section7Confirmation } from './sections/Section7Confirmation';
import { ESFQuestionnaire } from './ESFQuestionnaire';
import { PruefDokumentKarte } from './PruefDokumentKarte';
import type { ExtractedFields } from '../lib/documentExtractionApi';
import { htmlToPlainText, isEmptyContent } from '../lib/richText';

// Bidirectional customer field mapping: editor formData key -> customer API field name
const CUSTOMER_FIELD_MAP: Record<string, string> = {
  'org-name': 'name', email: 'email', telefon: 'phone',
  street: 'address', hausnummer: 'hausnummer', plz: 'plz', ort: 'ort', rechtsform: 'rechtsform',
  wirtschaftszweig: 'wirtschaftszweig', gruendung: 'gruendung',
  mitarbeiterzahl: 'mitarbeiterzahl', website: 'website',
  anrede: 'anrede', vorname: 'vorname', nachname: 'nachname',
  bilanzsumme: 'jahresbilanzsumme', jahresumsatz: 'jahresumsatz',
  geschaeftsgegenstand: 'geschaeftsgegenstand',
};

// Felder, die NUR aus den Stammdaten gelesen werden dürfen — der Editor befüllt sie vor,
// schreibt sie aber nie zurück.
//
// „Angemeldeter Geschäftsgegenstand" ist der Wortlaut aus Gewerbeanmeldung/Handelsregister
// und geht so in den BAFA-Antrag und den Verwendungsnachweis. Im Editor steht im selben Feld
// dagegen ein frei formulierter Beschreibungstext (KI-Vorschlag, Website-Analyse,
// Dokumenten-Extraktion). Über den bidirektionalen Sync hat dieser Text den amtlichen
// Wortlaut bei 9 Kunden überschrieben (z.B. „IT-Dienstleistungen" → dreisätziger Fließtext).
// Der Editortext bleibt am Bericht (section1-formData) erhalten, die Stammdaten nicht mehr.
const CUSTOMER_READONLY_FIELDS = new Set(['geschaeftsgegenstand']);

const METADATA_KEYS = ['aktenzeichen', 'startDate', 'endDate', 'berichtstitel'];

interface EditorWorkspaceProps {
  sectionId: string;
  onSectionChange: (sectionId: string) => void;
  onGenerateDraft: () => void;
  onRegenerateDraft: () => void;
  onInsertBlock: () => void;
  onCompletenessCheck: () => void;
  darkMode: boolean;
  onToggleDarkMode?: () => void;
  onNavigateSettings?: () => void;
  onNavigateBack?: () => void;
  initialReport?: any;
}

interface SectionData {
  title: string;
  helperText: string;
  cards?: Array<{
    id: string;
    title: string;
    listKey?: string;
    fields?: Array<{
      id: string;
      label: string;
      placeholder?: string;
      type: 'text' | 'textarea' | 'email' | 'number' | 'date' | 'select' | 'wz-search' | 'divider';
      options?: string[];
      rows?: number;
      fullWidth?: boolean;
      defaultValue?: string;
    }>;
  }>;
  subsections?: Array<{
    title: string;
    fields?: Array<{
      id: string;
      label: string;
      placeholder?: string;
      type: 'text' | 'textarea' | 'email' | 'number' | 'date' | 'select' | 'wz-search' | 'divider';
      options?: string[];
      rows?: number;
      fullWidth?: boolean;
    }>;
    subsections?: Array<{
      title: string;
      fields: Array<{
        id: string;
        label: string;
        placeholder?: string;
        type: 'text' | 'textarea' | 'email' | 'number' | 'date' | 'select' | 'wz-search' | 'divider';
        options?: string[];
        rows?: number;
        fullWidth?: boolean;
      }>;
    }>;
  }>;
  fields?: Array<{
    id: string;
    label: string;
    placeholder: string;
    type: 'text' | 'textarea';
  }>;
}

const sectionConfigs: Record<string, SectionData> = {
  section1: {
    title: '1. Darstellung des beratenen Unternehmens und Benennung des Beratungsauftrags',
    helperText: 'Stellen Sie wesentliche Informationen über das beratene Unternehmen dar und definieren Sie Umfang und Zielsetzung des Beratungsauftrags.',
    cards: [
      {
        id: 'card-1-1',
        title: '1.1 Beratungsauftrag und Grund / Anlass',
        fields: [
          { id: 'consulting-purpose', label: 'Beratungsauftrag und Grund / Anlass', placeholder: 'Beschreiben Sie den Beratungsauftrag und den Grund bzw. Anlass...', type: 'textarea', rows: 6, fullWidth: true },
        ],
      },
      {
        id: 'card-1-2',
        title: '1.2 Angaben zum Unternehmen',
        fields: [
          { id: 'org-name', label: 'Name', placeholder: '', type: 'text' },
          { id: 'rechtsform', label: 'Rechtsform', placeholder: 'z.B. GmbH, AG, UG', type: 'text' },
          { id: 'street', label: 'Straße', placeholder: 'z.B. Musterstraße', type: 'text' },
          { id: 'hausnummer', label: 'Nr.', placeholder: 'z.B. 123', type: 'text' },
          { id: 'plz', label: 'PLZ', placeholder: '', type: 'text' },
          { id: 'ort', label: 'Ort', placeholder: '', type: 'text' },
          { id: 'website', label: 'Website', placeholder: 'https://', type: 'text' },
          { id: 'land', label: 'Land', placeholder: 'Deutschland', type: 'text' },
          { id: 'divider-ansprechpartner', label: 'Ansprechperson', type: 'divider' },
          { id: 'anrede', label: 'Anrede', type: 'select', options: ['Herr', 'Frau', 'Divers'] },
          { id: 'vorname', label: 'Vorname', placeholder: '', type: 'text' },
          { id: 'nachname', label: 'Nachname', placeholder: '', type: 'text' },
          { id: 'telefon', label: 'Telefon', placeholder: '', type: 'text' },
          { id: 'email', label: 'E-Mail', placeholder: '', type: 'email' },
        ],
      },
      {
        id: 'card-1-3',
        title: '1.3 Unternehmensdaten',
        fields: [
          { id: 'mitarbeiterzahl', label: 'Mitarbeiterzahl', placeholder: '', type: 'number' },
          { id: 'gruendung', label: 'Gründungsdatum', placeholder: 'tt.mm.jjjj', type: 'date-text' },
          { id: 'bilanzsumme', label: 'Jahresbilanzsumme', placeholder: 'in Euro', type: 'text', fullWidth: true },
          { id: 'jahresumsatz', label: 'Jahresumsatz', placeholder: 'in Euro', type: 'text', fullWidth: true },
          { id: 'wirtschaftszweig', label: 'Wirtschaftszweigklassifikation', placeholder: 'z.B. WZ 62.01', type: 'wz-search', fullWidth: true },
        ],
      },
      {
        id: 'card-1-4',
        title: '1.4 Geschäftsgegenstand',
        fields: [
          { id: 'geschaeftsgegenstand', label: 'Geschäftsgegenstand', placeholder: 'Beschreiben Sie den Geschäftsgegenstand des Unternehmens...', type: 'textarea', rows: 6, fullWidth: true },
        ],
      },
      {
        id: 'card-1-5',
        title: '1.5 Zeitangaben',
        fields: [],
      },
    ],
  },
  analysis: {
    title: '2. Analyse der Unternehmenssituation unter Beachtung des Beratungsauftrages und der mit der Beratung verbundenen Zielsetzung',
    helperText: 'Dokumentieren Sie Ihre Erkenntnisse aus Recherchen, Datenanalysen und Gesprächen mit Beteiligten.',
    cards: [
      { id: 'card-2-1', title: '2.1 Ist-Zustand', listKey: 'istZustand', instruction: 'Beschreiben Sie die aktuelle Ausgangssituation in 4 Stichpunkten. Diese bilden die Grundlage für Stärken (2.2) und Schwächen (2.3). Bei Bedarf Vorschläge generieren oder manuell ergänzen.', maxEntries: 10 },
      { id: 'card-2-2', title: '2.2 Stärken und Potenziale', listKey: 'staerken', instruction: 'Beschreiben Sie vorhandene Stärken und Potenziale. Neben dem Ist-Zustand (2.1) können auch branchenübliche und strategisch plausible Stärken ergänzt werden.', maxEntries: 10 },
      { id: 'card-2-3', title: '2.3 Schwächen und Handlungsbedarfe', listKey: 'schwaechen', instruction: 'Beschreiben Sie Schwächen und Handlungsbedarfe im Beratungskontext. Neben dem Ist-Zustand können auch branchenübliche Defizite ergänzt werden.', maxEntries: 10 },
    ],
    fields: [
      { id: 'current-state', label: 'Ist-Zustand', placeholder: 'Beschreiben Sie die aktuelle Situation', type: 'textarea' },
      { id: 'key-findings', label: 'Stärken des Unternehmens', placeholder: 'Was sind die wichtigsten Feststellungen?', type: 'textarea' },
      { id: 'data-sources', label: 'Schwächen und Handlungsbedarfe', placeholder: 'Welche Daten wurden analysiert?', type: 'text' },
    ],
  },
  execution: {
    title: '3. Angaben zur Auftragsdurchführung und Erläuterung des Vorgehens',
    helperText: 'Beschreiben Sie die Methodik und Vorgehensweise während der Beratungsleistung.',
    fields: [],
  },
  weaknesses: {
    title: '4. Benennung der einzelnen ermittelten Schwachstellen und Ursachen des Beratungsbedarfs',
    helperText: 'Identifizieren Sie Herausforderungen, Risiken und Verbesserungspotenziale.',
    cards: [
      { id: 'card-4-1', title: '4.1 Ermittelte Schwachstellen', listKey: 'schwachstellen' },
      { id: 'card-4-2', title: '4.2 Fragestellungen zur Strukturierung', listKey: 'fragestellungen' },
    ],
    fields: [
      { id: 'identified-issues', label: 'Identifizierte Problembereiche', placeholder: 'Welche Schwachstellen wurden festgestellt?', type: 'textarea' },
      { id: 'risk-assessment', label: 'Risikobewertung', placeholder: 'Welche Risiken bestehen?', type: 'textarea' },
      { id: 'impact', label: 'Auswirkungen', placeholder: 'Wie wirken sich die Schwachstellen auf das Unternehmen aus?', type: 'textarea' },
      { id: 'urgency', label: 'Dringlichkeit', placeholder: 'Welche Probleme erfordern sofortiges Handeln?', type: 'textarea' },
    ],
  },
  'action-plan': {
    title: '5. Darstellung des detaillierten Maßnahmeplans zur Überwindung der ermittelten Schwachstellen',
    helperText: 'Geben Sie konkrete Handlungsempfehlungen und Umsetzungsschritte.',
    fields: [
      { id: 'recommendations', label: 'Empfohlene Maßnahmen', placeholder: 'Welche Maßnahmen sollten ergriffen werden?', type: 'textarea' },
      { id: 'priority', label: 'Priorisierung', placeholder: 'Welche Maßnahmen sind am wichtigsten?', type: 'textarea' },
      { id: 'resources', label: 'Erforderliche Ressourcen', placeholder: 'Was wird für die Umsetzung benötigt?', type: 'textarea' },
    ],
  },
  principles: {
    title: '6. Berücksichtigung der bereichsübergreifenden Grundsätze der Gleichstellung, Antidiskriminierung und ökologischen Nachhaltigkeit',
    helperText: 'Erläutern Sie, wie die bereichsübergreifenden Grundsätze in der Beratung berücksichtigt wurden.',
    fields: [
      { id: 'equality', label: 'Gleichstellung', placeholder: 'Wie wurden Aspekte der Gleichstellung berücksichtigt?', type: 'textarea' },
      { id: 'anti-discrimination', label: 'Antidiskriminierung', placeholder: 'Welche Maßnahmen zur Antidiskriminierung wurden beachtet?', type: 'textarea' },
      { id: 'sustainability', label: 'Ökologische Nachhaltigkeit', placeholder: 'Wie wurden Nachhaltigkeitsaspekte einbezogen?', type: 'textarea' },
    ],
  },
  confirmation: {
    title: '7. Bestätigung der Beratungsleistung und Eigenständigkeit der Berichterstellung',
    helperText: 'Bestätigen Sie die ordnungsgemäße Durchführung der Beratung und die eigenständige Erstellung des Berichts.',
    fields: [
      { id: 'consultant-confirmation', label: 'Beraterbestätigung', placeholder: 'Bestätigung zur ordnungsgemäßen Durchführung', type: 'textarea' },
      { id: 'independence-declaration', label: 'Eigenständigkeitserklärung', placeholder: 'Erklärung zur eigenständigen Berichterstellung', type: 'textarea' },
      { id: 'consultant-signature', label: 'Unterschrift Berater', placeholder: 'Name und Datum', type: 'text' },
    ],
  },
};

function hasRealContent(html: string | undefined | null): boolean {
  if (!html) return false;
  const stripped = html.replace(/<[^>]*>/g, '').trim();
  return stripped.length > 0;
}

type SigningDocFile = { id: number; name: string; type: string; label: string };

/** Liste der Dokumente, die zur Unterschrift an den Kunden gehen würden
 *  (Bericht + benötigte BAFA-Formulare) — zum Durchsehen VOR dem Versand.
 *  Wird ausgeblendet, sobald eine Unterschriftsanfrage verschickt wurde
 *  (signingStatus !== 'none'). */
function SigningDocsToReview({ reportId, refreshKey, signingStatus, onChanged }: { reportId: number; refreshKey?: number; signingStatus: 'none' | 'pending' | 'completed'; onChanged?: () => void }) {
  const [files, setFiles] = useState<SigningDocFile[]>([]);
  const [doc, setDoc] = useState<{ url: string; name: string } | null>(null);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const load = useCallback(() => {
    if (!reportId) { setFiles([]); return; }
    api.get<{ files?: SigningDocFile[] }>(`/api/reports/${reportId}/signing-preview`)
      .then(p => setFiles(Array.isArray(p?.files) ? p.files : []))
      .catch(() => setFiles([]));
  }, [reportId]);
  useEffect(() => { load(); }, [load, refreshKey, signingStatus]);

  if (signingStatus !== 'none') return null; // nach Versand ausblenden
  if (files.length === 0) return null;

  const open = async (f: SigningDocFile) => {
    setLoadingId(f.id);
    try { const { url } = await fetchFileBlob(f.id); setDoc({ url, name: f.name }); }
    catch { /* ignore */ }
    finally { setLoadingId(null); }
  };
  const close = () => setDoc(d => { if (d) URL.revokeObjectURL(d.url); return null; });
  const remove = async (f: SigningDocFile) => {
    setDeletingId(f.id);
    try { await deleteFile(f.id); load(); onChanged?.(); }
    catch { /* ignore */ }
    finally { setDeletingId(null); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
      <p className="text-sm font-semibold text-slate-900 dark:text-white">Dokumente zum Unterzeichnen</p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Vor dem Versand an den Kunden prüfen. Nach dem Verschicken wird diese Liste ausgeblendet.</p>
      <ul className="space-y-1.5">
        {files.map(f => (
          <li key={f.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2 min-w-0 text-slate-800 dark:text-slate-200">
              <svg className="w-4 h-4 flex-shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              <span className="truncate" title={f.name}>{f.label}</span>
            </span>
            <span className="flex-shrink-0 flex items-center gap-3">
              <button onClick={() => open(f)} disabled={loadingId === f.id} className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 disabled:opacity-50">
                {loadingId === f.id ? 'Lädt…' : 'Ansehen'}
              </button>
              <button onClick={() => remove(f)} disabled={deletingId === f.id} title="Löschen" className="text-slate-400 hover:text-red-600 disabled:opacity-50">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
            </span>
          </li>
        ))}
      </ul>
      {doc && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-black/70 p-4" onClick={close}>
          <div className="flex items-center justify-between gap-3 mb-2 text-white shrink-0" onClick={e => e.stopPropagation()}>
            <span className="text-sm font-medium truncate" title={doc.name}>{doc.name}</span>
            <button onClick={close} className="px-3 py-1.5 text-sm rounded-lg bg-white/15 hover:bg-white/25">Schließen</button>
          </div>
          <div className="flex-1 min-h-0" onClick={e => e.stopPropagation()}>
            <iframe src={doc.url} title={doc.name} className="w-full h-full rounded-lg bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}

function EditorDocuments({ reportId, refreshKey, signingStatus, onChanged }: { reportId: number; refreshKey?: number; signingStatus: 'none' | 'pending' | 'completed'; onChanged?: () => void }) {
  const [meta, setMeta] = useState<{ vorgangId: number; customerId: number; customerEmail?: string | null } | null>(null);
  const [zipping, setZipping] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Touch the (redirecting) files endpoint first — this lazily creates+links a Vorgang if the report has none.
      await api.get(`/api/reports/${reportId}/files`).catch(() => {});
      const r = await api.get<any>(`/api/reports/${reportId}`);
      if (!cancelled && r?.vorgangId && r?.customerId) {
        setMeta({ vorgangId: r.vorgangId, customerId: r.customerId, customerEmail: r.customer?.email ?? null });
      }
    })();
    return () => { cancelled = true; };
  }, [reportId]);
  if (!meta) return null;
  const downloadAll = async () => {
    setZipping(true);
    try { await downloadVorgangZip(meta.vorgangId); }
    catch (e: any) { alert(e?.message || 'Download fehlgeschlagen'); }
    finally { setZipping(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
      <p className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Dokumente</p>
      <DocumentArea vorgangId={meta.vorgangId} customerId={meta.customerId} refreshKey={refreshKey} customerEmail={meta.customerEmail} />
      <SigningDocsToReview reportId={reportId} refreshKey={refreshKey} signingStatus={signingStatus} onChanged={onChanged} />
      <button onClick={downloadAll} disabled={zipping}
        title="Alle PDF-Dokumente dieses Vorgangs als ZIP herunterladen"
        className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors">
        {zipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        {zipping ? 'ZIP wird erstellt…' : 'Alle Dokumente herunterladen'}
      </button>
    </div>
  );
}

export function EditorWorkspace({ sectionId, onSectionChange, onGenerateDraft, onRegenerateDraft, onInsertBlock, onCompletenessCheck, darkMode, onToggleDarkMode, onNavigateSettings, onNavigateBack, initialReport }: EditorWorkspaceProps) {
  const { user } = useAuth();
  // Admin can toggle prompt editor visibility (stored per user in localStorage)
  const showPromptEditorKey = user ? `showPromptEditor_${user.id}` : '';
  const getShowPromptEditor = () => {
    if (!user?.isAdmin) return false;
    try {
      const val = localStorage.getItem(showPromptEditorKey);
      return val === null ? true : val === 'true'; // Default: true for admins
    } catch { return true; }
  };
  const [aiDraft, setAiDraft] = useState('');
  const [finalText, setFinalText] = useState('');
  const [showAiDraft, setShowAiDraft] = useState(false);
  const [activeSection, setActiveSection] = useState(sectionId);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [titleMode, setTitleMode] = useState<'template' | 'custom'>('custom');
  const { status: subStatus } = useSubscriptionStatus();
  const templatesActive = !!subStatus?.features?.templates;
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [esfWarnOpen, setEsfWarnOpen] = useState(false);
  // Bericht bereits unterschrieben + jetzt erneut gespeichert (Kundenwunsch, 2026-10-12):
  // vor dem Speichern fragen, ob die unterschriebene Fassung im Vorgang mitaktualisiert
  // werden soll (Unterschriftsseite bleibt erhalten) oder nur der Editor-Entwurf gespeichert wird.
  const [signedUpdateConfirmOpen, setSignedUpdateConfirmOpen] = useState(false);
  const [signedRefreshMismatch, setSignedRefreshMismatch] = useState(false);
  const [applyTemplateDialog, setApplyTemplateDialog] = useState<Template | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const isAdmin = !!user?.isAdmin;

  // Auto-save state
  const [reportId, setReportId] = useState<number | null>(null);
  const [genCounts, setGenCounts] = useState<Map<string, { count: number; limit: number | null }>>(new Map());
  const [sectionMap, setSectionMap] = useState<Record<string, number>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveErrorMsg, setSaveErrorMsg] = useState<string | null>(null);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialLoadRef = useRef(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const reportCreatingRef = useRef(false);
  const customerSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevFormDataRef = useRef<Record<string, string>>({});
  // Aufgelaufene, noch nicht an den Kundensatz gesendete Feldänderungen (Editor→Kunde).
  // Akkumuliert über mehrere Renders, damit bei schnellen Mehrfeld-Edits innerhalb
  // des Debounce-Fensters keine Änderung verloren geht.
  const pendingCustomerRef = useRef<Record<string, string>>({});
  const [fileRefreshKey, setFileRefreshKey] = useState(0);
  const [bafaGenerating, setBafaGenerating] = useState(false);
  const [bafaGenerated, setBafaGenerated] = useState(false);
  const [bafaError, setBafaError] = useState<string | null>(null);
  // „Ganzen Bericht generieren" — sequenzieller Sammel-Lauf über alle Karten.
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ i: number; total: number; label: string } | null>(null);
  const [batchSummary, setBatchSummary] = useState<{ label: string; ok: boolean; reason?: string }[] | null>(null);
  const [batchConfirm, setBatchConfirm] = useState(false);
  const jobPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [bafaWarnFields, setBafaWarnFields] = useState<string[]>([]);
  // Kundenweit bereits unterschriebene Dauer-Doks (Vollmacht/GRC/De-Minimis),
  // die NICHT erneut zur Unterschrift angefordert werden — Anzeige in BAFA-Karte.
  const [signedDauerDocs, setSignedDauerDocs] = useState<string[]>([]);
  const [overwriteConfirm, setOverwriteConfirm] = useState(false);
  // Warnung, wenn bereits eine Unterschriftsanfrage versendet/abgeschlossen ist
  // und der Nutzer trotzdem neu generieren will.
  const [signingSentWarnOpen, setSigningSentWarnOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState<string | null>(null);

  // "Erledigt"/Überschreiben spiegelt, ob BAFA-Formulare für noch ZU
  // UNTERSCHREIBENDE Typen vorliegen — gekoppelt an die "zum Unterzeichnen"-
  // Liste (signing-preview.files). Bereits signierte Dauer-Doks (skipped) und
  // der Bericht zählen NICHT, sonst käme die Überschreiben-Abfrage trotz leerer
  // Liste (z.B. übrige vollmacht.pdf einer bereits signierten Vollmacht).
  useEffect(() => {
    if (!reportId) return;
    api.get<{ files?: { type: string }[] }>(`/api/reports/${reportId}/signing-preview`)
      .then(p => setBafaGenerated(Array.isArray(p?.files) && p.files.some(f => f.type !== 'bericht')))
      .catch(() => {});
  }, [reportId, fileRefreshKey]);

  // Bereits kundenweit unterschriebene Dauer-Doks für die BAFA-Karte (Signing-
  // Preview liefert `skipped` = wird nicht erneut angefordert). Braucht keine
  // generierten Formulare.
  useEffect(() => {
    if (!reportId) { setSignedDauerDocs([]); return; }
    api.get<{ send: string[]; skipped: string[] }>(`/api/reports/${reportId}/signing-preview`)
      .then(p => setSignedDauerDocs(Array.isArray(p?.skipped) ? p.skipped : []))
      .catch(() => {});
  }, [reportId, fileRefreshKey]);
  const [signingStatus, setSigningStatus] = useState<'none' | 'pending' | 'completed'>('none');
  // Detail-Info zur Unterschriftsanfrage: Sendedatum + Soll/Ist je Dokumenttyp (Häkchen).
  const [signingInfo, setSigningInfo] = useState<{
    status: 'pending' | 'completed';
    createdAt?: string;
    /** Zeitpunkt des tatsächlichen Mailversands — maßgeblich für „Gesendet am". */
    emailSentAt?: string | null;
    bitteKontoauszug?: boolean;
    docs?: Array<{ type: string; label: string; requested: boolean; received: boolean }>;
  } | null>(null);
  useEffect(() => {
    if (reportId == null) { setSigningInfo(null); return; }
    api.get<{ status: 'pending' | 'completed'; createdAt?: string; emailSentAt?: string | null; bitteKontoauszug?: boolean; docs?: Array<{ type: string; label: string; requested: boolean; received: boolean }> } | null>(
      `/api/reports/${reportId}/signing-request`,
    )
      .then(sr => setSigningInfo(sr ? { status: sr.status, createdAt: sr.createdAt, emailSentAt: sr.emailSentAt, bitteKontoauszug: sr.bitteKontoauszug, docs: sr.docs } : null))
      .catch(() => {});
  }, [reportId, fileRefreshKey, signingStatus]);
  const [signingEmail, setSigningEmail] = useState('');
  const [signingSending, setSigningSending] = useState(false);
  const [signingError, setSigningError] = useState<string | null>(null);
  const [signingPreview, setSigningPreview] = useState<SigningPreview | null>(null);
  const [signingPreviewLoading, setSigningPreviewLoading] = useState(false);
  const [vnMeldung, setVnMeldung] = useState<string | null>(null);
  const [vnSending, setVnSending] = useState(false);
  const [emailReviewOpen, setEmailReviewOpen] = useState(false);
  /** „Es liegt noch kein Verwendungsnachweis vor" — mit Vorgang/Kunde, damit im Dialog
   *  direkt angefordert werden kann. Null = Dialog zu. */
  const [vnFehlt, setVnFehlt] = useState<{ vorgangId: number; customerId: number } | null>(null);
  const [vnPruefung, setVnPruefung] = useState(false);
  /** „Bericht in aktueller Fassung speichern?" — offen, solange die Frage aussteht. */
  const [berichtSpeichernFrage, setBerichtSpeichernFrage] = useState<{ email: string } | null>(null);
  const [berichtSpeichert, setBerichtSpeichert] = useState(false);
  /** Dokumentarten am Vorgang — entscheiden, welcher Schritt als Nächstes ansteht. */
  const [vorgangKategorien, setVorgangKategorien] = useState<string[]>([]);
  /** Ist die abgelegte PDF noch der aktuelle Text? Kommt vom Server, damit Editor und
   *  Vorgangsansicht dieselbe Auskunft geben. */
  const [fassung, setFassung] = useState<{ pdfAm: string | null; textAm: string | null; veraltet: boolean } | null>(null);
  const [emailReviewContext, setEmailReviewContext] = useState<{ mode: 'forms' | 'vn'; forceTypes?: string[]; excludeTypes?: string[]; bitteKontoauszug?: boolean }>({ mode: 'forms' });
  // Stabile Referenz für SigningEmailReview.docAuswahl: ein neues Objekt bei JEDEM Render
  // (z.B. inline im JSX) ändert die Identität, die in fetchPreview's useCallback-Deps hängt —
  // das feuert dessen Lade-Effekt bei JEDEM EditorWorkspace-Re-Render neu und flutet
  // /signing-email-preview (2026-09-22: "Zu viele Anfragen" + Vorschau bleibt bei "Lädt…" hängen).
  const emailReviewDocAuswahl = useMemo(
    () => (emailReviewContext.mode === 'vn' ? undefined : { preview: signingPreview, loading: signingPreviewLoading }),
    [emailReviewContext.mode, signingPreview, signingPreviewLoading],
  );

  // State for section1 cards - each card has its own text state
  const [section1CardTexts, setSection1CardTexts] = useState<Record<string, { aiDraft: string; finalText: string; showAiDraft: boolean; saved: boolean }>>({
    'card-1-1': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
    'card-1-2': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
    'card-1-3': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
    'card-1-4': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
    'card-1-5': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
  });

  const [section2CardTexts, setSection2CardTexts] = useState<Record<string, { aiDraft: string; finalText: string; showAiDraft: boolean; saved: boolean }>>({
    'card-2-1': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
    'card-2-2': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
    'card-2-3': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
  });

  const [section4CardTexts, setSection4CardTexts] = useState<Record<string, { aiDraft: string; finalText: string; showAiDraft: boolean; saved: boolean }>>({
    'card-4-1': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
    'card-4-2': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
  });

  // State for sections 2-7 (keys match section IDs: analysis, execution, etc.)
  // `confirmed` wird nur von 'confirmation' genutzt (explizite Bestätigung
  // durch den Nutzer, siehe getSectionProgress) — für alle anderen Keys ungenutzt.
  const [sectionStates, setSectionStates] = useState<Record<string, { aiDraft: string; finalText: string; showAiDraft: boolean; saved: boolean; confirmed?: boolean }>>({
    'analysis': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
    'execution': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
    'weaknesses': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
    'action-plan': { aiDraft: '', finalText: '', showAiDraft: false, saved: false },
    'principles': { aiDraft: '', finalText: '', showAiDraft: false, saved: false, confirmed: false },
    'confirmation': { aiDraft: '', finalText: '', showAiDraft: false, saved: false, confirmed: false },
  });
  
  // Prompt popup state
  const [promptPopupData, setPromptPopupData] = useState<{
    cardId: string;
    cardTitle: string;
    prompt: string;
    contextPreview?: string;
    formData?: Record<string, string>;
    customerData?: { name?: string; branche?: string; rechtsform?: string; ort?: string };
    warnings?: string[];
  } | null>(null);
  const [promptPopupOpen, setPromptPopupOpen] = useState(false);
  const [tipsPopupOpen, setTipsPopupOpen] = useState(false);
  const [tipsPopupError, setTipsPopupError] = useState<string | null>(null);
  const [missingFieldsError, setMissingFieldsError] = useState<{ cardId: string; warnings: string[] } | null>(null);

  // Auto-save fade timer
  const autoSaveFadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Bulk suggest state
  const [bulkSuggesting, setBulkSuggesting] = useState(false);

  // Sections with multiple sub-cards manage collapse at card level, not section level
  const MULTI_CARD_SECTIONS = new Set(['section1', 'analysis', 'weaknesses']);

  // Collapse state — Default: alles zugeklappt (Fokusmodus ist ON by default).
  // Nach dem Datenload wird der erste unvollstaendige Block aufgeklappt
  // (ohne Scroll, sonst springt's beim Page-Load).
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    section1: false,
    analysis: false,
    execution: true,
    weaknesses: false,
    'action-plan': true,
    principles: true,
    confirmation: true,
  });

  const [collapsedSection1Cards, setCollapsedSection1Cards] = useState<Record<string, boolean>>({
    'card-1-1': true, 'card-1-2': true, 'card-1-3': true, 'card-1-4': true, 'card-1-5': true,
  });
  const [collapsedSection2Cards, setCollapsedSection2Cards] = useState<Record<string, boolean>>({
    'card-2-1': true, 'card-2-2': true, 'card-2-3': true,
  });
  const [collapsedSection4Cards, setCollapsedSection4Cards] = useState<Record<string, boolean>>({
    'card-4-1': true, 'card-4-2': true,
  });

  // Section IDs that have their OWN inner collapse (single-content sections).
  // Multi-card sections (section1, analysis, weaknesses) are always open at the
  // section level and only their inner cards collapse.
  const SINGLE_COLLAPSE_SECTIONS = ['execution', 'action-plan', 'principles', 'confirmation'] as const;
  type SingleCollapseId = typeof SINGLE_COLLAPSE_SECTIONS[number];

  // Workflow sequence used by Fokusmodus to pick the next card to work on.
  const CARD_SEQUENCE = [
    'card-1-1', 'card-1-2', 'card-1-3', 'card-1-4', 'card-1-5',
    'card-2-1', 'card-2-2', 'card-2-3',
    'execution',
    'card-4-1', 'card-4-2',
    'action-plan',
  ] as const;

  // Explicit Fokusmodus state — controls whether finishing a card auto-closes
  // it (focus on one block at a time) and what the global toggle does.
  // Fokusmodus by default ON — User sieht nur den naechsten Block. Toggle off
  // klappt anschliessend alles auf.
  const [focusMode, setFocusMode] = useState(true);

  // Bericht analysieren & optimieren (Revision-Flow)
  const [analysisOpen, setAnalysisOpen] = useState(false);
  /** Übernimmt überarbeitete Block-Texte in den Editor-State (Backend hat bereits gespeichert). */
  const handleRevisionAccepted = (items: Array<{ cardId: string; text: string }>) => {
    for (const { cardId, text } of items) {
      const patch = (prev: Record<string, { aiDraft: string; finalText: string; showAiDraft: boolean; saved: boolean }>) => ({
        ...prev,
        [cardId]: {
          ...(prev[cardId] || { aiDraft: '', finalText: '', showAiDraft: false, saved: false }),
          finalText: text, aiDraft: text, showAiDraft: false, saved: true,
        },
      });
      if (cardId.startsWith('card-1-')) setSection1CardTexts(patch);
      else if (cardId.startsWith('card-2-')) setSection2CardTexts(patch);
      else if (cardId.startsWith('card-4-')) setSection4CardTexts(patch);
      else setSectionStates(patch); // execution, action-plan
    }
  };
  const [focusInfoOpen, setFocusInfoOpen] = useState(false);
  const [preis, setPreis] = useState<string>('');
  const [mwstEnabled, setMwstEnabled] = useState<boolean>(false);
  const [foerderRate, setFoerderRate] = useState<50 | 80>(50);

  // For the status-badge UI: a closure over the latest completion checker so
  // child components can ask "is this card considered filled?".
  const isCardComplete = (cardId: string): boolean => cardCompletionRef.current(cardId);

  const findNextIncompleteId = (): string | null => {
    for (const id of CARD_SEQUENCE) {
      if (!cardCompletionRef.current(id)) return id;
    }
    return null;
  };

  // Helper: which collection holds the collapsed-state for a given id.
  const setCollapseFor = (id: string, value: boolean) => {
    if (id.startsWith('card-1-')) setCollapsedSection1Cards(p => ({ ...p, [id]: value }));
    else if (id.startsWith('card-2-')) setCollapsedSection2Cards(p => ({ ...p, [id]: value }));
    else if (id.startsWith('card-4-')) setCollapsedSection4Cards(p => ({ ...p, [id]: value }));
    else setCollapsedSections(p => ({ ...p, [id]: value }));
  };

  const scrollToWorkable = (id: string) => {
    // Warte bis die grid-rows-Transition (300ms) sicher fertig ist und
    // benutze rAF-Doppel-Frame fuer einen stabilen Layout-Pass — sonst
    // springt scrollIntoView auf eine veraltete Position.
    setTimeout(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const el = (SINGLE_COLLAPSE_SECTIONS as readonly string[]).includes(id)
          ? sectionRefs.current[id]
          : document.querySelector(`[data-card-id="${id}"]`);
        if (el && 'scrollIntoView' in el) {
          (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }));
    }, 350);
  };

  const toggleFocusMode = () => {
    if (focusMode) {
      // Turn OFF — expand everything.
      setFocusMode(false);
      setCollapsedSection1Cards(prev => Object.fromEntries(Object.keys(prev).map(k => [k, false])));
      setCollapsedSection2Cards(prev => Object.fromEntries(Object.keys(prev).map(k => [k, false])));
      setCollapsedSection4Cards(prev => Object.fromEntries(Object.keys(prev).map(k => [k, false])));
      setCollapsedSections(prev => {
        const next = { ...prev };
        SINGLE_COLLAPSE_SECTIONS.forEach(id => { next[id] = false; });
        return next;
      });
    } else {
      // Turn ON — collapse everything, then open just the next-incomplete.
      setFocusMode(true);
      const open = findNextIncompleteId();
      setCollapsedSection1Cards(prev => Object.fromEntries(Object.keys(prev).map(k => [k, k !== open])));
      setCollapsedSection2Cards(prev => Object.fromEntries(Object.keys(prev).map(k => [k, k !== open])));
      setCollapsedSection4Cards(prev => Object.fromEntries(Object.keys(prev).map(k => [k, k !== open])));
      setCollapsedSections(prev => {
        const next = { ...prev };
        SINGLE_COLLAPSE_SECTIONS.forEach(id => { next[id] = id !== open; });
        return next;
      });
      if (open) scrollToWorkable(open);
    }
  };

  // Auto-advance: when the user leaves the last filled field of a card,
  // open the next card / section if the current one is "complete".
  // The completion / target logic is defined further below where the relevant
  // state variables are in scope; here we just hold a stable callback wrapper.
  const cardCompletionRef = useRef<(cardId: string) => boolean>(() => false);
  const cardAdvanceTargetRef = useRef<(cardId: string) => { kind: 'section1' | 'section2' | 'section4' | 'section'; id: string } | null>(() => null);
  // Tracks the most recent auto-advance so that a click on the just-opened
  // target's header (which often arrives right after the blur that triggered
  // the auto-open) doesn't toggle the card back closed.
  const lastAutoAdvanceRef = useRef<{ id: string; ts: number } | null>(null);
  const wasJustAutoOpened = (id: string) => {
    const e = lastAutoAdvanceRef.current;
    return !!e && e.id === id && Date.now() - e.ts < 350;
  };

  const handleCardComplete = (cardId: string) => {
    if (!cardCompletionRef.current(cardId)) return;

    if (focusMode) {
      // Fokusmodus: das NAECHSTE NOCH NICHT ausgefuellte Element aus der
      // Sequenz oeffnen (bereits fertige werden uebersprungen), aktuelles
      // schliessen und smooth dahin scrollen.
      const fromIdx = (CARD_SEQUENCE as readonly string[]).indexOf(cardId);
      let nextIncomplete: string | null = null;
      if (fromIdx >= 0) {
        for (let i = fromIdx + 1; i < CARD_SEQUENCE.length; i++) {
          const id = CARD_SEQUENCE[i];
          if (!cardCompletionRef.current(id)) { nextIncomplete = id; break; }
        }
      }
      if (nextIncomplete) {
        // Aktuelle Karte zusammenklappen (nur ein Block sichtbar) und zur
        // naechsten offenen Karte wechseln.
        setCollapseFor(cardId, true);
        setCollapseFor(nextIncomplete, false);
        lastAutoAdvanceRef.current = { id: nextIncomplete, ts: Date.now() };
        // Warte bis collapse/expand-Animationen (grid-rows transition-300)
        // fertig sind — sonst zielt scrollIntoView auf eine veraltete
        // Position und der Smooth-Scroll wirkt verloren.
        // Two-frame wait so collapse/expand transitions actually committed,
        // then scroll. We use requestAnimationFrame after the timeout to land
        // exactly when layout is stable.
        setTimeout(() => {
          requestAnimationFrame(() => requestAnimationFrame(() => {
            const el = (SINGLE_COLLAPSE_SECTIONS as readonly string[]).includes(nextIncomplete!)
              ? sectionRefs.current[nextIncomplete!]
              : document.querySelector(`[data-card-id="${nextIncomplete}"]`);
            if (el && 'scrollIntoView' in el) {
              (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }));
        }, 350);
      }
      return;
    }

    // Non-Fokus: einfach den unmittelbar naechsten Block aufklappen
    // (User sieht so die "Kette" weiter), kein Scroll, kein Close.
    const target = cardAdvanceTargetRef.current(cardId);
    if (!target) return;
    if (target.kind === 'section1') {
      setCollapsedSection1Cards(prev => prev[target.id] === false ? prev : { ...prev, [target.id]: false });
    } else if (target.kind === 'section2') {
      setCollapsedSection2Cards(prev => prev[target.id] === false ? prev : { ...prev, [target.id]: false });
    } else if (target.kind === 'section4') {
      setCollapsedSection4Cards(prev => prev[target.id] === false ? prev : { ...prev, [target.id]: false });
    } else if (target.kind === 'section') {
      setCollapsedSections(prev => prev[target.id] === false ? prev : { ...prev, [target.id]: false });
    }
    lastAutoAdvanceRef.current = { id: target.id, ts: Date.now() };
  };
  const [suggestingField, setSuggestingField] = useState<string | null>(null);

  // Section data refs (updated by child components via onDataChange)
  const sectionDataRef = useRef<Record<string, any>>({});

  // State for dynamic consulting period entries
  const [consultingDays, setConsultingDays] = useState<Array<{ date: string; hours: string }>>([
    { date: '', hours: '' }
  ]);
  const [consultants, setConsultants] = useState<Array<{ name: string }>>([
    { name: 'Andreas Helm' }
  ]);

  const addConsultant = () => setConsultants(prev => [...prev, { name: '' }]);
  const removeConsultant = (index: number) => setConsultants(prev => prev.filter((_, i) => i !== index));
  const updateConsultant = (index: number, value: string) => setConsultants(prev => prev.map((c, i) => i === index ? { name: value } : c));

  // Beratungstag-Buchungen aus ANDEREN Berichten — für die Überschneidungs-Warnung in Karte 1.5.
  const [externalDayBookings, setExternalDayBookings] = useState<Array<{ date: string; reportId: number; berichtstitel: string; kundeName: string }>>([]);
  useEffect(() => {
    if (reportId === null) { setExternalDayBookings([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API_BASE}/api/reports/day-bookings?excludeReportId=${reportId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) setExternalDayBookings(data);
      } catch {
        /* Überschneidungs-Warnung ist optional — Fehler still ignorieren */
      }
    })();
    return () => { cancelled = true; };
  }, [reportId]);

  // State for dynamic current-state entries
  const [currentStateEntries, setCurrentStateEntries] = useState<Array<{ text: string }>>([
    { text: '' }
  ]);

  // State for dynamic key-findings entries
  const [keyFindingsEntries, setKeyFindingsEntries] = useState<Array<{ text: string }>>([
    { text: '' }
  ]);

  // State for dynamic data-sources entries
  const [dataSourcesEntries, setDataSourcesEntries] = useState<Array<{ text: string }>>([
    { text: '' }
  ]);

  // State for dynamic consulting objectives entries
  const [consultingObjectivesEntries, setConsultingObjectivesEntries] = useState<Array<{ text: string }>>([
    { text: '' }
  ]);

  // State for dynamic execution steps entries
  const [executionStepsEntries, setExecutionStepsEntries] = useState<Array<{ text: string }>>([
    { text: '' }
  ]);

  // State for dynamic weaknesses entries (strukturiert mit IDs)
  const [schwachstellenEntries, setSchwachstellenEntries] = useState<Schwachstelle[]>([
    { id: generateId(), problem: '', ursache: '', auswirkung: '' }
  ]);
  const [schwachstellenVersion, setSchwachstellenVersion] = useState(0);

  // State for dynamic fragestellungen entries (4.2)
  const [fragestellungenEntries, setFragestellungenEntries] = useState<Array<{ text: string }>>(
    sectionDataRef.current['weaknesses']?.fragestellungen || [{ text: '' }]
  );

  // Auto-advance refs — recomputed each render so closures see latest state.
  cardCompletionRef.current = (cardId: string): boolean => {
    const has = (k: string) => !!(formData[k] && String(formData[k]).trim());
    // Generated final text counts as "complete" too — user may have filled the
    // card via "Text generieren" (AI) instead of typing into the left input.
    const finalDone = (id: string, source: 'section1' | 'section2' | 'section4') => {
      const map = source === 'section1' ? section1CardTexts
        : source === 'section2' ? section2CardTexts
        : section4CardTexts;
      return hasRealContent(map[id]?.finalText);
    };
    // Auto-advance only fires when BOTH the left input (instruction/list) and
    // the right output (finalText, typically AI-generated) are filled. Either
    // half alone isn't enough — a stray click shouldn't push the user forward
    // before they've actually produced the deliverable.
    switch (cardId) {
      case 'card-1-1': return has('consulting-purpose') && finalDone('card-1-1', 'section1');
      case 'card-1-2': return has('org-name') && finalDone('card-1-2', 'section1');
      case 'card-1-3':
        return has('mitarbeiterzahl') && has('gruendung') && has('bilanzsumme')
          && has('jahresumsatz') && has('wirtschaftszweig')
          && finalDone('card-1-3', 'section1');
      case 'card-1-4': return has('geschaeftsgegenstand') && finalDone('card-1-4', 'section1');
      case 'card-1-5':
        return consultants.some(c => c.name?.trim())
          && consultingDays.some(d => d.date && d.hours)
          && finalDone('card-1-5', 'section1');
      case 'card-2-1': return currentStateEntries.some(e => e.text?.trim()) && finalDone('card-2-1', 'section2');
      case 'card-2-2': return keyFindingsEntries.some(e => e.text?.trim()) && finalDone('card-2-2', 'section2');
      case 'card-2-3': return dataSourcesEntries.some(e => e.text?.trim()) && finalDone('card-2-3', 'section2');
      case 'card-4-1':
        // Only `problem` is editable in Section4Cards' schwachstellen UI
        // (ursache/auswirkung exist in the entity but aren't surfaced here).
        return schwachstellenEntries.some(e => e.problem?.trim())
          && finalDone('card-4-1', 'section4');
      case 'card-4-2': return fragestellungenEntries.some(e => e.text?.trim()) && finalDone('card-4-2', 'section4');
      // Single sections
      case 'execution':
        return executionStepsEntries.some(e => e.text?.trim())
          && hasRealContent(sectionStates['execution']?.finalText);
      case 'action-plan':
        return recommendationsEntries.some(e => e.bezeichnung?.trim())
          && hasRealContent(sectionStates['action-plan']?.finalText);
      default: return false;
    }
  };
  cardAdvanceTargetRef.current = (cardId: string) => {
    switch (cardId) {
      case 'card-1-1': return { kind: 'section1' as const, id: 'card-1-2' };
      case 'card-1-2': return { kind: 'section1' as const, id: 'card-1-3' };
      case 'card-1-3': return { kind: 'section1' as const, id: 'card-1-4' };
      case 'card-1-4': return { kind: 'section1' as const, id: 'card-1-5' };
      case 'card-1-5': return { kind: 'section2' as const, id: 'card-2-1' };
      case 'card-2-1': return { kind: 'section2' as const, id: 'card-2-2' };
      case 'card-2-2': return { kind: 'section2' as const, id: 'card-2-3' };
      case 'card-2-3': return { kind: 'section' as const, id: 'execution' };
      case 'execution': return { kind: 'section4' as const, id: 'card-4-1' };
      case 'card-4-1': return { kind: 'section4' as const, id: 'card-4-2' };
      case 'card-4-2': return { kind: 'section' as const, id: 'action-plan' };
      case 'action-plan': return { kind: 'section' as const, id: 'principles' };
      default: return null;
    }
  };

  // State for dynamic action-plan entries (strukturiert mit IDs + Schwachstelle-Referenz)
  const [recommendationsEntries, setRecommendationsEntries] = useState<Massnahme[]>([
    { id: generateId(), schwachstelleId: '', bezeichnung: '', beschreibung: '', aufwand: '' }
  ]);


  const handleApplyExtraction = (fields: ExtractedFields) => {
    if (fields.istZustand.length) {
      setCurrentStateEntries(prev => [
        ...prev.filter(e => e.text.trim()),
        ...fields.istZustand.map(text => ({ text })),
      ]);
    }
    if (fields.staerken.length) {
      setKeyFindingsEntries(prev => [
        ...prev.filter(e => e.text.trim()),
        ...fields.staerken.map(text => ({ text })),
      ]);
    }
    if (fields.schwaechen.length) {
      setDataSourcesEntries(prev => [
        ...prev.filter(e => e.text.trim()),
        ...fields.schwaechen.map(text => ({ text })),
      ]);
    }
    if (fields.schwachstellen.length) {
      setSchwachstellenEntries(prev => [
        ...prev.filter(e => e.problem.trim()),
        ...fields.schwachstellen.map(problem => ({ id: generateId(), problem })),
      ]);
    }
    if (fields.beratungsziele.length) {
      setExecutionStepsEntries(prev => [
        ...prev.filter(e => e.text.trim()),
        ...fields.beratungsziele.map(text => ({ text })),
      ]);
    }
    if (fields.massnahmen.length) {
      setRecommendationsEntries(prev => [
        ...prev.filter(e => e.bezeichnung.trim()),
        ...fields.massnahmen.map(bezeichnung => ({
          id: generateId(),
          schwachstelleId: '',
          bezeichnung,
          beschreibung: '',
          aufwand: '' as const,
        })),
      ]);
    }
    // Freitextfelder (Berichtstitel, Geschäftsgegenstand, Beratungsauftrag): immer überschreiben,
    // wenn die KI einen Wert geliefert hat (leere KI-Werte überschreiben nichts).
    const bt = fields.berichtstitel?.trim();
    const gg = fields.geschaeftsgegenstand?.trim();
    const ba = fields.beratungsauftrag?.trim();
    if (bt || gg || ba) {
      setFormData(prev => {
        const updated = { ...prev };
        if (bt) updated['berichtstitel'] = bt;
        if (gg) updated['geschaeftsgegenstand'] = gg;
        if (ba) updated['consulting-purpose'] = ba;
        return updated;
      });
    }
  };

  // Section 6 opt-in toggle — defaulting to false bis auf Weiteres (Kundenwunsch, 2026-10-12):
  // Abschnitt soll bei neuen/leeren Berichten nicht mehr vorausgewählt sein. Berichte mit
  // bereits geschriebenem Grundsätze-Text schalten beim Laden automatisch wieder ein
  // (siehe unten, sec.sectionKey === 'principles'), damit vorhandener Text nicht hinter
  // der Checkbox verschwindet.
  const [includePrinciples, setIncludePrinciples] = useState(false);

  // ESF file tracking (for PDF merge on export)
  const [esfFileId, setEsfFileId] = useState<number | null>(null);
  const [esfEnabled, setEsfEnabled] = useState(true);

  // Intro banner
  const [showIntroBanner, setShowIntroBanner] = useState(() => {
    try { return localStorage.getItem('editor_intro_dismissed') !== 'true'; } catch { return true; }
  });
  const dismissIntroBanner = () => {
    setShowIntroBanner(false);
    try { localStorage.setItem('editor_intro_dismissed', 'true'); } catch {}
  };

  // Generation-Limits helpers
  const loadGenCounts = async (id: number) => {
    if (isAdmin) return;
    try {
      const res = await generationLimitsApi.getCountsForReport(id);
      const m = new Map<string, { count: number; limit: number | null }>();
      for (const it of res.items) m.set(`${it.cardId}|${it.action}`, { count: it.count, limit: it.limit });
      setGenCounts(m);
    } catch { /* ignorieren — Counts sind optional */ }
  };

  const getRemaining = (cardId: string, action: GenAction): number | null => {
    if (isAdmin) return null;
    const e = genCounts.get(`${cardId}|${action}`);
    if (!e || e.limit === null) return null;
    return Math.max(0, e.limit - e.count);
  };

  const incrementLocalCount = (cardId: string, action: GenAction) => {
    setGenCounts(prev => {
      const m = new Map(prev);
      const key = `${cardId}|${action}`;
      const cur = m.get(key) ?? { count: 0, limit: null };
      m.set(key, { count: cur.count + 1, limit: cur.limit });
      return m;
    });
  };

  // Export handlers
  const [exportingFormat, setExportingFormat] = useState<'pdf' | 'docx' | 'txt' | null>(null);

  const getReportData = useCallback(() => {
    const data = collectReportData({
      formData,
      selectedCustomer,
      section1CardTexts,
      section2CardTexts,
      section4CardTexts,
      sectionStates,
      sectionDataRef: sectionDataRef.current,
      consultingDays,
      consultants,
      advisorNumber: user?.advisorNumber,
    });
    if (!includePrinciples) {
      data.sections = data.sections
        .filter(s => s.number !== 6)
        .map((s, i) => ({
          ...s,
          number: i + 1,
          title: s.title.replace(/^\d+\./, `${i + 1}.`),
        }));
    }
    return data;
  }, [formData, selectedCustomer, section1CardTexts, section2CardTexts, section4CardTexts, sectionStates, consultingDays, consultants, includePrinciples, user?.advisorNumber]);

  /** Fire-and-forget: erster Export legt im Backend ein 'bericht_erstellt'-Event an. */
  const trackReportExport = useCallback((rid: number | null) => {
    if (!rid) return;
    api.post(`/api/reports/${rid}/track-export`, {}).catch(() => { /* ignorieren, Export hat geklappt */ });
  }, []);

  const handleExportPdf = useCallback(async () => {
    setExportingFormat('pdf');
    try {
      // Always fetch ESF file fresh at export time (only if toggle is ON)
      let resolvedEsfFileId: number | undefined;
      if (reportId && esfEnabled) {
        try {
          const files = await api.get<{ id: number; category: string }[]>(`/api/reports/${reportId}/files`);
          const esfFiles = files.filter(f => f.category === 'ESF Fragebogen');
          if (esfFiles.length > 0) {
            resolvedEsfFileId = esfFiles.reduce((a, b) => (a.id > b.id ? a : b)).id;
          }
        } catch { /* ignore, export without ESF */ }
      }
      const data = getReportData();
      const filename = buildReportFilename(user?.filenamePattern, {
        kundenname: data.company.name,
        az: data.metadata.aktenzeichen,
        typ: 'bericht',
        fallback: `BAFA_Beratungsbericht_${data.metadata.aktenzeichen || 'Bericht'}_${new Date().toISOString().slice(0, 10)}`,
      }) + '.pdf';
      await exportPdf(data, resolvedEsfFileId, filename);
      trackReportExport(reportId);
    } catch (e) {
      console.error('PDF Export fehlgeschlagen:', e);
    } finally {
      setExportingFormat(null);
    }
  }, [getReportData, reportId, esfEnabled, user]);

  const handleExportDocx = useCallback(async () => {
    setExportingFormat('docx');
    try {
      await exportDocx(getReportData());
      trackReportExport(reportId);
    } catch (e) {
      console.error('DOCX Export fehlgeschlagen:', e);
    } finally {
      setExportingFormat(null);
    }
  }, [getReportData, reportId]);

  const handleExportTxt = useCallback(() => {
    setExportingFormat('txt');
    try {
      const data = getReportData();
      const filename = buildReportFilename(user?.filenamePattern, {
        kundenname: data.company.name,
        az: data.metadata.aktenzeichen,
        typ: 'bericht',
        fallback: `BAFA_Beratungsbericht_${data.metadata.aktenzeichen || 'Bericht'}_${new Date().toISOString().slice(0, 10)}`,
      }) + '.txt';
      exportTxt(data, filename);
      trackReportExport(reportId);
    } catch (e) {
      console.error('TXT Export fehlgeschlagen:', e);
    } finally {
      setExportingFormat(null);
    }
  }, [getReportData, reportId, user]);

  /**
   * Abschnitte 6 (Grundsätze) und 7 (Bestätigung) als bestätigt markieren.
   *
   * Beide tragen von Anfang an ihren Standardtext; „fertig" sind sie erst mit dem Haken des
   * Beraters. Das ist beim manuellen Durchgehen richtig — aber wer den Bericht speichert
   * oder komplett generieren lässt, hat sie ohnehin übernommen: Ihr Text steht dann bereits
   * im PDF. Die Fortschrittsanzeige auf 0% zu lassen, während der fertige Bericht beim
   * Kunden liegt, ist schlicht falsch.
   *
   * Gespeichert wird über die bestehende Abschnitts-Automatik (Effekte weiter unten).
   */
  const bestaetigeSchlussabschnitte = useCallback(() => {
    setSectionStates(prev => {
      if (prev['principles']?.confirmed && prev['confirmation']?.confirmed) return prev;
      return {
        ...prev,
        principles: { ...prev['principles'], confirmed: true },
        confirmation: { ...prev['confirmation'], confirmed: true },
      };
    });
  }, []);

  const SIGNED_CATEGORIES = ['Bericht PDF – Vollständig unterschrieben', 'Bericht PDF – Unterschrieben'];

  const doSavePdf = useCallback(async (opts?: { alsoRefreshSigned?: boolean }) => {
    if (!reportId) return;
    setExportingFormat('pdf');
    try {
      let resolvedEsfFileId: number | undefined;
      if (esfEnabled) {
        try {
          const files = await api.get<{ id: number; category: string }[]>(`/api/reports/${reportId}/files`);
          const esfFiles = files.filter(f => f.category === 'ESF Fragebogen');
          if (esfFiles.length > 0) resolvedEsfFileId = esfFiles.reduce((a, b) => (a.id > b.id ? a : b)).id;
        } catch { /* ignore */ }
      }
      // Der Standardtext beider Schlussabschnitte steht in genau diesem PDF — damit sind
      // sie übernommen, egal ob vorher jemand auf „Bestätigen" geklickt hat.
      bestaetigeSchlussabschnitte();
      const bytes = await getPdfBytes(getReportData(), resolvedEsfFileId);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const token = localStorage.getItem('auth_token');
      const reportData = getReportData();
      const berichtFilename = buildReportFilename(user?.filenamePattern, {
        kundenname: reportData.company.name,
        az: reportData.metadata.aktenzeichen,
        typ: 'bericht',
        fallback: 'bericht',
      }) + '.pdf';
      const form = new FormData();
      form.append('file', blob, berichtFilename);
      form.append('category', 'Bericht PDF');
      const { API_BASE } = await import('../lib/apiBase');
      const uploadRes = await fetch(`${API_BASE}/api/reports/${reportId}/files`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      // Versions-Chaos vermeiden: Nach erfolgreichem Speichern die VORHERIGEN „Bericht PDF"-
      // Versionen entfernen, sodass nur die aktuelle bleibt. Nur die exakte Kategorie
      // „Bericht PDF" — die unterschriebenen Fassungen (eigene Kategorien) bleiben unberührt.
      let createdId: number | undefined;
      if (uploadRes.ok) {
        try {
          const created = await uploadRes.json().catch(() => null);
          createdId = typeof created?.id === 'number' ? created.id : undefined;
          const files = await api.get<{ id: number; category: string }[]>(`/api/reports/${reportId}/files`);
          const berichtPdfs = files.filter(f => f.category === 'Bericht PDF');
          // Behalten: die gerade erstellte Datei; Fallback (falls keine ID aus der Antwort):
          // die neueste (höchste ID). So kann NIE die neue Version gelöscht werden.
          let keepId: number | undefined = createdId;
          if (keepId === undefined && berichtPdfs.length > 0) {
            keepId = berichtPdfs.reduce((a, b) => (a.id > b.id ? a : b)).id;
          }
          for (const f of berichtPdfs) {
            if (f.id === keepId) continue;
            try { await api.delete(`/api/files/${f.id}`); } catch { /* einzelne Löschung darf das Speichern nicht stoppen */ }
          }
        } catch { /* Aufräumen ist best-effort, blockiert das Speichern nie */ }
      }
      // Bericht war bereits unterschrieben und der Nutzer hat im Dialog bestätigt: die
      // unterschriebene Fassung im Vorgang mit dem neuen Inhalt aktualisieren (Kundenwunsch,
      // 2026-10-12) — Unterschriftsseite bleibt dabei erhalten, siehe refresh-signed-document.
      if (opts?.alsoRefreshSigned && uploadRes.ok && createdId != null) {
        try {
          await api.post(`/api/reports/${reportId}/refresh-signed-document`, { newFileId: createdId });
        } catch (e: any) {
          if (e?.message === 'PAGE_COUNT_MISMATCH') {
            setSignedRefreshMismatch(true);
          } else {
            console.error('Unterschriebene Fassung konnte nicht aktualisiert werden:', e);
          }
        }
      }
      setFileRefreshKey(k => k + 1);
    } catch (e) {
      console.error('PDF Speichern fehlgeschlagen:', e);
    } finally {
      setExportingFormat(null);
    }
  }, [getReportData, reportId, esfEnabled, user, bestaetigeSchlussabschnitte]);

  // Vor dem eigentlichen Speichern prüfen, ob dieser Bericht bereits unterschrieben wurde —
  // falls ja, erst das Bestätigungsfenster zeigen (siehe signedUpdateConfirmOpen), statt
  // direkt zu speichern. Eigene Funktion, weil sie von zwei Stellen aufgerufen wird: dem
  // normalen „PDF speichern" UND dem „Trotzdem speichern" im ESF-Warnhinweis.
  const checkSignedThenSave = useCallback(async () => {
    if (!reportId) { await doSavePdf(); return; }
    try {
      const files = await api.get<{ id: number; category: string }[]>(`/api/reports/${reportId}/files`);
      if (files.some(f => SIGNED_CATEGORIES.includes(f.category))) {
        setSignedUpdateConfirmOpen(true);
        return;
      }
    } catch { /* ignore, proceed without check */ }
    await doSavePdf();
  }, [reportId, doSavePdf]);

  const handleSavePdf = useCallback(async () => {
    if (!reportId || !esfEnabled) { await checkSignedThenSave(); return; }
    try {
      const files = await api.get<{ id: number; category: string }[]>(`/api/reports/${reportId}/files`);
      const hasEsf = files.some(f => f.category === 'ESF Fragebogen');
      if (!hasEsf) { setEsfWarnOpen(true); return; }
    } catch { /* ignore, proceed without check */ }
    await checkSignedThenSave();
  }, [reportId, esfEnabled, checkSignedThenSave]);

  const doGenerateBafaForms = async () => {
    if (!reportId) return;
    setBafaWarnFields([]);
    setBafaGenerating(true);
    setBafaError(null);
    try {
      await api.post(`/api/reports/${reportId}/bafa-forms`);
      setFileRefreshKey(k => k + 1); // löst Neu-Ableitung von bafaGenerated + Listen aus
      // Akkurate Rückmeldung: was steht tatsächlich zum Unterzeichnen bereit,
      // was wird übersprungen (Dauer-Dok bereits unterschrieben).
      let msg = 'Dauer-Dokumente wurden erstellt.';
      try {
        const p = await api.get<{ skipped?: string[]; files?: { type: string; label: string }[] }>(`/api/reports/${reportId}/signing-preview`);
        const ready = (p.files || []).filter(f => f.type !== 'bericht').map(f => f.label);
        const skipped = p.skipped || [];
        const parts: string[] = [];
        if (ready.length) parts.push(`✓ Zum Unterzeichnen bereit: ${ready.join(', ')}.`);
        if (skipped.length) parts.push(`Bereits unterschrieben – nicht erneut nötig: ${skipped.join(', ')}.`);
        if (parts.length) msg = parts.join(' ');
      } catch { /* Fallback: generische Meldung */ }
      setAlertMsg(msg);
    } catch (err: any) {
      setBafaError(err.message ?? 'Fehler beim Generieren');
    } finally {
      setBafaGenerating(false);
    }
  };

  const handleGenerateBafaForms = async () => {
    if (!reportId) {
      setAlertMsg('Bitte speichern Sie zuerst den Bericht (Kunde, Berichtstitel und Vorgangsnummer), bevor Sie die Dauer-Dokumente generieren.');
      return;
    }
    const c = selectedCustomer as any;
    const missing: string[] = [];
    if (!c?.name) missing.push('Firmenname');
    if (!c?.address) missing.push('Straße');
    if (!c?.plz) missing.push('Postleitzahl');
    if (!c?.ort) missing.push('Ort');
    if (!c?.vorname && !c?.nachname) missing.push('Kontaktperson');
    if (!c?.mitarbeiterzahl) missing.push('Mitarbeiterzahl');
    if (!c?.jahresumsatz && !c?.jahresbilanzsumme) missing.push('Jahresumsatz oder Jahresbilanzsumme');
    if (!c?.unternehmenstyp) missing.push('Unternehmenstyp');
    if (missing.length > 0) {
      setBafaWarnFields(missing);
      return;
    }
    if (signingStatus !== 'none') {
      // Bereits zur Unterschrift versendet (oder abgeschlossen) → warnen statt
      // stillschweigend neu erzeugen.
      setSigningSentWarnOpen(true);
      return;
    }
    if (bafaGenerated) {
      setOverwriteConfirm(true);
      return;
    }
    await doGenerateBafaForms();
  };

  // Fetch the signing preview, then open the preview dialog. Nimmt die E-Mail direkt
  // entgegen (statt nur aus signingEmail-State zu lesen) — sonst stale Closure, wenn
  // Button-Klick und Aufruf im selben Tick passieren (kein separates Bestätigungs-
  // Fenster mehr dazwischen, siehe Button-Handler unten).
  /**
   * VN zur Unterschrift schicken — aber erst nachsehen, ob überhaupt einer da ist.
   *
   * Ohne die Prüfung öffnet sich der Versand-Dialog und meldet „Kein Verwendungsnachweis
   * hochgeladen." Das ist eine Sackgasse: Der Berater weiß, dass keiner da ist, und will
   * ihn holen. Also wird direkt gefragt, statt ihn wegzuschicken.
   */
  const handleVnUnterschrift = async () => {
    if (!reportId) return;
    setVnMeldung(null);
    setVnPruefung(true);
    try {
      const r = await api.get<any>(`/api/reports/${reportId}`);
      if (r?.vorgangId) {
        const dateien = await api.get<Array<{ category: string | null }>>(`/api/vorgaenge/${r.vorgangId}/files`);
        if (!hatVerwendungsnachweis(dateien.map(f => f.category)) && r?.customerId) {
          setVnFehlt({ vorgangId: r.vorgangId, customerId: r.customerId });
          return;
        }
      }
    } catch {
      // Prüfung fehlgeschlagen → wie bisher weiter; der Dialog meldet den Rest.
    } finally {
      setVnPruefung(false);
    }
    setSigningEmail(selectedCustomer?.email ?? '');
    setEmailReviewContext({ mode: 'vn', bitteKontoauszug: false });
    setEmailReviewOpen(true);
  };

  /**
   * Bericht zur Unterschrift schicken — aber erst nachsehen, ob es einen gibt.
   *
   * Der Editor hält den Bericht als Datensatz; die PDF entsteht erst beim Speichern. Wurde
   * sie gelöscht oder nie erzeugt, ging die Mail trotzdem raus — und der Kunde landete auf
   * einer Seite, die ihm nur den Kontoauszug anbot. Zu unterschreiben gab es nichts, und
   * niemand merkte es, bis er sich meldete.
   */
  const oeffneBerichtVersand = async (emailOverride?: string) => {
    if (!reportId) return;
    const email = (emailOverride ?? signingEmail ?? '').trim() || selectedCustomer?.email || '';
    try {
      const files = await api.get<{ id: number; category: string }[]>(`/api/reports/${reportId}/files`);
      if (!hatBerichtPdf(files.map(f => f.category))) {
        setBerichtSpeichernFrage({ email });
        return;
      }
    } catch {
      // Prüfung fehlgeschlagen → wie bisher weiter; besser senden als blockieren.
    }
    setSigningEmail(email);
    handleOpenSigningPreview(email);
  };

  const handleOpenSigningPreview = async (emailOverride?: string) => {
    const email = emailOverride ?? signingEmail;
    if (!reportId || !email) return;
    setSigningError(null);
    setSigningPreview(null);
    setSigningPreviewLoading(true);
    // Die Dokumentauswahl lebt im E-Mail-Dialog — der geht sofort auf, die Liste lädt darin nach.
    setEmailReviewContext({ mode: 'forms' });
    setEmailReviewOpen(true);
    try {
      const preview = await api.get<SigningPreview>(
        `/api/reports/${reportId}/signing-preview`,
      );
      setSigningPreview(preview);
    } catch (err: any) {
      setSigningError(err.message ?? 'Fehler beim Laden der Vorschau');
    } finally {
      setSigningPreviewLoading(false);
    }
  };

  /**
   * Absicht aus der Adresszeile: `/editor/69?aktion=bericht-unterschrift`.
   *
   * Die Kundenliste zeigt am Vorgang den nächsten Schritt als Knopf; ausgeführt wird er
   * hier, wo der Versand ohnehin zu Hause ist. So gibt es weiter EINE Umsetzung des
   * Versands statt zweier, die auseinanderlaufen.
   *
   * Der Parameter wird nach dem Auslösen aus der Adresszeile entfernt — sonst öffnete ein
   * Neuladen den Dialog wieder.
   */
  const aktionAusgefuehrt = useRef(false);
  useEffect(() => {
    if (aktionAusgefuehrt.current || !reportId) return;
    const params = new URLSearchParams(window.location.search);
    const aktion = params.get('aktion');
    if (aktion !== 'bericht-unterschrift' && aktion !== 'vn-unterschrift') return;
    // Erst wenn der Kunde geladen ist — sonst geht der Dialog ohne Empfängeradresse auf.
    if (!selectedCustomer) return;
    aktionAusgefuehrt.current = true;
    params.delete('aktion');
    const rest = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    if (aktion === 'bericht-unterschrift') {
      oeffneBerichtVersand(selectedCustomer.email ?? '');
    } else {
      handleVnUnterschrift();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, selectedCustomer]);

  // Step 2: confirmed in the preview dialog → send the signing request (with any forced types).
  const handleSendForSigning = async (forceTypes: string[] = [], excludeTypes: string[] = [], bitteKontoauszug = false, subject?: string, message?: string, anrede?: string, emailOverride?: string) => {
    const email = (emailOverride ?? '').trim() || signingEmail;
    if (!reportId || !email) return;
    setSigningSending(true);
    setSigningError(null);
    try {
      await api.post(`/api/reports/${reportId}/signing-request`, {
        customerEmail: email,
        ...(forceTypes.length > 0 ? { forceTypes } : {}),
        ...(excludeTypes.length > 0 ? { excludeTypes } : {}),
        bitteKontoauszug,
        ...(subject !== undefined ? { subject } : {}),
        ...(message !== undefined ? { message } : {}),
        ...(anrede !== undefined ? { anrede } : {}),
      });
      setSigningStatus('pending');
      setSigningPreview(null);
      setEmailReviewOpen(false);
    } catch (err: any) {
      setSigningError(err.message ?? 'Fehler beim Senden');
    } finally {
      setSigningSending(false);
    }
  };

  // VN (Verwendungsnachweis) — eigene Signing-Runde, im Editor ausgelöst.
  const handleSendVnForSigning = async (subject?: string, message?: string, anrede?: string, emailOverride?: string) => {
    const email = (emailOverride ?? '').trim() || signingEmail || selectedCustomer?.email || '';
    if (!reportId || !email) return;
    setVnSending(true);
    setVnMeldung(null);
    try {
      await sendVnForSigning(reportId, email, { subject, message, anrede });
      setEmailReviewOpen(false);
      setVnMeldung('VN wurde zur Unterschrift an den Kunden gesendet.');
    } catch (err: any) {
      // Confidence-Gate im Backend → Prüf-Meldung statt Fehlversand.
      if (typeof err?.message === 'string' && err.message.includes('VN_PAGES_UNSURE')) {
        setVnMeldung('VN-Unterschriftsseiten konnten nicht sicher erkannt werden – bitte den VN prüfen und Seiten ggf. manuell senden.');
      } else {
        setVnMeldung(err?.message ?? 'Fehler beim VN-Versand.');
      }
    } finally {
      setVnSending(false);
    }
  };

  // Refs for each section
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Helper: calculate progress for a given section
  useEffect(() => {
    if (!reportId) { setVorgangKategorien([]); setFassung(null); return; }
    let abgebrochen = false;
    api.get<Array<{ category: string | null }>>(`/api/reports/${reportId}/files`)
      .then(fs => { if (!abgebrochen) setVorgangKategorien(fs.map(f => f.category ?? '')); })
      .catch(() => { if (!abgebrochen) setVorgangKategorien([]); });
    // Auch nach jedem Speichern neu holen: Genau dann verschiebt sich der Textstand
    // gegenüber der PDF — die Warnung muss sofort stehen, nicht erst beim nächsten Aufruf.
    api.get<{ pdfAm: string | null; textAm: string | null; veraltet: boolean }>(`/api/reports/${reportId}/bericht-stand`)
      .then(st => { if (!abgebrochen) setFassung(st); })
      .catch(() => { if (!abgebrochen) setFassung(null); });
    return () => { abgebrochen = true; };
  }, [reportId, fileRefreshKey, saveStatus]);

  const getSectionProgress = useCallback((sectionId: string): number => {
    if (sectionId === 'section1') {
      const totalCards = 5;
      const validKeys = ['card-1-1', 'card-1-2', 'card-1-3', 'card-1-4', 'card-1-5'];
      const cardsWithContent = Object.entries(section1CardTexts)
        .filter(([key, card]) => validKeys.includes(key) && hasRealContent(card.finalText))
        .length;
      return Math.round((cardsWithContent / totalCards) * 100);
    }
    if (sectionId === 'analysis') {
      const totalCards = 3;
      const validKeys = ['card-2-1', 'card-2-2', 'card-2-3'];
      const cardsWithContent = Object.entries(section2CardTexts)
        .filter(([key, card]) => validKeys.includes(key) && hasRealContent(card.finalText))
        .length;
      return Math.round((cardsWithContent / totalCards) * 100);
    }
    if (sectionId === 'weaknesses') {
      const totalCards = 2;
      const validKeys = ['card-4-1', 'card-4-2'];
      const cardsWithContent = Object.entries(section4CardTexts)
        .filter(([key, card]) => validKeys.includes(key) && hasRealContent(card.finalText))
        .length;
      return Math.round((cardsWithContent / totalCards) * 100);
    }
    if (sectionId === 'confirmation' || sectionId === 'principles') {
      // Wie alle anderen Abschnitte: Text da → fertig. Früher zählte hier NUR der Klick auf
      // „Bestätigen" — mit der Begründung, der Standardtext stehe ja von Anfang an drin.
      // In der Praxis blieben die Balken dadurch dauerhaft leer, auch bei längst
      // generiertem und verschicktem Bericht. Eine Anzeige, die den fertigen Bericht als
      // unfertig ausweist, ist schlechter als eine, die den Standardtext mitzählt.
      if (sectionStates[sectionId]?.confirmed) return 100;
      return hasRealContent(sectionStates[sectionId]?.finalText) ? 100 : 0;
    }
    if (hasRealContent(sectionStates[sectionId]?.finalText)) return 100;
    return 0;
  }, [section1CardTexts, section2CardTexts, section4CardTexts, sectionStates]);

  /** Alle Textabschnitte haben Inhalt → der Bericht ist erstellt und kann geprüft werden. */
  const TEXT_ABSCHNITTE = ['section1', 'analysis', 'execution', 'weaknesses', 'action-plan'];
  const berichtGefuellt = TEXT_ABSCHNITTE.every(id => getSectionProgress(id) === 100);

  /** Grobe Schaltbedingung fuer "Als Vorlage speichern": mindestens einer der Abschnitte,
   *  aus denen Vorlagen Inhalte ziehen, hat ueberhaupt Fortschritt. Verhindert den Klick auf
   *  einen frisch angelegten, noch komplett leeren Bericht — die eigentliche, verbindliche
   *  Pruefung (sind die Listen wirklich befuellt) macht weiterhin das Backend. */
  const hasTemplateQuellinhalt = ['analysis', 'weaknesses', 'action-plan', 'execution'].some(
    (id) => getSectionProgress(id) > 0,
  );

  /**
   * Der nächste Schritt als EIN Knopf über „Bericht komplett erstellen".
   *
   * Bisher musste man wissen, wo die jeweils nächste Handlung liegt: Speichern unten bei den
   * Exporten, der Versand in der BAFA-Karte weiter unten, der VN noch daneben. Der Zustand
   * sagt aber eindeutig, was dran ist — also steht genau das oben.
   */
  const naechsterSchritt = naechsterEditorSchritt({ kategorien: vorgangKategorien });
  const neuErstellenErlaubt = darfNeuErstellen({ kategorien: vorgangKategorien, signaturRunde: signingStatus });
  const fassungVeraltet = !!fassung?.veraltet;
  const fassungDatum = (() => {
    if (!fassung?.pdfAm) return '';
    const d = new Date(fassung.pdfAm);
    return isNaN(d.getTime())
      ? ''
      : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(', ', ' um ') + ' Uhr';
  })();

  /** Wann zuletzt zur Unterschrift geschickt — der Versand darf wiederholt werden. */
  const letzteAnforderung = (() => {
    const iso = signingInfo?.emailSentAt ?? signingInfo?.createdAt;
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? null
      : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(', ', ' um ');
  })();

  // Section steps configuration
  const sections = [
    { id: 'section1', number: 1, title: 'Unternehmen', progress: 100, completed: true },
    { id: 'analysis', number: 2, title: 'Analyse', progress: 75, completed: false },
    { id: 'execution', number: 3, title: 'Vorgehen', progress: 50, completed: false },
    { id: 'weaknesses', number: 4, title: 'Schwachstellen', progress: 25, completed: false },
    { id: 'action-plan', number: 5, title: 'Maßnahmeplan', progress: 0, completed: false },
    { id: 'principles', number: 6, title: 'Grundsätze', progress: 0, completed: false },
    { id: 'confirmation', number: 7, title: 'Bestätigung', progress: 0, completed: false },
  ];
  // „Grundsätze" ist opt-in (Kundenwunsch 2026-10-12): per CSS ausgeblendet (nicht aus dem
  // Baum entfernt), solange nicht eingeschlossen — Entfernen+Neumounten der Karte (inkl.
  // RichTextEditor darin) führte in Tests zu einer Endlosschleife beim Wiedereinblenden,
  // daher bewusst nur `display:none` statt Filtern aus `sections`/bedingtem Rendern.
  // Der "+ Grundsätze hinzufügen"-Link unten in der Sidebar setzt includePrinciples wieder true.
  const principlesHiddenStyle = includePrinciples ? undefined : { display: 'none' as const };

  // Scroll to section when clicking stepper — also expands if collapsed
  // Abschluss-Bereiche unter den Abschnitten (ESF-Fragebogen, Dokumente/Export) —
  // eigene Einträge in der Abschnittsleiste.
  const esfRef = useRef<HTMLDivElement | null>(null);
  const exportRef = useRef<HTMLDivElement | null>(null);
  const scrollToRef = (el: HTMLDivElement | null) => {
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setIsMobileSidebarOpen(false);
  };

  const scrollToSection = (sectionId: string) => {
    setCollapsedSections(prev => prev[sectionId] ? { ...prev, [sectionId]: false } : prev);
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
      onSectionChange(sectionId);
      setIsMobileSidebarOpen(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    if (wasJustAutoOpened(sectionId)) return;
    const isCurrentlyCollapsed = !!collapsedSections[sectionId];
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
    if (isCurrentlyCollapsed) {
      // Scroll to section after the CSS expand animation starts
      setTimeout(() => {
        const element = sectionRefs.current[sectionId];
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  };

  const allExpanded = Object.values(collapsedSections).every(v => !v);

  const toggleAll = () => {
    const collapse = allExpanded;
    setCollapsedSections(prev => Object.fromEntries(Object.keys(prev).map(k => [k, collapse])));
  };

  // Auto-expand next section when current reaches 100%
  const prevProgressRef = useRef<Record<string, number>>({});
  useEffect(() => {
    const sectionIds = ['section1', 'analysis', 'execution', 'weaknesses', 'action-plan', 'principles', 'confirmation'];
    sectionIds.forEach((id, index) => {
      const progress = getSectionProgress(id);
      const prev = prevProgressRef.current[id] ?? -1;
      if (progress === 100 && prev !== 100 && prev !== -1 && index < sectionIds.length - 1) {
        const nextId = sectionIds[index + 1];
        setTimeout(() => {
          setCollapsedSections(p => p[nextId] ? { ...p, [nextId]: false } : p);
        }, 500);
      }
      prevProgressRef.current[id] = progress;
    });
  }, [getSectionProgress]);

  // IntersectionObserver to detect active section
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -60% 0px', // Trigger when section is near top
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute('data-section-id');
          if (id) {
            setActiveSection(id);
            onSectionChange(id);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all sections
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      observer.disconnect();
    };
  }, [onSectionChange]);

  // Close mobile sidebar on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileSidebarOpen) {
        setIsMobileSidebarOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileSidebarOpen]);

  // Auto-fade saved status after 3 seconds
  useEffect(() => {
    if (saveStatus === 'saved') {
      if (autoSaveFadeRef.current) clearTimeout(autoSaveFadeRef.current);
      autoSaveFadeRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    }
    return () => {
      if (autoSaveFadeRef.current) clearTimeout(autoSaveFadeRef.current);
    };
  }, [saveStatus]);

  // Body overflow is managed by the ReportEditor container (h-[calc(100vh-3.5rem)])

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileSidebarOpen]);

  // ========== Handler Functions ==========
  
  // Consulting days
  const addConsultingDay = () => {
    setConsultingDays([...consultingDays, { date: '', hours: '' }]);
  };

  const removeConsultingDay = (index: number) => {
    if (consultingDays.length > 1) {
      setConsultingDays(consultingDays.filter((_, i) => i !== index));
    }
  };

  const updateConsultingDay = (index: number, field: 'date' | 'hours', value: string) => {
    const updated = [...consultingDays];
    updated[index][field] = value;
    setConsultingDays(updated);
  };

  // Current state entries
  const addCurrentStateEntry = () => {
    setCurrentStateEntries([...currentStateEntries, { text: '' }]);
  };

  const removeCurrentStateEntry = (index: number) => {
    if (currentStateEntries.length > 1) {
      setCurrentStateEntries(currentStateEntries.filter((_, i) => i !== index));
    }
  };

  const updateCurrentStateEntry = (index: number, value: string) => {
    const updated = [...currentStateEntries];
    updated[index].text = value;
    setCurrentStateEntries(updated);
  };

  // Key findings entries
  const addKeyFindingsEntry = () => {
    setKeyFindingsEntries([...keyFindingsEntries, { text: '' }]);
  };

  const removeKeyFindingsEntry = (index: number) => {
    if (keyFindingsEntries.length > 1) {
      setKeyFindingsEntries(keyFindingsEntries.filter((_, i) => i !== index));
    }
  };

  const updateKeyFindingsEntry = (index: number, value: string) => {
    const updated = [...keyFindingsEntries];
    updated[index].text = value;
    setKeyFindingsEntries(updated);
  };

  // Data sources entries
  const addDataSourcesEntry = () => {
    setDataSourcesEntries([...dataSourcesEntries, { text: '' }]);
  };

  const removeDataSourcesEntry = (index: number) => {
    if (dataSourcesEntries.length > 1) {
      setDataSourcesEntries(dataSourcesEntries.filter((_, i) => i !== index));
    }
  };

  const updateDataSourcesEntry = (index: number, value: string) => {
    const updated = [...dataSourcesEntries];
    updated[index].text = value;
    setDataSourcesEntries(updated);
  };

  // Consulting objectives entries
  const addConsultingObjectivesEntry = () => {
    setConsultingObjectivesEntries([...consultingObjectivesEntries, { text: '' }]);
  };

  const removeConsultingObjectivesEntry = (index: number) => {
    if (consultingObjectivesEntries.length > 1) {
      setConsultingObjectivesEntries(consultingObjectivesEntries.filter((_, i) => i !== index));
    }
  };

  const updateConsultingObjectivesEntry = (index: number, value: string) => {
    const updated = [...consultingObjectivesEntries];
    updated[index].text = value;
    setConsultingObjectivesEntries(updated);
  };

  // Section1 Cards handlers
  const handleUpdateSection1CardText = (cardId: string, field: 'aiDraft' | 'finalText' | 'showAiDraft' | 'saved', value: any) => {
    setSection1CardTexts(prev => ({
      ...prev,
      [cardId]: {
        ...prev[cardId],
        [field]: value,
        // Reset saved state when text changes
        ...(field === 'finalText' && { saved: false })
      }
    }));
  };

  // Felder, in die jemand nur "..." oder "---" geschrieben hat, sind fachlich leer.
  // Ungefiltert landen sie sonst als Wert im Bericht ("Website: ...").
  const ohnePlatzhalter = (wert: string): string =>
    /^[.\-_–—\s]+$/.test(wert) ? '' : wert;

  const buildCard12Html = (): string => {
    const fd = formData;
    const c = selectedCustomer as any;
    // Formularfeld mit Fallback auf Kundendaten
    const get = (formKey: string, custKey?: string) =>
      ohnePlatzhalter(fd[formKey] || (c && custKey ? String(c[custKey] || '') : '') || '');
    const anrede = get('anrede', 'anrede');
    const vorname = get('vorname', 'vorname');
    const nachname = get('nachname', 'nachname');
    const nameTeile = [anrede, vorname, nachname].filter(Boolean).join(' ');
    const telefon = get('telefon', 'phone');
    const email = get('email', 'email');
    const orgName = get('org-name', 'name');
    const rechtsform = get('rechtsform', 'rechtsform');
    const strasse = [get('street', 'address'), get('hausnummer', 'hausnummer')].filter(Boolean).join(' ');
    const plz = get('plz', 'plz');
    const ort = get('ort', 'ort');
    const land = get('land');
    const website = get('website', 'website');

    let html = `<h2>1.2 Angaben zum Unternehmen</h2>\n`;
    html += `<p style="font-size:14px; font-weight:600;">Ansprechperson</p>\n`;
    if (nameTeile) html += `<p style="font-size:14px;"><strong>Name:</strong> ${nameTeile}</p>\n`;
    if (telefon) html += `<p style="font-size:14px;"><strong>Telefon:</strong> ${telefon}</p>\n`;
    if (email) html += `<p style="font-size:14px;"><strong>E-Mail:</strong> ${email}</p>\n`;
    html += `<p style="font-size:14px; margin-top:10px;"><strong>Unternehmensname:</strong> ${orgName}</p>\n`;
    if (rechtsform) html += `<p style="font-size:14px;"><strong>Rechtsform:</strong> ${rechtsform}</p>\n`;
    if (strasse) html += `<p style="font-size:14px;"><strong>Straße und Hausnummer:</strong> ${strasse}</p>\n`;
    if (plz || ort) html += `<p style="font-size:14px;"><strong>PLZ und Ort:</strong> ${[plz, ort].filter(Boolean).join(' ')}</p>\n`;
    if (land) html += `<p style="font-size:14px;"><strong>Land:</strong> ${land}</p>\n`;
    if (website) html += `<p style="font-size:14px;"><strong>Website:</strong> ${website}</p>\n`;
    return html;
  };

  const buildCard13Html = (): string => {
    const fd = formData;
    const c = selectedCustomer as any;
    const get = (formKey: string, custKey?: string) =>
      ohnePlatzhalter(fd[formKey] || (c && custKey ? String(c[custKey] || '') : '') || '');

    const mitarbeiter = get('mitarbeiterzahl', 'mitarbeiterzahl');
    const gruendung = get('gruendung', 'gruendung');
    const bilanzsumme = get('bilanzsumme', 'jahresbilanzsumme');
    const jahresumsatz = get('jahresumsatz', 'jahresumsatz');
    const wz = get('wirtschaftszweig', 'wirtschaftszweig');

    // Geldbeträge mit Tausenderpunkt und Euro. Steht schon eine Währung oder ein
    // Zusatz im Feld ("50.000 EUR", "ca. 50000"), bleibt die Eingabe unangetastet.
    const formatEuro = (wert: string): string => {
      const roh = wert.trim();
      if (!/^\d+([.,]\d{1,2})?$/.test(roh)) return roh;
      const zahl = Number(roh.replace(',', '.'));
      if (!Number.isFinite(zahl)) return roh;
      return `${zahl.toLocaleString('de-DE')} €`;
    };

    // Gründungsdatum formatieren (YYYY-MM-DD → dd.mm.yyyy)
    let gruendungDisplay = gruendung;
    const isoMatch = gruendung.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      gruendungDisplay = `${isoMatch[3]}.${isoMatch[2]}.${isoMatch[1]}`;
    }

    let html = `<h2>1.3 Unternehmensdaten</h2>\n`;
    if (mitarbeiter) html += `<p style="font-size:14px;"><strong>Mitarbeiterzahl:</strong> ${mitarbeiter}</p>\n`;
    if (gruendungDisplay) html += `<p style="font-size:14px;"><strong>Gründungsdatum:</strong> ${gruendungDisplay}</p>\n`;
    if (bilanzsumme) html += `<p style="font-size:14px;"><strong>Jahresbilanzsumme:</strong> ${formatEuro(bilanzsumme)}</p>\n`;
    if (jahresumsatz) html += `<p style="font-size:14px;"><strong>Jahresumsatz:</strong> ${formatEuro(jahresumsatz)}</p>\n`;
    if (wz) html += `<p style="font-size:14px;"><strong>Wirtschaftszweigklassifikation:</strong> ${wz}</p>\n`;
    return html;
  };

  // Identische Logik wie im Export (collectReportData): Beratungszeitraum = erster–letzter
  // Beratungstag, ohne Tage bleibt er leer (KEIN Fallback auf manuelle Start/Enddaten).
  const buildCard15Html = (): string => buildZeitangabenHtml(consultingDays, consultants);

  const DIRECT_RENDER_CARDS = ['card-1-2', 'card-1-3', 'card-1-5'];
  const DIRECT_RENDER_LABELS: Record<string, string> = {
    'card-1-2': '1.2 Angaben zum Unternehmen',
    'card-1-3': '1.3 Unternehmensdaten',
    'card-1-5': '1.5 Zeitangaben',
  };

  const [directRenderInfo, setDirectRenderInfo] = useState<{ open: boolean; cardId: string }>({ open: false, cardId: '' });

  const buildDirectRenderHtml = (cardId: string): string => {
    if (cardId === 'card-1-2') return buildCard12Html();
    if (cardId === 'card-1-3') return buildCard13Html();
    if (cardId === 'card-1-5') return buildCard15Html();
    return '';
  };

  const handleGenerateSection1Card = (cardId: string) => {
    if (DIRECT_RENDER_CARDS.includes(cardId)) {
      const isPromptEditorActive = getShowPromptEditor();

      if (isPromptEditorActive) {
        // Admin mit Prompt-Editor aktiv: Info-Popup zeigen, dann direkt rendern
        setDirectRenderInfo({ open: true, cardId });
      } else {
        // Nicht-Admin oder Prompt-Editor deaktiviert: Tipps-Popup zeigen
        setTipsPopupOpen(true);
        setTipsPopupError(null);

        const delay = 10000 + Math.random() * 4000;
        setTimeout(() => {
          const html = buildDirectRenderHtml(cardId);
          handlePromptGenerated(html, cardId);
          setTipsPopupOpen(false);
        }, delay);
      }
      return;
    }

    handleGenerateWithPopulate(undefined, cardId);
  };

  const handleDirectRenderConfirm = () => {
    const html = buildDirectRenderHtml(directRenderInfo.cardId);
    handlePromptGenerated(html, directRenderInfo.cardId);
    setDirectRenderInfo({ open: false, cardId: '' });
  };

  const handleSaveSection1Card = (cardId: string) => {
    // Mark card as saved
    setSection1CardTexts(prev => ({
      ...prev,
      [cardId]: {
        ...prev[cardId],
        saved: true
      }
    }));

    // Here you could also:
    // - Save to localStorage
    // - Send to backend
    // - Update global form data
    // - Make data available to other sections
    console.log(`Saved card ${cardId}:`, section1CardTexts[cardId]?.finalText);
  };

  // Handler for saving sections 2-7
  const handleSaveSection = (sectionId: string) => {
    setSectionStates(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        saved: true
      }
    }));
    console.log(`Saved section ${sectionId}:`, sectionStates[sectionId]?.finalText);
  };

  // Handler for updating section state
  const handleUpdateSectionState = (sectionId: string, field: 'aiDraft' | 'finalText' | 'showAiDraft' | 'saved', value: any) => {
    setSectionStates(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [field]: value,
        // Reset saved state when text changes
        ...(field === 'finalText' && { saved: false })
      }
    }));
  };

  // Execution steps entries
  const addExecutionStepsEntry = () => {
    setExecutionStepsEntries([...executionStepsEntries, { text: '' }]);
  };

  const removeExecutionStepsEntry = (index: number) => {
    if (executionStepsEntries.length > 1) {
      setExecutionStepsEntries(executionStepsEntries.filter((_, i) => i !== index));
    }
  };

  const updateExecutionStepsEntry = (index: number, value: string) => {
    const updated = [...executionStepsEntries];
    updated[index].text = value;
    setExecutionStepsEntries(updated);
  };



  // Handler for card-level suggest (Vorschlaege generieren per Card)
  const [cardSuggestLoading, setCardSuggestLoading] = useState<string | null>(null);
  const [cardSuggestPopup, setCardSuggestPopup] = useState<{ sectionId: string; cardId: string; prompt: string } | null>(null);

  const buildCardSuggestPrompt = (sectionId: string, cardId: string): string => {
    const thema = resolveBeratungsthema(formData);
    const name = selectedCustomer?.name || '';
    const branche = selectedCustomer?.wirtschaftszweig || '';
    const geschaeft = formData['geschaeftsgegenstand'] || (selectedCustomer as any)?.geschaeftsgegenstand || '';
    const mitarbeiter = formData['mitarbeiterzahl'] || (selectedCustomer as any)?.mitarbeiterzahl || '';
    const ort = formData['ort'] || selectedCustomer?.ort || '';

    // Website-Analyse Kontext
    let websiteContext = '';
    if ((selectedCustomer as any)?.websiteAnalysisJson) {
      try {
        const analysis = JSON.parse((selectedCustomer as any).websiteAnalysisJson);
        const lines: string[] = [];
        for (const [field, val] of Object.entries(analysis)) {
          if (val && val !== 'null') lines.push(`- ${field}: ${val}`);
        }
        if (lines.length) websiteContext = `\nWEBSITE-ANALYSE:\n${lines.join('\n')}\n`;
      } catch {}
    }

    const unternehmen = `UNTERNEHMEN:\n- Name: ${name}\n- Branche: ${branche}\n- Geschaeftsgegenstand: ${geschaeft}\n- Mitarbeiterzahl: ${mitarbeiter}\n- Ort: ${ort}\n- Beratungsthema: ${thema}\n${websiteContext}`;

    if (cardId === 'card-2-1') {
      const existing = currentStateEntries.filter(e => e.text.trim()).map(e => e.text);
      // Stichpunkte auf neutrale Kurzform reduzieren um Stil-Leak zu vermeiden
      const neutralize = (s: string) => s
        .replace(/^(Aktuell|Derzeit|Es erfolgt|Es werden|Es bestehen|Im Unternehmen|Auf der Website)\s*/i, '')
        .replace(/\.$/, '')
        .trim();
      const exclusionBlock = existing.length
        ? `\nAUSSCHLUSS:\nDie folgenden Inhalte dürfen inhaltlich NICHT erneut erzeugt werden:\n${existing.map(s => `- ${neutralize(s)}`).join('\n')}\n`
        : '';
      return `Erstelle konkrete Stichpunkte zum IST-Zustand eines Unternehmens im Kontext des Beratungsthemas.

ZIEL:
Die Stichpunkte beschreiben ausschließlich den aktuellen Zustand von Prozessen, Systemen und Abläufen im Beratungskontext.
KEINE Aufgaben, KEINE Prüfungen, KEINE Maßnahmen.

ABGRENZUNG (ZWINGEND):
- KEINE Beschreibung von Geschäftsmodell, Angeboten, Zielgruppen oder Unternehmensstruktur
- KEINE Inhalte aus Abschnitt 1.4 (Geschäftsgegenstand)
- KEINE Stammdaten wie Mitarbeiterzahl, Umsatz, Gründung
- Fokus ausschließlich auf Prozesse, Systeme, Kommunikationswege, Datenerfassung und digitale Abläufe

KONTEXTNUTZUNG:
- Die Website-Analyse ist nur eine Informationsquelle, nicht die alleinige Grundlage
- Es dürfen allgemeine, realistische Aussagen zu Abläufen getroffen werden, wenn sie aus Branche und Betriebsgröße plausibel sind
- Es dürfen KEINE konkreten Tools, Systeme, Software oder technischen Details erfunden werden
- KEINE Begriffe wie "branchentypisch", "übliche Systeme", "gängige Tools"

REGELN:
- Genau 4 Stichpunkte erzeugen
- Jeder Stichpunkt beschreibt genau EINEN konkreten Zustand
- Jeder Stichpunkt ist genau EIN vollständiger Satz
- Maximal 18 Wörter pro Stichpunkt
- Nur neutrale Beschreibung – keine Bewertung
- KEINE Maßnahmen oder To-Do-Formulierungen
- KEINE interpretierenden Formulierungen ("hindeutet", "lässt vermuten", "es ist davon auszugehen")
- Jeder Punkt muss direkt zum Beratungsthema passen
- Formuliere sachlich, ohne implizite Risiko- oder Defizitbewertung

FORMULIERUNG:
- Einheitlicher Stil, z. B.:
  "Aktuell…", "Es erfolgt…", "Es werden…", "Im Unternehmen…", "Auf der Website…"
- Keine Stichwortform
- Keine Mehrfachaussagen
- VERBOTEN: "beschränkt sich", "nur", "lediglich", "ausschließlich"

THEMATISCHE BREITE (SEHR WICHTIG):
Die 4 Stichpunkte müssen unterschiedliche Bereiche abdecken:
1. Datenerfassung / Datenverarbeitung
2. Kommunikation / Informationsfluss
3. Prozesse / operative Abläufe
4. System- oder Struktur-Ebene (z. B. Standorte, Organisation von Abläufen)
KEINE Dopplungen oder Varianten desselben Themas (z. B. mehrfach Website)

STRUKTUR:
- Genau 4 Stichpunkte
- Jeder Punkt behandelt einen eigenständigen Aspekt
- Die Punkte müssen sich logisch zu einem Einführungsabsatz verbinden lassen
${exclusionBlock}
${unternehmen}

AUSGABEFORMAT:
{"istZustand":["...","...","...","..."]}

OUTPUT:
- Nur gültiges JSON
- Keine Zusatztexte`;
    }
    if (cardId === 'card-2-2') {
      const existing = keyFindingsEntries.filter(e => e.text.trim()).map(e => e.text);
      const neutralize = (s: string) => s
        .replace(/^(Vorhanden ist|Es bestehen|Im Unternehmen|Die bestehende|Die aktuelle)\s*/i, '')
        .replace(/\.$/, '')
        .trim();
      const exclusionBlock = existing.length
        ? `\nAUSSCHLUSS:\nDie folgenden Inhalte dürfen inhaltlich NICHT erneut erzeugt werden:\n${existing.map(s => `- ${neutralize(s)}`).join('\n')}\n`
        : '';
      // Ist-Zustand als Kontext für Konsistenz
      const istZustandContext = currentStateEntries.filter(e => e.text.trim()).map(e => e.text);
      const istZustandBlock = istZustandContext.length
        ? `\nEINGABEN:\nIst-Zustand:\n${istZustandContext.map(s => `- ${s}`).join('\n')}\n`
        : '';
      return `Erstelle konkrete Stichpunkte zu Stärken und Potenzialen eines Unternehmens im Kontext des Beratungsthemas.

ZIEL:
Die Stichpunkte beschreiben ausschließlich vorhandene positive Strukturen, Systeme, organisatorische Gegebenheiten oder sachlich ableitbare Potenziale im Beratungskontext.
KEINE Schwächen, KEINE Defizite, KEINE Maßnahmen, KEINE Handlungsempfehlungen.

ABGRENZUNG (ZWINGEND):
- KEINE Beschreibung von Geschäftsmodell, Angeboten, Zielgruppen oder Marktposition
- KEINE Inhalte aus Abschnitt 1.4 (Geschäftsgegenstand)
- KEINE Schwächen, Risiken oder Problemformulierungen
- KEINE Maßnahmen oder Entwicklungsvorschläge
- KEINE Stammdaten wie Mitarbeiterzahl, Umsatz oder Gründung als eigenständige Stärke
- Mitarbeiterzahl darf nur indirekt erscheinen (z.B. "an mehreren Standorten"), nicht als Zahl

KONTEXTNUTZUNG:
- Grundlage sind die Inhalte aus Abschnitt 2.1 (Ist-Zustand)
- Die Website-Analyse ist nur ergänzend, nicht führend
- Es dürfen KEINE konkreten Systeme, Tools oder technischen Details erfunden werden
- KEINE Begriffe wie "branchentypisch", "übliche Systeme", "gängige Tools"

REGELN:
- Genau 4 Stichpunkte erzeugen
- Jeder Stichpunkt beschreibt genau EINEN positiven Aspekt
- Jeder Stichpunkt ist genau EIN vollständiger Satz
- Maximal 18 Wörter pro Stichpunkt
- Nur sachliche, leicht positive Formulierung
- KEINE negativen Formulierungen wie "kein", "fehlt", "nicht vorhanden"
- KEINE Maßnahmenverben wie "einführen", "optimieren", "verbessern", "entwickeln"
- KEINE interpretierenden oder ableitenden Formulierungen wie:
  "schafft", "ermöglicht", "bildet die Grundlage", "weist darauf hin", "führt zu"
- Keine allgemeinen oder generischen Aussagen
- Jeder Punkt muss spezifisch genug sein, dass er NICHT auf jedes beliebige Unternehmen passen würde
- KEINE absoluten Wörter wie "vollständig", "immer", "durchgängig", "fest integriert", "komplett"
- Statt absoluter Formulierungen neutrale verwenden: "werden abgewickelt", "sind eingebunden", "erfolgt"

FORMULIERUNG:
- Formuliere natürlich und lesbar, wie ein Berater es schreiben würde
- Nicht technisch-konstruiert oder maschinell klingend
- Einheitlicher Stil, z. B.:
  "Das Website-Kontaktformular…", "Digitale Arbeitsmittel werden…", "Zahlungsprozesse werden…", "Zwischen den Standorten…"
- Keine Stichwortform
- Keine Mehrfachaussagen
- Keine Ausschmückungen

THEMATISCHE BREITE (SEHR WICHTIG):
Die 4 Stichpunkte müssen klar unterscheidbare Bereiche abdecken:
1. Datenerfassung / digitale Eingangspunkte
2. Zahlungsprozesse / operative Systeme
3. Kommunikation / Informationsfluss zwischen Standorten
4. Integration digitaler Arbeitsmittel in den Betrieb
Jeder Punkt muss einen eigenen, klar unterscheidbaren Aspekt behandeln
KEINE Dopplungen oder Varianten desselben Themas

KONSISTENZ:
- Inhalte müssen direkt aus 2.1 ableitbar sein
- Keine Aussagen, die 2.1 widersprechen
- Potenziale nur, wenn sie klar aus vorhandenen Strukturen entstehen
${exclusionBlock}${istZustandBlock}
${unternehmen}

AUSGABEFORMAT:
{"staerken":["...","...","...","..."]}

OUTPUT:
- Nur gültiges JSON
- Keine Zusatztexte
- Genau 4 Stichpunkte`;
    }
    if (cardId === 'card-2-3') {
      const existing = dataSourcesEntries.filter(e => e.text.trim()).map(e => e.text);
      const neutralize = (s: string) => s
        .replace(/^(Aktuell|Derzeit|Es erfolgt|Es werden|Es bestehen|Im Unternehmen)\s*/i, '')
        .replace(/\.$/, '')
        .trim();
      const exclusionBlock = existing.length
        ? `\nAUSSCHLUSS:\nDie folgenden Inhalte dürfen inhaltlich NICHT erneut erzeugt werden:\n${existing.map(s => `- ${neutralize(s)}`).join('\n')}\n`
        : '';
      // Ist-Zustand + Stärken als Kontext
      const istZustandContext = currentStateEntries.filter(e => e.text.trim()).map(e => e.text);
      const staerkenContext = keyFindingsEntries.filter(e => e.text.trim()).map(e => e.text);
      const contextBlock = (istZustandContext.length || staerkenContext.length)
        ? `\nEINGABEN:\n${istZustandContext.length ? `Ist-Zustand (2.1):\n${istZustandContext.map(s => `- ${s}`).join('\n')}\n` : ''}${staerkenContext.length ? `\nStärken (2.2):\n${staerkenContext.map(s => `- ${s}`).join('\n')}\n` : ''}`
        : '';
      return `Erstelle konkrete Stichpunkte zu Schwächen und Handlungsbedarfen eines Unternehmens im Kontext des Beratungsthemas.

ZIEL:
Die Stichpunkte benennen ausschließlich konkrete Defizite und Handlungsbedarfe im Beratungskontext.
KEINE Lösungen, KEINE Maßnahmen, KEINE Empfehlungen.

ABGRENZUNG (ZWINGEND):
- KEINE Lösungsvorschläge oder Maßnahmen (gehört in Abschnitt 5)
- KEINE Stärken oder positiven Bewertungen (gehört in 2.2)
- KEINE Beschreibung von Geschäftsmodell, Zielgruppen oder Marktposition
- Fokus ausschließlich auf Defizite in Prozessen, Systemen und Abläufen im Beratungskontext

KONTEXTNUTZUNG:
- Die Angaben aus Abschnitt 2.1 (Ist-Zustand) und 2.2 (Stärken) sind die wichtigste Grundlage
- Schwächen müssen logisch aus dem Ist-Zustand ableitbar sein
- Die Website-Analyse ist nur eine ergänzende Informationsquelle
- Erfinde KEINE konkreten Systeme, Tools oder internen Details

REGELN:
- Jeder Stichpunkt beschreibt genau EIN konkretes Defizit
- Jeder Stichpunkt ist genau EIN vollständiger Satz
- Maximal 18 Wörter pro Stichpunkt
- Sachlich und konstruktiv formulieren – keine Schuldzuweisungen
- Jeder Punkt muss direkt zum Beratungsthema passen
- KEINE Lösungsverben: einführen, optimieren, verbessern, umsetzen, implementieren
- KEINE generischen Aussagen ohne Konkretisierung

FORMULIERUNG:
- Formuliere als beobachtbaren Zustand, nicht als Vermutung
- Einheitlicher Stil, z. B.:
  "Es bestehen…", "Es erfolgt…", "Geschäftliche Kommunikation…", "Ein dokumentiertes Verfahren…"
- VERBOTEN: "vermutlich", "wahrscheinlich", "nicht erkennbar", "scheint", "möglicherweise"
- VERBOTEN: "nicht erkennbar", "soweit ersichtlich", "nach außen hin"
- Statt "kein X erkennbar" → "Ein X ist nicht vorhanden" oder "X erfolgt ohne Y"

THEMATISCHE BREITE:
- Die Stichpunkte müssen unterschiedliche Schwachstellenbereiche abdecken
- Nicht alle Punkte auf denselben Bereich fokussieren
- Mögliche Aspekte: Zugangsverwaltung, Datenschutz, Netzwerksicherheit, Kommunikationswege, Endgeräte, Mitarbeiterzugänge

KONSISTENZ:
- Die Schwächen müssen mit dem Ist-Zustand aus 2.1 vereinbar sein
- Sie dürfen den Stärken aus 2.2 nicht direkt widersprechen
- Jede Schwäche muss in Abschnitt 4 als Schwachstelle weiterverwendbar sein
${exclusionBlock}${contextBlock}
${unternehmen}

AUSGABEFORMAT:
{"schwaechen":["...","...","...","...","..."]}

OUTPUT:
- Nur gültiges JSON
- Keine Zusatztexte
- 5 bis 6 Stichpunkte`;
    }
    if (cardId === 'card-4-1') {
      const s2Text = section2CardTexts['card-2-3']?.finalText || sectionStates['analysis']?.finalText || '';
      const match = s2Text.match(/2\.3[^]*$/i);
      const cleanText = (match ? match[0] : s2Text).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      const existing = schwachstellenEntries.filter(e => e.problem.trim()).map(e => e.problem);
      const exclusionBlock = existing.length
        ? `\nAUSSCHLUSS:\n\nDie folgenden Schwachstellen dürfen NICHT erneut generiert werden:\n${existing.map(s => `- ${s}`).join('\n')}\n`
        : '';
      return `Übernimm die Schwächen aus Abschnitt 2.3 als formale Schwachstellen.

ZIEL:
Jede Schwäche aus 2.3 wird zu einer eigenständigen, adressierbaren Schwachstelle.
KEINE Lösungen, KEINE Maßnahmen, KEINE neuen Schwachstellen erfinden.

REGELN:

1. Jede Schwäche aus 2.3 einzeln als Schwachstelle übernehmen – nicht zusammenfassen.
2. Nur das PROBLEM beschreiben, nicht das Symptom und nicht die Lösung.
3. KEINE Lösungsverben: einführen, optimieren, verbessern, umsetzen, implementieren, empfehlen
4. KEINE neuen Schwachstellen hinzufügen, die nicht in 2.3 stehen.
5. Jede Schwachstelle muss zum Beratungsthema "${thema}" passen.

FORMULIERUNG (SEHR WICHTIG):

Jeder Stichpunkt MUSS kurz und prägnant sein (maximal 8-10 Wörter).
Formuliere als knappe Problembeschreibung.

Beispiel (richtig):
"Fehlende zentrale Verwaltung der IT-Infrastruktur"
"Kein dokumentiertes Datensicherungskonzept vorhanden"

Beispiel (falsch – Lösung):
"Zentrales IT-Management einführen"

Beispiel (falsch – Symptom):
"Mitarbeiter beschweren sich über IT-Probleme"

SCHWÄCHEN AUS ABSCHNITT 2.3:
${cleanText || '(kein Text vorhanden)'}
${exclusionBlock}
AUSGABEFORMAT:

Gib ausschließlich gültiges JSON zurück:
{"schwachstellen":["...","..."]}

OUTPUT-REGELN:
- Keine zusätzlichen Texte
- Keine Erklärungen
- Kein Text außerhalb des JSON
- Anzahl Schwachstellen = Anzahl Schwächen in 2.3 (1:1-Übernahme)
- Keine Wiederholung oder Umformulierung vorhandener Punkte`;
    }
    if (cardId === 'card-4-2') {
      const schwachstellenList = schwachstellenEntries
        .filter(e => e.problem.trim())
        .map(e => `- ${e.problem}`)
        .join('\n');
      return `Erstelle Themencluster fuer Abschnitt 4.2 \u201EFragestellungen zur Strukturierung der Beratung\u201C.

ZIEL:
Pro Schwachstelle wird EIN Themencluster erzeugt.
Jedes Cluster besteht aus: Clustertitel + zentrale Analyseleitfrage.
Die Analyseleitfrage fasst den methodischen Pruefansatz fuer diese Schwachstelle zusammen.

Schwachstellen:
${schwachstellenList || '(keine Schwachstellen vorhanden)'}

REGELN:
1. Genau EIN Cluster pro Schwachstelle.
2. Clustertitel: kurz, fachlich praezise, keine konstruierten Doppelbegriffe.
   Gut: \u201ENetzwerksegmentierung der Kassensysteme\u201C
   Schlecht: \u201ENetzwerksegmentierung und Kassensystemintegration\u201C
3. Analyseleitfrage: Fasst den Analysefokus des Clusters zusammen.
   Formulierung: \u201ENach welchen Kriterien\u201C, \u201EWelche Verfahren bestehen\u201C, \u201EIn welchem Umfang\u201C, \u201EIn welcher Form\u201C, \u201EWie erfolgt\u201C
   VERBOTEN: \u201EWie ist...?\u201C, \u201EHaben Sie...?\u201C, \u201EGibt es...?\u201C
4. VERBOTEN: Unternehmensname in Titeln oder Fragen.
5. Jede Frage muss sich auf das Beratungsthema \u201E${thema}\u201C beziehen.
6. KEINE themenfremden Cluster (z. B. Marketing, Website \u2013 es sei denn, das IST das Beratungsthema).

FORMAT:
Jeder Eintrag: "Clustertitel: Analyseleitfrage?"
Beispiel: "Zugriffs- und Berechtigungsmanagement: Nach welchen Kriterien erfolgt die Vergabe von Zugriffsrechten auf IT-Systeme?"

KONTEXT (nur zur Einordnung, NICHT in die Fragen einbauen):
- Unternehmen: ${name}
- Beratungsthema: ${thema}

AUSGABEFORMAT:
{"fragestellungen":["Clustertitel: Analyseleitfrage?","..."]}

OUTPUT-REGELN:
- Nur gueltiges JSON, keine Erklaerungen
- Genau ein Eintrag pro Schwachstelle
- Jeder Eintrag endet mit Fragezeichen`;
    }
    return '';
  };

  const handleCardSuggest = (sectionId: string, cardId: string) => {
    const prompt = buildCardSuggestPrompt(sectionId, cardId);
    if (!prompt) return;
    if (user?.isAdmin) {
      setCardSuggestPopup({ sectionId, cardId, prompt });
    } else {
      // Non-Admin: direkter Flow mit Tips-Popup, kein editierbarer Prompt
      runCardSuggest(sectionId, cardId, prompt, { mode: 'direct' });
    }
  };

  const applyCardSuggestItems = (cardId: string, items: any[]) => {
    if (!items.length) return;
    const strings = items.map((item: any) => typeof item === 'string' ? item : (item.text || item.problem || ''));
    // Neue Ergebnisse an bestehende Einträge ANHÄNGEN (nicht ersetzen)
    if (cardId === 'card-2-1') setCurrentStateEntries(prev => {
      const existing = prev.filter(e => e.text.trim());
      return [...existing, ...strings.map(t => ({ text: t }))];
    });
    if (cardId === 'card-2-2') setKeyFindingsEntries(prev => {
      const existing = prev.filter(e => e.text.trim());
      return [...existing, ...strings.map(t => ({ text: t }))];
    });
    if (cardId === 'card-2-3') setDataSourcesEntries(prev => {
      const existing = prev.filter(e => e.text.trim());
      return [...existing, ...strings.map(t => ({ text: t }))];
    });
    if (cardId === 'card-4-1') setSchwachstellenEntries(prev => {
      const existing = prev.filter(e => e.problem.trim());
      return [...existing, ...strings.map(t => ({ id: generateId(), problem: t }))];
    });
    if (cardId === 'card-4-2') {
      setFragestellungenEntries(prev => {
        const existing = prev.filter(e => e.text?.trim());
        const newEntries = [...existing, ...strings.map(t => ({ text: t }))];
        sectionDataRef.current['weaknesses'] = { ...sectionDataRef.current['weaknesses'], fragestellungen: newEntries };
        return newEntries;
      });
    }
  };

  const runCardSuggest = async (
    sectionId: string,
    cardId: string,
    prompt: string,
    opts: { mode: 'popup' | 'direct' },
  ) => {
    setCardSuggestLoading(cardId);
    if (opts.mode === 'direct') {
      setTipsPopupOpen(true);
      setTipsPopupError(null);
    }

    const responseKeys: Record<string, string> = {
      'card-2-1': 'istZustand', 'card-2-2': 'staerken', 'card-2-3': 'schwaechen',
      'card-4-1': 'schwachstellen', 'card-4-2': 'fragestellungen',
    };

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 120000); // 2 Minuten Timeout

      const result = await suggestBulk<any>({
        sectionId,
        customPrompt: prompt,
        context: {
          unternehmen: selectedCustomer?.name || '',
          branche: selectedCustomer?.wirtschaftszweig || '',
          berichtstitel: formData['berichtstitel'] || '',
        },
      }, controller.signal);
      clearTimeout(timeout);

      if (!result.ok) {
        // 409 / 429 / Netzwerk / Abort → einheitliche deutsche Meldung
        const msg = formatGenerationError(result.error);
        if (opts.mode === 'direct') {
          setTipsPopupError(msg);
          // ALREADY_RUNNING offen lassen → „Zurücksetzen"-Button bleibt nutzbar.
          if (result.error.code !== 'ALREADY_RUNNING') {
            const autoCloseMs = result.error.code === 'RATE_LIMITED' ? 8000 : 6000;
            setTimeout(() => { setTipsPopupOpen(false); setTipsPopupError(null); }, autoCloseMs);
          }
          return;
        }
        throw new Error(msg);
      }
      const res: any = result.data;

      const key = responseKeys[cardId] || '';
      let items = res[key];
      if (!items?.length) {
        const firstArr = Object.values(res).find((v): v is string[] => Array.isArray(v) && v.length > 0);
        items = firstArr || [];
      }

      applyCardSuggestItems(cardId, items);

      if (opts.mode === 'direct') {
        setTipsPopupOpen(false);
      } else {
        setCardSuggestPopup(null);
      }
    } catch (err: any) {
      console.error(`[CardSuggest] ${cardId} failed:`, err);
      if (opts.mode === 'direct') {
        setTipsPopupError(err.message || 'Unbekannter Fehler');
        setTimeout(() => { setTipsPopupOpen(false); setTipsPopupError(null); }, 6000);
      } else {
        setAlertMsg(`Vorschläge konnten nicht generiert werden: ${err.message || 'Unbekannter Fehler'}`);
      }
    } finally {
      setCardSuggestLoading(null);
    }
  };

  const handleCardSuggestSubmit = async () => {
    if (!cardSuggestPopup) return;
    const { sectionId, cardId, prompt } = cardSuggestPopup;
    await runCardSuggest(sectionId, cardId, prompt, { mode: 'popup' });
  };

  // Handler for AI text generation (all sections)
  const handleGenerateWithPopulate = async (overrideSectionId?: string, cardIdOverride?: string, opts?: { batch?: boolean; collect?: boolean }) => {
    const targetSectionId = overrideSectionId || activeSection;
    const cardId = cardIdOverride || targetSectionId;

    // Collect dynamic list data for the current section
    let dynamicListData: Record<string, any> = {};
    if (cardId === 'card-1-5') {
      // Zeitangaben: Berater, Beratungstage, auto-berechneter Zeitraum
      const dates = consultingDays.map(d => d.date).filter(d => d).sort();
      dynamicListData = {
        berater: consultants.filter(c => c.name.trim()).map(c => c.name).join(', '),
        beratungstage: consultingDays.filter(d => d.date).map(d => `${d.date} – ${d.hours} Stunden`).join('\n'),
        start: dates.length > 0 ? dates[0] : '',
        ende: dates.length > 0 ? dates[dates.length - 1] : '',
        gesamtStunden: consultingDays.reduce((sum, d) => sum + (parseFloat(d.hours) || 0), 0),
        anzahlTage: consultingDays.filter(d => d.date).length,
      };
    } else if (targetSectionId === 'analysis' || cardId?.startsWith('card-2-')) {
      // Section 2 Cards: Nur die Daten der jeweiligen Card sammeln
      if (cardId === 'card-2-1') {
        dynamicListData = { istZustand: currentStateEntries.filter(e => e.text.trim()) };
      } else if (cardId === 'card-2-2') {
        dynamicListData = { staerken: keyFindingsEntries.filter(e => e.text.trim()) };
      } else if (cardId === 'card-2-3') {
        dynamicListData = { schwaechen: dataSourcesEntries.filter(e => e.text.trim()) };
      } else {
        // Ganzer Abschnitt 2 (z.B. wenn ohne cardId aufgerufen)
        dynamicListData = {
          istZustand: currentStateEntries.filter(e => e.text.trim()),
          staerken: keyFindingsEntries.filter(e => e.text.trim()),
          schwaechen: dataSourcesEntries.filter(e => e.text.trim()),
        };
      }
    } else if (cardId === 'card-4-1') {
      // Card 4.1: Schwachstellen direkt aus schwachstellenEntries lesen (Source of Truth)
      dynamicListData = {
        schwachstellen: schwachstellenEntries
          .filter(e => e.problem.trim())
          .map(e => ({ text: e.problem })),
      };
    } else if (cardId === 'card-4-2') {
      // Card 4.2: Fragestellungen direkt aus fragestellungenEntries lesen (Source of Truth)
      // WICHTIG: Nur fragestellungen übergeben, kein anderer Kontext
      // Die Schwachstellen belegen 4.1 bis 4.N — die Fragestellungen bekommen die
      // naechste freie Nummer, sonst stuenden zwei Unterabschnitte auf 4.2.
      const anzahlSchwachstellen = schwachstellenEntries.filter(e => e.problem.trim()).length;
      dynamicListData = {
        fragestellungen: fragestellungenEntries.filter(e => e.text.trim()),
        abschnittsnummer: anzahlSchwachstellen > 0 ? `4.${anzahlSchwachstellen + 1}` : '',
      };
      // Debug: Logge was tatsächlich übergeben wird
      console.log('[4.2 DEBUG] fragestellungenEntries:', fragestellungenEntries.filter(e => e.text.trim()).map(e => e.text));
    } else if (sectionDataRef.current[targetSectionId]) {
      // Use data from section child components
      const sData = sectionDataRef.current[targetSectionId];
      // Filter out empty entries for arrays (supports both {text} and {problem} formats)
      dynamicListData = Object.fromEntries(
        Object.entries(sData).map(([key, val]) =>
          Array.isArray(val)
            ? [key, (val as Array<any>).filter(e => (e.text || e.problem || e.bezeichnung || '').trim())]
            : [key, val]
        )
      );
    }

    // Vorherige Abschnitte als Kontext einbinden
    if (targetSectionId === 'execution' && sectionStates['analysis']?.finalText) {
      dynamicListData.analyse_abschnitt2 = sectionStates['analysis'].finalText.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }
    // KEIN analyse_abschnitt2 für weaknesses/card-4-1 - verhindert Content-Bleeding
    if (targetSectionId === 'action-plan') {
      // Mapping-Validierung: prüfe ob jede Maßnahme einer gültigen Schwachstelle zugeordnet ist
      const schwachstelleIdSet = new Set(schwachstellenEntries.filter(s => s.problem.trim()).map(s => s.id));
      const mappingIssues: string[] = [];
      recommendationsEntries.filter(m => m.bezeichnung.trim()).forEach((m, i) => {
        if (!m.schwachstelleId) mappingIssues.push(`Maßnahme ${i + 1} "${m.bezeichnung}" hat keine Schwachstellen-Zuordnung`);
        else if (!schwachstelleIdSet.has(m.schwachstelleId)) mappingIssues.push(`Maßnahme ${i + 1} "${m.bezeichnung}" referenziert nicht-existierende Schwachstelle`);
      });
      if (mappingIssues.length > 0) {
        console.warn('[Abschnitt 5] Mapping-Probleme vor Render:', mappingIssues);
      }

      // Gepaarte Struktur: Schwachstelle + zugehörige Maßnahme als Block
      // Die ID-Zuordnung wird bis zum Render durchgetragen
      const schwachstellenFiltered = schwachstellenEntries.filter(s => !isEmptyContent(s.problem));
      if (schwachstellenFiltered.length > 0) {
        // Schwachstellen mit IDs für Render-Prompt
        dynamicListData.schwachstellen_strukturiert = schwachstellenFiltered
          .map((s, i) => `${i + 1}. [ID: ${s.id}] ${htmlToPlainText(s.problem).replace(/\n+/g, ' ')}`)
          .join('\n');

        // Gepaarte Struktur: Schwachstelle → Maßnahme
        const paare = schwachstellenFiltered.map((s, i) => {
          const massnahme = recommendationsEntries.find(m => m.schwachstelleId === s.id);
          const block = [`Schwachstelle ${i + 1} [ID: ${s.id}]: ${htmlToPlainText(s.problem).replace(/\n+/g, ' ')}`];
          if (massnahme && !isEmptyContent(massnahme.bezeichnung)) {
            block.push(`  Massnahme: ${htmlToPlainText(massnahme.bezeichnung).replace(/\n+/g, ' ')}`);
            if (!isEmptyContent(massnahme.beschreibung)) block.push(`  Beschreibung: ${htmlToPlainText(massnahme.beschreibung).replace(/\n+/g, ' ')}`);
            if (massnahme.aufwand) block.push(`  Aufwand: ${massnahme.aufwand}`);
          } else {
            block.push('  Massnahme: (nicht zugeordnet)');
          }
          return block.join('\n');
        });
        dynamicListData.massnahmen_strukturiert = paare.join('\n\n');
      }
    }

    // Load saved prompt: DB first, then localStorage fallback, then default
    let savedPrompt = '';
    try {
      const res = await api.get<any>(`/api/prompts?sectionKey=${targetSectionId}&cardId=${cardId}`);
      // Extrahiere promptText robust aus API-Response
      const extractText = (obj: any): string => {
        if (!obj) return '';
        if (typeof obj === 'string') return obj;
        if (typeof obj === 'object') {
          const pt = obj.promptText;
          if (typeof pt === 'string') return pt;
          if (typeof pt === 'object' && pt) return extractText(pt);
        }
        return '';
      };
      const text = extractText(res);
      if (text) {
        savedPrompt = text;
      }
    } catch (err) {
      console.error('[Prompt-Load] API-Fehler für', cardId, err);
    }

    // Standard-Prompt vom Backend holen (Single Source of Truth)
    let defaultPrompt = '';
    try {
      const defaultRes = await api.get<{ promptText: string }>(`/api/prompts/default?cardId=${cardId}`);
      defaultPrompt = (defaultRes as any)?.promptText || '';
    } catch (err) {
      console.warn('[Prompt-Default] Backend nicht erreichbar, nutze Frontend-Fallback für', cardId);
    }
    // Fallback auf Frontend-Kopie wenn Backend nicht erreichbar
    if (!defaultPrompt) {
      defaultPrompt = getDefaultPromptForCard(cardId);
    }

    const customerData = selectedCustomer ? {
      name: selectedCustomer.name,
      branche: selectedCustomer.wirtschaftszweig,
      rechtsform: selectedCustomer.rechtsform,
      ort: selectedCustomer.ort,
    } : undefined;

    // 'beratungsauftrag' existiert als Formularfeld nicht — der Text steht unter
    // 'consulting-purpose' (1.1-Textarea). Ohne diese Brücke bekam JEDE Karte, deren
    // Prompt {{beratungsauftrag}} referenziert (prompt-config.ts: 2.1–2.3, execution,
    // 4.1, 4.2, action-plan), dort immer einen leeren String (Root Cause LIVIN'ROLL-
    // Vorfall 2026-07-23, zusammen mit dem thema-Fix in buildCardSuggestPrompt oben).
    const beratungsauftrag = formData['consulting-purpose'] || '';

    // Für card-4-1 und card-4-2: NUR die eigenen Daten übergeben, kein formData
    // formData enthält alle Formularfelder des Berichts und verursacht Content-Bleeding
    const isolatedCards = ['card-4-1', 'card-4-2'];
    const mergedData = isolatedCards.includes(cardId)
      ? { ...dynamicListData, beratungsauftrag, zielsetzung: formData['zielsetzung'] || '', berichtstitel: formData['berichtstitel'] || '' }
      : { ...formData, beratungsauftrag, ...dynamicListData };

    // collect-Modus: nur das fertige Daten-Paket zurückgeben (für den serverseitigen
    // Sammel-Job „Bericht komplett erstellen"), NICHT generieren.
    if (opts?.collect) {
      return { cardId, formData: mergedData, customerData, customerId: selectedCustomer?.id };
    }

    let contextPreview = buildContextPreview(cardId, mergedData, customerData);
    // Website-Analyse-Hinweis anhängen wenn vorhanden
    if (selectedCustomer && (selectedCustomer as any).websiteAnalysisStatus === 'done') {
      contextPreview += '\n• Website-Analyse: ✓ verfügbar (wird serverseitig eingebunden)';
    }
    const warnings = validateInputs(cardId, mergedData);

    // Block generation if required fields are missing
    if (warnings.length > 0) {
      if (opts?.batch) throw new Error(`Pflichtfelder fehlen: ${warnings.join(', ')}`);
      setMissingFieldsError({ cardId, warnings });
      return;
    }

    // Block generation if limit reached (non-admin only)
    if (getRemaining(cardId, 'generate') === 0) {
      if (opts?.batch) throw new Error('Generierungs-Limit für diesen Abschnitt erreicht.');
      setTipsPopupOpen(true);
      setTipsPopupError('Limit für diesen Abschnitt erreicht. Wenden Sie sich an den Administrator.');
      setTimeout(() => { setTipsPopupOpen(false); setTipsPopupError(null); }, 6000);
      return;
    }

    // If admin with prompt editor enabled → show popup
    // Otherwise (or in batch mode) → generate directly with saved/default prompt
    if (getShowPromptEditor() && !opts?.batch) {
      setPromptPopupData({
        cardId,
        cardTitle: cardIdOverride
          ? sectionConfigs['section1']?.cards?.find(c => c.id === cardIdOverride)?.title || cardId
          : sectionConfigs[targetSectionId]?.title || targetSectionId,
        prompt: defaultPrompt,
        contextPreview,
        formData: mergedData,
        customerData,
        warnings: warnings.length > 0 ? warnings : undefined,
      });
      setPromptPopupOpen(true);
    } else {
      // Non-admin / Batch: show tips popup during generation (außer im Batch — dort
      // zeigt der „Ganzen Bericht generieren"-Lauf seinen eigenen Fortschritt).
      if (!opts?.batch) {
        setTipsPopupOpen(true);
        setTipsPopupError(null);
      }

      const result = await generateV2({
        cardId,
        // Non-admin: kein sectionPrompt im Body — Backend lädt ihn serverseitig
        // aus DB/Default. Nur der Admin-Popup (PromptPopup.tsx) schickt einen
        // Override, um Live-Edits ohne DB-Save zu testen.
        formData: mergedData,
        customerData: customerData || {},
        customerId: selectedCustomer?.id || undefined,
        reportId: reportId ?? undefined,
      });

      if (!result.ok) {
        // 409 / 429 / Sicherheits-Block / Netzwerk — alle durch formatGenerationError
        // in eine deutsche, endnutzer-taugliche Meldung uebersetzt.
        console.warn('[PROMPT] Direct-Flow fehlgeschlagen:', result.error);
        if (result.error?.code === 'GEN_CARD_LIMIT_REACHED' && reportId) {
          void loadGenCounts(reportId);
        }
        // Batch: Fehler werfen, damit der Sammel-Lauf sofort stoppt.
        if (opts?.batch) throw new Error(formatGenerationError(result.error));
        setTipsPopupError(formatGenerationError(result.error));
        // Bei ALREADY_RUNNING NICHT automatisch schließen — der „Zurücksetzen"-
        // Button bleibt sichtbar, damit der Nutzer den hängenden Lock lösen kann.
        if (result.error.code !== 'ALREADY_RUNNING') {
          const autoCloseMs = result.error.code === 'RATE_LIMITED' ? 8000 : 6000;
          setTimeout(() => { setTipsPopupOpen(false); setTipsPopupError(null); }, autoCloseMs);
        }
        return;
      }

      if (result.warnings && result.warnings.length > 0) {
        console.warn('[PROMPT] Backend-Warnungen:', result.warnings);
      }

      incrementLocalCount(cardId, 'generate');
      if (!opts?.batch) setTipsPopupOpen(false);
      handlePromptGenerated(result.data.text || (result.data as any).content || '', cardId);
    }
  };

  // Mappt eine cardId auf ihren Abschnitt (sectionId) für handleGenerateWithPopulate.
  const cardToSection = (cardId: string): { sectionId: string; cardOverride?: string } => {
    if (cardId.startsWith('card-1-')) return { sectionId: 'section1', cardOverride: cardId };
    if (cardId.startsWith('card-2-')) return { sectionId: 'analysis', cardOverride: cardId };
    if (cardId.startsWith('card-4-')) return { sectionId: 'weaknesses', cardOverride: cardId };
    return { sectionId: cardId }; // execution, action-plan
  };

  // Menschenlesbares Label pro Karte für die Fortschrittsanzeige.
  const cardLabel = (cardId: string): string => {
    const s1 = sectionConfigs['section1']?.cards?.find((c: any) => c.id === cardId);
    if (s1) return s1.title;
    const { sectionId } = cardToSection(cardId);
    const conf = sectionConfigs[sectionId];
    if (conf?.cards) {
      const c = conf.cards.find((c: any) => c.id === cardId);
      if (c) return c.title;
    }
    return conf?.title || cardId;
  };

  // Liest den aktuellen finalText einer Karte aus dem passenden State.
  const getCardFinalText = (cardId: string): string => {
    if (cardId.startsWith('card-1-')) return section1CardTexts[cardId]?.finalText || '';
    if (cardId.startsWith('card-2-')) return section2CardTexts[cardId]?.finalText || '';
    if (cardId.startsWith('card-4-')) return section4CardTexts[cardId]?.finalText || '';
    return sectionStates[cardId]?.finalText || '';
  };

  // Gibt es bereits KI-generierten Fließtext? (Direct-Render-Karten 1.2/1.3/1.5 zählen NICHT,
  // da deren Inhalt nur aus den Kundendaten gerendert wird.) Steuert die Überschreib-Warnung.
  const hasExistingGeneratedText = (): boolean =>
    CARD_SEQUENCE.some(
      (c) => !DIRECT_RENDER_CARDS.includes(c) && getCardFinalText(c).replace(/<[^>]*>/g, '').trim().length > 0,
    );

  // „Bericht komplett erstellen": Der Loop läuft SERVERSEITIG (Backend-Job), damit er das
  // Verlassen des Editors / Schließen des Tabs überlebt. Das Frontend baut nur die Daten-
  // Pakete, startet den Job und pollt den Fortschritt. Die fertigen Texte werden über die
  // normale Speicher-Logik (handlePromptGenerated) übernommen.
  const generateAllSections = async () => {
    if (batchGenerating || !reportId) return;
    setBatchSummary(null);
    setBatchGenerating(true);
    setBatchProgress({ i: 0, total: CARD_SEQUENCE.length, label: 'wird vorbereitet…' });
    // Wer den ganzen Bericht erzeugen lässt, übernimmt auch die beiden Schlussabschnitte.
    bestaetigeSchlussabschnitte();
    try {
      // Pakete bauen: KI-Karten → Daten-Paket (collect), Direct-Render → fertiges HTML.
      const cards: any[] = [];
      for (const cardId of CARD_SEQUENCE) {
        const label = cardLabel(cardId);
        if (DIRECT_RENDER_CARDS.includes(cardId)) {
          cards.push({ cardId, label, kind: 'direct', html: buildDirectRenderHtml(cardId) });
        } else {
          const { sectionId, cardOverride } = cardToSection(cardId);
          const p: any = await handleGenerateWithPopulate(sectionId, cardOverride, { collect: true });
          if (p) cards.push({ cardId, label, kind: 'ai', formData: p.formData, customerData: p.customerData, customerId: p.customerId });
        }
      }
      await api.post(`/api/reports/${reportId}/generate-all`, { cards });
      // Marke für den globalen Header-Indikator (über alle Seiten sichtbar).
      try { localStorage.setItem('activeGenReportId', String(reportId)); } catch { /* ignore */ }
      startJobPolling();
    } catch (e: any) {
      setBatchGenerating(false);
      setBatchProgress(null);
      setBatchSummary([{ label: 'Start fehlgeschlagen', ok: false, reason: e?.message || 'Fehler' }]);
    }
  };

  // Job fertig: Die Texte hat der Backend-Job bereits direkt in den Bericht gespeichert.
  // Hier nur die Zusammenfassung zeigen + Job als gesehen markieren. Das Anzeigen der
  // neuen Texte im Editor erfolgt durch Neuladen (Button im Zusammenfassungs-Popup).
  const applyJobResults = async (job: any) => {
    if (Array.isArray(job.results)) {
      setBatchSummary(job.results.map((r: any) => ({ label: r.label, ok: r.ok, reason: r.reason })));
    }
    try { await api.post(`/api/reports/${reportId}/generate-all/consume`, { jobId: job.id }); } catch { /* ignore */ }
  };

  const stopJobPolling = () => {
    if (jobPollRef.current) { clearInterval(jobPollRef.current); jobPollRef.current = null; }
  };

  const startJobPolling = () => {
    stopJobPolling();
    jobPollRef.current = setInterval(async () => {
      if (!reportId) return;
      try {
        const res = await api.get<{ job: any }>(`/api/reports/${reportId}/generate-all/status`);
        const job = res.job;
        if (!job) { stopJobPolling(); setBatchGenerating(false); setBatchProgress(null); return; }
        if (job.status === 'running') {
          setBatchGenerating(true);
          setBatchProgress({ i: job.done, total: job.total, label: job.currentLabel || '…' });
        } else {
          stopJobPolling();
          setBatchGenerating(false);
          setBatchProgress(null);
          if (!job.consumedAt) await applyJobResults(job);
        }
      } catch { /* weiter pollen */ }
    }, 2000);
  };

  const stopBatchGeneration = async () => {
    if (reportId) { try { await api.post(`/api/reports/${reportId}/generate-all/cancel`, {}); } catch { /* ignore */ } }
    stopJobPolling();
    setBatchGenerating(false);
    setBatchProgress(null);
  };

  // Beim Öffnen eines Berichts: laufenden Sammel-Job erkennen (Polling fortsetzen) oder
  // fertige, noch nicht übernommene Ergebnisse einspielen. So überlebt die Generierung
  // das Verlassen des Editors / Schließen des Tabs.
  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ job: any }>(`/api/reports/${reportId}/generate-all/status`);
        if (cancelled) return;
        const job = res.job;
        if (!job) return;
        if (job.status === 'running') {
          setBatchGenerating(true);
          setBatchProgress({ i: job.done, total: job.total, label: job.currentLabel || '…' });
          startJobPolling();
        } else if (!job.consumedAt && Array.isArray(job.results) && job.results.length > 0) {
          await applyJobResults(job);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; stopJobPolling(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Handle prompt generated callback
  const handlePromptGenerated = (text: string, directCardId?: string) => {
    const cardId = directCardId || promptPopupData?.cardId;
    if (!cardId) return;

    if (cardId.startsWith('card-1-')) {
      setSection1CardTexts(prev => ({
        ...prev,
        [cardId]: {
          ...prev[cardId],
          aiDraft: text,
          finalText: text,
          showAiDraft: true,
        }
      }));
    } else if (cardId.startsWith('card-2-')) {
      setSection2CardTexts(prev => ({
        ...prev,
        [cardId]: { ...prev[cardId], aiDraft: text, finalText: text, showAiDraft: true }
      }));
    } else if (cardId.startsWith('card-4-')) {
      setSection4CardTexts(prev => ({
        ...prev,
        [cardId]: { ...prev[cardId], aiDraft: text, finalText: text, showAiDraft: true }
      }));
    } else {
      setSectionStates(prev => ({
        ...prev,
        [cardId]: {
          ...prev[cardId],
          aiDraft: text,
          finalText: text,
          showAiDraft: true,
        }
      }));
    }

    setPromptPopupOpen(false);
    setPromptPopupData(null);
  };

  // ========== Bulk Suggest ==========
  const handleBulkSuggest = async () => {
    if (!selectedCustomer) return;
    setBulkSuggesting(true);
    try {
      const res = await api.post<{
        istZustand?: string[];
        staerken?: string[];
        schwaechen?: string[];
      }>('/api/prompts/suggest-bulk', {
        customerName: selectedCustomer.name,
        branche: selectedCustomer.wirtschaftszweig,
        geschaeftsgegenstand: formData['geschaeftsgegenstand'] || '',
        beratungsauftrag: formData['consulting-purpose'] || '',
      });

      if (res.istZustand?.length) {
        setCurrentStateEntries(prev => {
          const nonEmpty = prev.filter(e => e.text.trim());
          return [...nonEmpty, ...res.istZustand!.map(t => ({ text: t }))];
        });
      }
      if (res.staerken?.length) {
        setKeyFindingsEntries(prev => {
          const nonEmpty = prev.filter(e => e.text.trim());
          return [...nonEmpty, ...res.staerken!.map(t => ({ text: t }))];
        });
      }
      if (res.schwaechen?.length) {
        setDataSourcesEntries(prev => {
          const nonEmpty = prev.filter(e => e.text.trim());
          return [...nonEmpty, ...res.schwaechen!.map(t => ({ text: t }))];
        });
      }
    } catch (err) {
      console.error('Bulk suggest failed:', err);
    } finally {
      setBulkSuggesting(false);
    }
  };

  // Handle customer selection and autofill — uses CUSTOMER_FIELD_MAP as single source of truth
  const handleCustomerSelect = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    const fields: Record<string, string> = {};
    for (const [formKey, customerKey] of Object.entries(CUSTOMER_FIELD_MAP)) {
      fields[formKey] = customer ? (String((customer as any)[customerKey] || '')) : '';
    }
    setFormData(prevData => ({ ...prevData, ...fields }));
    prevFormDataRef.current = { ...prevFormDataRef.current, ...fields };
  };

  // Handler for form field changes
  const handleFieldChange = (fieldId: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  // ========== Initial Report Loading ==========
  useEffect(() => {
    if (!initialReport) {
      setDataLoaded(true);
      isInitialLoadRef.current = false;
      return;
    }

    const loadReport = async () => {
      try {
        // Load metadata
        const updates: Record<string, string> = {};
        if (initialReport.aktenzeichen) updates.aktenzeichen = initialReport.aktenzeichen;
        if (initialReport.berichtstitel) updates.berichtstitel = initialReport.berichtstitel;
        if (initialReport.startDate) updates.startDate = initialReport.startDate;
        if (initialReport.endDate) updates.endDate = initialReport.endDate;
        if (initialReport.preis != null) setPreis(String(initialReport.preis));
        if (initialReport.mwstEnabled != null) setMwstEnabled(!!initialReport.mwstEnabled);
        if (initialReport.foerderRate === 80) setFoerderRate(80);
        else setFoerderRate(50);

        // Helper: map a customer object to formData using CUSTOMER_FIELD_MAP
        const applyCustomerToUpdates = (c: Customer) => {
          Object.entries(CUSTOMER_FIELD_MAP).forEach(([formKey, customerKey]) => {
            let val: any;
            // Sonderfall: Telefon — versuche neue Felder (phoneVorwahl+Nummer), dann Fallback auf altes Feld
            if (formKey === 'telefon') {
              const vorwahl = (c as any).phoneVorwahl?.trim();
              const nummer = (c as any).phoneNummer?.trim();
              if (vorwahl || nummer) {
                val = [vorwahl, nummer].filter(Boolean).join(' ');
              } else {
                val = (c as any).phone;
              }
            } else if (formKey === 'email') {
              // Verteiler-Email bevorzugen, sonst Stamm-Email
              val = (c as any).verteilerEmail || (c as any).email;
            } else {
              val = (c as any)[customerKey];
            }
            if (val) updates[formKey] = String(val);
          });
        };

        // Fetch fresh customer data from API
        if (initialReport.customer?.id) {
          try {
            const fresh = await api.get<Customer>(`/api/customers/${initialReport.customer.id}`);
            setSelectedCustomer(fresh);
            applyCustomerToUpdates(fresh);
          } catch {
            // Fallback to snapshot
            const c = initialReport.customer;
            setSelectedCustomer(c);
            applyCustomerToUpdates(c);
          }
        } else if (initialReport.customer) {
          const c = initialReport.customer;
          setSelectedCustomer(c);
          applyCustomerToUpdates(c);
        }

        if (Object.keys(updates).length > 0) {
          setFormData(prev => ({ ...prev, ...updates }));
          prevFormDataRef.current = { ...prevFormDataRef.current, ...updates };
        }

        // Load sections and restore dynamic list entries
        if (initialReport.sections && Array.isArray(initialReport.sections)) {
          const newSectionMap: Record<string, number> = {};
          const newCardTexts = { ...section1CardTexts };
          // Nur geänderte Keys sammeln, NICHT von sectionStates spreaden: dieser
          // Effekt läuft async (await weiter oben) und sectionStates ist ab dem
          // Zeitpunkt der Effekt-Erstellung eine stale Closure. Ein voller Replace
          // mit dieser stale Kopie würde zwischenzeitliche Updates aus Kind-
          // Komponenten (z.B. Section6/7-Default-Text beim Mounten) wieder
          // verwerfen. Stattdessen unten per setSectionStates(prev => ...) mergen.
          const newSectionStates: typeof sectionStates = {};

          for (const sec of initialReport.sections) {
            newSectionMap[sec.sectionKey] = sec.id;
            let content: any = {};
            try {
              content = typeof sec.content === 'string' ? JSON.parse(sec.content) : (sec.content || {});
            } catch { content = {}; }

            if (sec.sectionKey === 'section1') {
              // Restore formData from section1 content, but keep fresh customer data for empty fields
              if (content.formData) {
                setFormData(prev => {
                  // Start with current state (includes customer data from applyCustomerToUpdates)
                  const merged = { ...prev };
                  // Apply saved section data, but only non-empty values.
                  // ABER: kanonische Metadaten (Bezeichnung/Aktenzeichen/Daten)
                  // kommen aus dem reports-Datensatz — der section1-Inhalt ist
                  // eine denormalisierte Kopie und kann veraltet sein.
                  for (const [key, val] of Object.entries(content.formData as Record<string, string>)) {
                    if (METADATA_KEYS.includes(key)) continue;
                    if (val && String(val).trim()) {
                      merged[key] = String(val);
                    }
                  }
                  // Re-apply customer data for fields that are still empty
                  for (const [formKey] of Object.entries(CUSTOMER_FIELD_MAP)) {
                    if (!merged[formKey] && updates[formKey]) {
                      merged[formKey] = updates[formKey];
                    }
                  }
                  return merged;
                });
              }
              // Restore card texts
              if (content.cardTexts) {
                for (const [cardId, cardData] of Object.entries(content.cardTexts)) {
                  if (newCardTexts[cardId]) {
                    newCardTexts[cardId] = { ...(newCardTexts[cardId]), ...(cardData as any) };
                  }
                }
              }
              // Restore consulting days and consultants
              if (content.consultingDays && Array.isArray(content.consultingDays) && content.consultingDays.length > 0) {
                setConsultingDays(content.consultingDays);
              }
              if (content.consultants && Array.isArray(content.consultants) && content.consultants.length > 0) {
                setConsultants(content.consultants);
              }
            } else if (sec.sectionKey === 'analysis') {
              // Support both old English keys and new German keys for backwards compatibility
              const ist = content.istZustand || content.currentStateEntries;
              const str = content.staerken || content.keyFindingsEntries;
              const schw = content.schwaechen || content.dataSourcesEntries;
              if (ist?.length) setCurrentStateEntries(ist);
              if (str?.length) setKeyFindingsEntries(str);
              if (schw?.length) setDataSourcesEntries(schw);
              // Restore card texts
              if (content.cardTexts) {
                setSection2CardTexts(prev => {
                  const merged = { ...prev };
                  for (const [cardId, cardData] of Object.entries(content.cardTexts)) {
                    if (merged[cardId]) merged[cardId] = { ...merged[cardId], ...(cardData as any) };
                  }
                  return merged;
                });
              }
              // Migration: old finalText without cards
              if (!content.cardTexts && (sec.finalText || content.finalText)) {
                const ft = sec.finalText || content.finalText || '';
                if (ft) newSectionStates['analysis'] = { aiDraft: '', finalText: ft, showAiDraft: true, saved: false };
              }
            } else if (sec.sectionKey === 'execution') {
              const ft = sec.finalText || content.finalText || '';
              if (ft) newSectionStates['execution'] = { aiDraft: '', finalText: ft, showAiDraft: true, saved: false };
              const steps = content.durchfuehrungsschritte || content.executionStepsEntries;
              if (steps?.length) setExecutionStepsEntries(steps);
              if (content.consultingObjectivesEntries?.length) setConsultingObjectivesEntries(content.consultingObjectivesEntries);
            } else if (sec.sectionKey === 'weaknesses') {
              const ft = sec.finalText || content.finalText || '';
              if (ft) newSectionStates['weaknesses'] = { aiDraft: '', finalText: ft, showAiDraft: true, saved: false };
              // Migration: alte Daten (problembereiche / {text}) → neues Schwachstelle-Format
              const schw = content.schwachstellen || content.problembereiche || content.identifiedIssuesEntries;
              if (schw?.length) setSchwachstellenEntries(schw.map(migrateSchwachstelle));
              // Restore card texts
              if (content.cardTexts) {
                setSection4CardTexts(prev => {
                  const merged = { ...prev };
                  for (const [cardId, cardData] of Object.entries(content.cardTexts)) {
                    if (merged[cardId]) merged[cardId] = { ...merged[cardId], ...(cardData as any) };
                  }
                  return merged;
                });
              }
              // Fragestellungen restore
              if (content.fragestellungen?.length) {
                setFragestellungenEntries(content.fragestellungen);
                sectionDataRef.current['weaknesses'] = {
                  ...sectionDataRef.current['weaknesses'],
                  fragestellungen: content.fragestellungen,
                };
              }
            } else if (sec.sectionKey === 'action-plan') {
              const ft = sec.finalText || content.finalText || '';
              if (ft) newSectionStates['action-plan'] = { aiDraft: '', finalText: ft, showAiDraft: true, saved: false };
              // Migration: alte Daten ({text}) → neues Massnahme-Format
              const mass = content.massnahmen || content.recommendationsEntries;
              if (mass?.length) setRecommendationsEntries(mass.map(migrateMassnahme));
            } else if (sec.sectionKey === 'principles') {
              const ft = sec.finalText || content.finalText || '';
              const wasConfirmed = !!content.confirmed;
              // Bewusst OHNE Auto-Einblenden (Kundenwunsch, 2026-10-12): der Abschnitt bleibt
              // beim Öffnen immer ausgeblendet, auch wenn schon Text/Bestätigung vorliegt —
              // der Inhalt wird trotzdem geladen, damit er beim manuellen Wieder-Einblenden
              // über "+ Grundsätze hinzufügen" korrekt erscheint statt mit dem Standardtext
              // überschrieben zu werden.
              if (ft || wasConfirmed) {
                newSectionStates['principles'] = { aiDraft: '', finalText: ft, showAiDraft: true, saved: false, confirmed: wasConfirmed };
              }
            } else if (sec.sectionKey === 'confirmation') {
              const ft = sec.finalText || content.finalText || '';
              const wasConfirmed = !!content.confirmed;
              if (ft || wasConfirmed) {
                newSectionStates['confirmation'] = { aiDraft: '', finalText: ft, showAiDraft: true, saved: false, confirmed: wasConfirmed };
              }
            }
          }

          setSectionMap(newSectionMap);
          setSection1CardTexts(newCardTexts);
          setSectionStates(prev => ({ ...prev, ...newSectionStates }));
        }

        // initialReport kann ein Kunden-Stub OHNE id sein (Neuer Bericht aus der
        // Kundenakte: /editor?customerId=…). Dann reportId NICHT setzen, sonst
        // wird es `undefined` statt `null` → `reportId === null` greift nicht und
        // der "Neuen Bericht speichern"-Button fehlt. Auch keine /reports/undefined-Calls.
        if (initialReport.id) {
          setReportId(initialReport.id);
          void loadGenCounts(initialReport.id);
          // Load signing status
          api.get<{ status: string } | null>(`/api/reports/${initialReport.id}/signing-request`)
            .then(sr => setSigningStatus(sr ? (sr.status as 'pending' | 'completed') : 'none'))
            .catch(() => {});
        }
        setDataLoaded(true);
      } catch (err) {
        console.error('Failed to load report:', err);
        setDataLoaded(true); // Auch bei Fehler rendern
      } finally {
        // Mark initial load as done after a short delay to let React process state updates
        setTimeout(() => {
          isInitialLoadRef.current = false;
        }, 500);
      }
    };

    loadReport();
  }, [initialReport]);

  // Nach dem Datenload: wenn Fokusmodus an (default), den ersten
  // unvollstaendigen Block aufklappen. Ohne Scroll — sonst springt die
  // Seite direkt nach dem Laden.
  const autoOpenedAfterLoadRef = useRef(false);
  useEffect(() => {
    if (!dataLoaded || autoOpenedAfterLoadRef.current) return;
    autoOpenedAfterLoadRef.current = true;
    if (!focusMode) return;
    setTimeout(() => {
      const next = findNextIncompleteId();
      if (next) setCollapseFor(next, false);
    }, 0);
  }, [dataLoaded]);

  // ========== Auto-Save Functions ==========
  const autoSaveMetadata = useCallback(async () => {
    if (!reportId || isInitialLoadRef.current) return;
    try {
      await api.patch(`/api/reports/${reportId}`, {
        berichtstitel: formData['berichtstitel'] || '',
        aktenzeichen: formData['aktenzeichen'] || '',
      });
    } catch (err) {
      console.error('[AutoSave] metadata failed:', err);
    }
  }, [reportId, formData]);

  // Zentrales Speichern mit sichtbarem Status + automatischem Nachspeichern bei
  // kurzen Aussetzern (z.B. API-Neustart/Netz-Blip): 3 Versuche mit steigendem
  // Abstand. Erst wenn alle scheitern, wird "Fehler beim Speichern" angezeigt.
  const runSave = useCallback(async (doSave: () => Promise<unknown>, label: string) => {
    setSaveStatus('saving');
    let lastErr: unknown;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await doSave();
        setSaveErrorMsg(null);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(prev => (prev === 'saved' ? 'idle' : prev)), 1500);
        return;
      } catch (err) {
        lastErr = err;
        if (attempt < 2) await new Promise(res => setTimeout(res, 700 * (attempt + 1)));
      }
    }
    const reason = lastErr instanceof Error ? lastErr.message : String(lastErr);
    console.error(`[AutoSave] ${label} failed after retries:`, lastErr);
    setSaveErrorMsg(`${label}: ${reason}`);
    setSaveStatus('error');
    setTimeout(() => setSaveStatus(prev => (prev === 'error' ? 'idle' : prev)), 7000);
  }, []);

  const autoSaveSection = useCallback(async (sectionKey: string, content: Record<string, any>) => {
    if (!reportId || isInitialLoadRef.current) return;
    const secId = sectionMap[sectionKey];
    if (!secId) return;
    const payload: Record<string, any> = { content: JSON.stringify(content) };
    // Also save finalText as a separate field if present
    if (content.finalText) payload.finalText = content.finalText;
    await runSave(() => api.patch(`/api/sections/${secId}`, payload), sectionKey);
  }, [reportId, sectionMap, runSave]);

  // Flush all pending saves on unmount or page leave
  const flushSaveRef = useRef<() => void>(() => {});
  useEffect(() => {
    flushSaveRef.current = () => {
      if (!reportId || isInitialLoadRef.current) return;
      // Fire-and-forget: save section1 immediately
      const secId = sectionMap['section1'];
      if (secId) {
        const payload = JSON.stringify({ formData, cardTexts: section1CardTexts, consultingDays, consultants });
        navigator.sendBeacon?.(`${API_BASE}/api/sections/${secId}/beacon`, payload) ||
          fetch(`${API_BASE}/api/sections/${secId}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
            body: JSON.stringify({ content: payload }), keepalive: true,
          }).catch(() => {});
      }
    };
  }, [reportId, sectionMap, formData, section1CardTexts, consultingDays, consultants]);

  useEffect(() => {
    const handleBeforeUnload = () => flushSaveRef.current();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      flushSaveRef.current(); // Also flush on component unmount
    };
  }, []);

  // Auto-save metadata (800ms debounce)
  useEffect(() => {
    if (!reportId || isInitialLoadRef.current) return;
    const changed: Record<string, string> = {};
    for (const key of METADATA_KEYS) {
      if (formData[key] !== undefined && formData[key] !== prevFormDataRef.current[key]) {
        changed[key] = formData[key];
      }
    }
    // NUR die Metadaten-Keys als synchronisiert markieren — nicht das ganze
    // formData übernehmen, sonst wird die Diff-Baseline des Kunden-Syncs (gleiche
    // Ref) bei jeder Änderung gelöscht und Editor→Kunde feuert nie.
    for (const key of METADATA_KEYS) {
      if (formData[key] !== undefined) prevFormDataRef.current[key] = formData[key];
    }
    if (Object.keys(changed).length === 0) return;

    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      void runSave(() => api.patch(`/api/reports/${reportId}`, changed), 'Bezeichnung/Vorgangsnummer');
    }, 800);

    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [formData, reportId, runSave]);

  // Auto-save Förderfelder
  useEffect(() => {
    if (!reportId || isInitialLoadRef.current) return;
    const timer = setTimeout(() => {
      void runSave(() => api.patch(`/api/reports/${reportId}`, {
        preis: preis !== '' ? parseFloat(preis) : null,
        mwstEnabled,
        foerderRate,
      }), 'Förderfelder');
    }, 800);
    return () => clearTimeout(timer);
  }, [preis, mwstEnabled, foerderRate, reportId, runSave]);

  // Auto-save section1 content (includes formData, cardTexts, consultingDays, consultants)
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    const timer = setTimeout(() => {
      autoSaveSection('section1', {
        formData,
        cardTexts: section1CardTexts,
        consultingDays,
        consultants,
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [formData, section1CardTexts, consultingDays, consultants, autoSaveSection]);

  // Auto-save analysis section — keys match Section2Data interface
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    const timer = setTimeout(() => {
      console.log('[AutoSave] Saving analysis section...', {
        istZustand: currentStateEntries.length,
        staerken: keyFindingsEntries.length,
        schwaechen: dataSourcesEntries.length,
        cardTexts: Object.keys(section2CardTexts).filter(k => section2CardTexts[k]?.finalText).length
      });
      autoSaveSection('analysis', {
        cardTexts: section2CardTexts,
        istZustand: currentStateEntries,
        staerken: keyFindingsEntries,
        schwaechen: dataSourcesEntries,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [section2CardTexts, currentStateEntries, keyFindingsEntries, dataSourcesEntries, autoSaveSection]);

  // Auto-save execution section — keys match Section3Data interface
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    const timer = setTimeout(() => {
      autoSaveSection('execution', {
        finalText: sectionStates['execution']?.finalText || '',
        durchfuehrungsschritte: executionStepsEntries,
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [sectionStates['execution']?.finalText, executionStepsEntries, autoSaveSection]);

  // Auto-save weaknesses section
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    const timer = setTimeout(() => {
      autoSaveSection('weaknesses', {
        cardTexts: section4CardTexts,
        schwachstellen: schwachstellenEntries,
        fragestellungen: fragestellungenEntries,
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [section4CardTexts, schwachstellenEntries, autoSaveSection]);

  // Auto-save action-plan section — keys match Section5Data interface
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    const timer = setTimeout(() => {
      autoSaveSection('action-plan', {
        finalText: sectionStates['action-plan']?.finalText || '',
        massnahmen: recommendationsEntries,
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [sectionStates['action-plan']?.finalText, recommendationsEntries, autoSaveSection]);

  // Auto-save principles section
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    const timer = setTimeout(() => {
      autoSaveSection('principles', {
        finalText: sectionStates['principles']?.finalText || '',
        confirmed: !!sectionStates['principles']?.confirmed,
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [sectionStates['principles']?.finalText, sectionStates['principles']?.confirmed, autoSaveSection]);

  // Auto-save confirmation section
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    const timer = setTimeout(() => {
      autoSaveSection('confirmation', {
        finalText: sectionStates['confirmation']?.finalText || '',
        confirmed: !!sectionStates['confirmation']?.confirmed,
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [sectionStates['confirmation']?.finalText, sectionStates['confirmation']?.confirmed, autoSaveSection]);

  // Bidirectional customer sync (1500ms debounce)
  useEffect(() => {
    if (!selectedCustomer?.id) return;
    // Während des initialen Ladens (inkl. nachgelagerter Feld-Normalisierung wie
    // Datums-/Namensformatierung) NUR die Baseline mitführen, NICHT syncen. Sonst
    // würden beim ersten echten Edit auch nur normalisierte (nicht vom Nutzer
    // geänderte) Felder wie gruendung/name mitgeschrieben → Datenkorruption.
    if (isInitialLoadRef.current) {
      for (const [formKey] of Object.entries(CUSTOMER_FIELD_MAP)) {
        if (formData[formKey] !== undefined) prevFormDataRef.current[formKey] = formData[formKey];
      }
      return;
    }
    // Neue Diffs gegen die zuletzt synchronisierte Baseline ermitteln und in die
    // Pending-Queue legen. Baseline pro Key sofort nachziehen, damit auch ein
    // Zurücksetzen auf den ursprünglichen Wert als Änderung erkannt wird.
    for (const [formKey, customerKey] of Object.entries(CUSTOMER_FIELD_MAP)) {
      const val = formData[formKey];
      if (val !== undefined && val !== prevFormDataRef.current[formKey]) {
        // Nur-Lese-Felder: Baseline mitziehen, aber NICHT in die Stammdaten zurückschreiben.
        if (!CUSTOMER_READONLY_FIELDS.has(formKey)) pendingCustomerRef.current[customerKey] = val;
        prevFormDataRef.current[formKey] = val;
      }
    }
    // Solange etwas aussteht (auch aus früheren Renders), Debounce neu setzen.
    // Bei leerer Queue NICHT den Timer löschen-und-nicht-neu-setzen riskieren.
    if (Object.keys(pendingCustomerRef.current).length === 0) return;

    const customerId = selectedCustomer.id;
    if (customerSyncTimer.current) clearTimeout(customerSyncTimer.current);
    customerSyncTimer.current = setTimeout(async () => {
      const payload = pendingCustomerRef.current;
      pendingCustomerRef.current = {};
      try {
        await api.patch(`/api/customers/${customerId}`, payload);
      } catch (err) {
        console.error('Customer sync failed:', err);
        // Bei Fehler zurück in die Queue, damit die nächste Änderung erneut sendet.
        pendingCustomerRef.current = { ...payload, ...pendingCustomerRef.current };
      }
    }, 1500);

    return () => { if (customerSyncTimer.current) clearTimeout(customerSyncTimer.current); };
  }, [formData, selectedCustomer]);

  // Create new report handler (with guard against double-creation)
  const handleCreateReport = async () => {
    if (reportCreatingRef.current) return;
    if (!selectedCustomer) {
      setAlertMsg('Bitte wählen Sie zuerst einen Kunden aus.');
      return;
    }
    if (!formData['berichtstitel']?.trim()) {
      setAlertMsg('Bitte geben Sie einen Berichtstitel ein.');
      return;
    }
    if (!formData['aktenzeichen']?.trim()) {
      setAlertMsg('Bitte geben Sie eine Vorgangsnummer ein.');
      return;
    }
    reportCreatingRef.current = true;
    try {
      setSaveStatus('saving');
      // Wird der Bericht aus einem Vorgang heraus angelegt (/editor?vorgangId=…), an den Vorgang binden.
      const vorgangIdParam = new URLSearchParams(window.location.search).get('vorgangId');
      const result = await api.post<any>('/api/reports', {
        berichtstitel: formData['berichtstitel'],
        aktenzeichen: formData['aktenzeichen'],
        customerId: selectedCustomer.id,
        startDate: formData['startDate'] || null,
        endDate: formData['endDate'] || null,
        ...(vorgangIdParam ? { vorgangId: Number(vorgangIdParam) } : {}),
      });
      setReportId(result.id);
      if (result.sections && Array.isArray(result.sections)) {
        const map: Record<string, number> = {};
        result.sections.forEach((s: any) => {
          if (s.sectionKey && s.id) map[s.sectionKey] = s.id;
        });
        setSectionMap(map);
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(prev => prev === 'saved' ? 'idle' : prev), 2000);
    } catch (err: any) {
      console.error('Create report failed:', err);
      setAlertMsg(err.message || 'Fehler beim Erstellen des Berichts.');
      setSaveStatus('idle');
    } finally {
      reportCreatingRef.current = false;
    }
  };

  // Set default values for section1 fields
  useEffect(() => {
    const section1Config = sectionConfigs['section1'];
    if (section1Config.cards) {
      const defaultValues: Record<string, string> = {};
      section1Config.cards.forEach(card => {
        card.fields.forEach(field => {
          if (field.defaultValue && !formData[field.id]) {
            defaultValues[field.id] = field.defaultValue;
          }
        });
      });
      if (Object.keys(defaultValues).length > 0) {
        setFormData(prev => ({ ...prev, ...defaultValues }));
      }
    }
  }, []); // Run only once on mount

  return (
    <>
    <div className="flex flex-1 relative min-w-0">
      {/* MOBILE STEPPER NAVIGATION - Only visible on mobile */}
      <div className={`lg:hidden fixed left-0 top-[72px] z-40 w-7 flex flex-col items-center py-3 gap-3`}>
        {sections.map((section) => {
          const isActive = activeSection === section.id;
          const progress = getSectionProgress(section.id);
          const isCompleted = progress === 100;
          return (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className="flex items-center justify-center"
              title={section.title}
              style={section.id === 'principles' ? principlesHiddenStyle : undefined}
            >
              <div className={`rounded-full transition-all ${
                isActive
                  ? 'w-3 h-3 bg-blue-500 shadow-[0_0_6px_2px_rgba(59,130,246,0.5)]'
                  : isCompleted
                  ? 'w-2 h-2 bg-green-500'
                  : darkMode ? 'w-2 h-2 bg-slate-600' : 'w-2 h-2 bg-slate-300'
              }`} />
            </button>
          );
        })}
      </div>

      {/* MOBILE OVERLAY */}
      {isMobileSidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40 transition-opacity"
          onClick={() => setIsMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* LEFT SIDEBAR - Desktop & Mobile Slide-In */}
      <div className={`
        ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        fixed lg:sticky inset-y-0 left-0 z-50 lg:z-auto lg:top-14 lg:h-[calc(100vh-3.5rem)]
        w-[220px] border-r overflow-y-auto
        transition-transform duration-300 ease-in-out
        ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}
      `}>
        {/* Close button for mobile */}
        <div className="lg:hidden flex justify-end p-4">
          <button
            onClick={() => setIsMobileSidebarOpen(false)}
            className={`p-2 rounded-lg transition-colors ${
              darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'
            }`}
            aria-label="Menü schließen"
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`} />
          </button>
        </div>
        
        <div className="px-2 pb-3 lg:px-2 lg:pb-3 lg:pt-8 lg:sticky lg:top-0">
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h2 className={`text-base lg:text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Berichtabschnitte
            </h2>
            <HilfeLink anleitungId="bericht-schreiben" label="Anleitung: Bericht schreiben" />
          </div>
          
          {/* Kunde & Bericht Info */}
          {(selectedCustomer?.name || initialReport?.customer?.name || formData['berichtstitel'] || initialReport?.berichtstitel) && (
            <div className={`mb-4 lg:mb-6 p-3 lg:p-4 rounded-lg border ${
              darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'
            }`}>
              {(selectedCustomer?.name || initialReport?.customer?.name) && (
                <div className="mb-2">
                  <span className={`text-[12px] font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    KUNDE
                  </span>
                  <p className={`text-sm font-medium truncate ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                    {selectedCustomer?.name || initialReport?.customer?.name}
                  </p>
                </div>
              )}
              {(formData['berichtstitel'] || initialReport?.berichtstitel) && (
                <div>
                  <span className={`text-[12px] font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    BERICHT
                  </span>
                  <p className={`text-sm font-medium truncate ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                    {formData['berichtstitel'] || initialReport?.berichtstitel}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Fokusmodus Toggle — oeffnet nur die naechste zu bearbeitende Karte */}
          <div className={`relative mb-3 lg:mb-4 p-2.5 rounded-lg border ${
            darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'
          }`}>
            <div className="flex items-center justify-between gap-3 select-none">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className={`text-xs font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Fokusmodus
                </span>
                <button
                  type="button"
                  onClick={() => setFocusInfoOpen(v => !v)}
                  aria-label="Was ist Fokusmodus?"
                  className={`p-0.5 rounded transition-colors ${
                    darkMode
                      ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                </button>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={focusMode}
                onClick={toggleFocusMode}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 cursor-pointer ${
                  focusMode
                    ? 'bg-blue-600'
                    : darkMode ? 'bg-slate-600' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    focusMode ? 'translate-x-[18px]' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Der nächste Schritt — steht ÜBER dem Generieren, weil er der wahrscheinlichere
                Klick ist, sobald der Bericht steht. */}
            {!batchGenerating && (
              <div className={`mt-2.5 pt-2.5 border-t ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                <button
                  type="button"
                  onClick={() => {
                    if (fassungVeraltet || naechsterSchritt === 'speichern') handleSavePdf();
                    else if (naechsterSchritt === 'unterschrift_anfordern') oeffneBerichtVersand(selectedCustomer?.email ?? '');
                    else handleVnUnterschrift();
                  }}
                  disabled={exportingFormat !== null || berichtSpeichert || vnPruefung}
                  title={fassungVeraltet
                    ? 'Die abgelegte Fassung gibt den heutigen Text nicht mehr wieder'
                    : naechsterSchritt === 'speichern'
                    ? 'Erzeugt das Bericht-PDF und legt es beim Vorgang ab'
                    : naechsterSchritt === 'unterschrift_anfordern'
                      ? 'Den gespeicherten Bericht dem Kunden zur Unterschrift schicken'
                      : 'Den Verwendungsnachweis dem Kunden zur Unterschrift schicken'}
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg disabled:opacity-50 text-white transition-colors ${
                    fassungVeraltet ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                >
                  {exportingFormat === 'pdf' && naechsterSchritt === 'speichern' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {/* „neu erzeugen", nicht „generieren": der Knopf baut nur die PDF neu —
                      die Texte bleiben, wie sie sind. „Generieren" las sich wie eine
                      KI-Neuerstellung des Berichts. */}
                  {fassungVeraltet ? 'Bericht-PDF neu erzeugen'
                    : naechsterSchritt === 'speichern' ? 'Bericht PDF generieren'
                    : naechsterSchritt === 'unterschrift_anfordern' ? 'Bericht zur Unterschrift senden'
                    : 'VN Unterschrift anfordern'}
                </button>
                {/* Schon verschickt? Dann steht darunter, wann zuletzt — der Knopf bleibt
                    trotzdem bedienbar: Nachfassen ist der Normalfall, nicht die Ausnahme. */}
                {fassungVeraltet ? (
                  <p className="mt-1 text-[11px] text-red-600 dark:text-red-400 text-center">
                    Es gab Änderungen seit der letzten Fassung vom {fassungDatum}
                    {signingStatus !== 'none' && ' — der Kunde sieht noch die alte'}
                  </p>
                ) : naechsterSchritt !== 'speichern' && letzteAnforderung ? (
                  <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500 text-center">
                    zuletzt am {letzteAnforderung} angefordert
                  </p>
                ) : null}
              </div>
            )}

            {/* Ganzen Bericht generieren — Text links, Start-Knopf rechts, im Fokusmodus-Rahmen.
                Liegt der Bericht beim Kunden, entfällt der Knopf: „neu erstellen" hieße dann,
                die Texte zu überschreiben, während draußen eine andere Fassung unterschrieben
                wird. Einzelne Abschnitte lassen sich weiter erzeugen. */}
            {(neuErstellenErlaubt || batchGenerating) && (
            <div className={`mt-2.5 pt-2.5 border-t ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
              {!batchGenerating ? (
                <button
                  type="button"
                  onClick={() => setBatchConfirm(true)}
                  title={berichtGefuellt
                    ? 'Erzeugt alle Abschnitte neu und überschreibt die vorhandenen Texte'
                    : 'Generiert alle Abschnitte des Berichts nacheinander automatisch'}
                  className={`w-full inline-flex items-center justify-center px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
                    berichtGefuellt
                      ? darkMode
                        ? 'border border-slate-600 text-slate-300 hover:bg-slate-700'
                        : 'border border-slate-300 text-slate-600 hover:bg-slate-100'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {berichtGefuellt ? 'Bericht neu erstellen' : 'Bericht komplett erstellen'}
                </button>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 min-w-0">
                      <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                      <span className="truncate">{batchProgress ? `${batchProgress.i}/${batchProgress.total}: ${batchProgress.label}` : 'läuft…'}</span>
                    </span>
                    <button
                      type="button"
                      onClick={stopBatchGeneration}
                      className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                    >
                      <X className="w-3 h-3" /> Stoppen
                    </button>
                  </div>
                  {batchProgress && (
                    <div className="h-1.5 w-full rounded-full bg-blue-200 dark:bg-blue-900 overflow-hidden">
                      <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${Math.round((batchProgress.i / batchProgress.total) * 100)}%` }} />
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {focusInfoOpen && (
              <>
                {/* Click-outside catcher */}
                <div className="fixed inset-0 z-30" onClick={() => setFocusInfoOpen(false)} />
                <div
                  className={`absolute left-0 right-0 top-full mt-1 z-40 rounded-lg shadow-lg border p-3 text-xs leading-relaxed ${
                    darkMode ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
                  }`}
                >
                  <p className="font-semibold mb-1.5">Fokusmodus</p>
                  <p className="mb-2">
                    Im Fokusmodus ist immer nur <strong>ein</strong> Block aufgeklappt — der naechste, an dem Du arbeiten musst. Sobald Du ihn ausgefuellt hast, klappt automatisch der naechste noch leere Block auf und scrollt dorthin.
                  </p>
                  <p>
                    Schalte Fokusmodus aus, um alle Bloecke gleichzeitig zu sehen und frei zu navigieren.
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Overall Progress — temporaer ausgeblendet */}
          <div className={`hidden mb-3 lg:mb-4 p-2.5 rounded-lg border ${
            darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'
          }`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className={`text-xs font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Gesamtfortschritt
              </span>
              <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {Math.round(sections.reduce((acc, section) => acc + getSectionProgress(section.id), 0) / sections.length)}%
              </span>
            </div>
            <div className={`h-1.5 rounded-full overflow-hidden ${
              darkMode ? 'bg-slate-600' : 'bg-slate-200'
            }`}>
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-300"
                style={{
                  width: `${Math.round(sections.reduce((acc, section) => acc + getSectionProgress(section.id), 0) / sections.length)}%`
                }}
              />
            </div>
          </div>

          <div className="space-y-1">
            {sections.map((section) => {
              const isActive = activeSection === section.id;
              const progress = getSectionProgress(section.id);
              const isDisabled = section.id === 'principles' && !includePrinciples;
              const stepperNumber = !includePrinciples && section.number > 6 ? section.number - 1 : section.number;

              return (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-2 py-2 rounded-lg transition-colors ${
                    isDisabled
                      ? 'opacity-40'
                      : isActive
                      ? darkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'
                      : darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'
                  } border ${
                    isActive && !isDisabled
                      ? darkMode ? 'border-blue-700' : 'border-blue-200'
                      : 'border-transparent'
                  }`}
                  style={isDisabled ? principlesHiddenStyle : undefined}
                >
                  <div className="flex items-start gap-2">
                    <div className="relative flex-shrink-0 mt-0.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                        isDisabled
                          ? darkMode ? 'bg-slate-700 text-slate-500' : 'bg-slate-200 text-slate-400'
                          : progress === 100
                          ? 'bg-green-500 text-white'
                          : isActive
                          ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                          : darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {stepperNumber}
                      </div>
                      {progress === 100 && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-slate-800">
                          <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className={`text-xs font-medium ${
                          darkMode ? 'text-slate-200' : 'text-slate-800'
                        }`}>
                          {section.title}
                        </div>
                        <div className={`text-xs font-semibold ml-2 shrink-0 ${
                          progress === 100
                            ? 'text-green-500'
                            : darkMode ? 'text-slate-300' : 'text-slate-500'
                        }`}>
                          {progress}%
                        </div>
                      </div>
                      {/* Progress Bar */}
                      <div className={`h-1 rounded-full overflow-hidden ${
                        darkMode ? 'bg-slate-700' : 'bg-slate-200'
                      }`}>
                        <div
                          className={`h-full transition-all duration-300 ${
                            progress === 100 ? 'bg-green-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      {section.id === 'section1' && (
                        <div className={`text-[12px] mt-0.5 ${
                          darkMode ? 'text-slate-500' : 'text-slate-400'
                        }`}>
                          {Object.entries(section1CardTexts).filter(([key, card]) => ['card-1-1','card-1-2','card-1-3','card-1-4','card-1-5'].includes(key) && hasRealContent(card.finalText)).length} von 5 fertig
                        </div>
                      )}
                      {section.id !== 'section1' && (
                        <div className={`text-[12px] mt-0.5 ${
                          darkMode ? 'text-slate-500' : 'text-slate-400'
                        }`}>
                          {/* Mehrkarten-Abschnitte (Analyse, Schwachstellen) tragen den Text in den
                              Karten, nicht im finalText — sonst stünde „Noch kein Inhalt" neben 100 %. */}
                          {progress > 0 || hasRealContent(sectionStates[section.id]?.finalText) ? 'Text vorhanden' : 'Noch kein Inhalt'}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Abschluss: ESF-Fragebogen + Dokumente, durch eine Linie von den Inhalts-Abschnitten getrennt */}
          {(() => {
            const letzteNr = sections.length - (!includePrinciples && sections.some(s => s.id === 'principles') ? 1 : 0);
            const esfStatus: { text: string; ok: boolean } = !esfEnabled
              ? { text: 'Nicht benötigt', ok: true }
              : esfFileId != null || vorgangKategorien.includes('ESF Fragebogen')
              ? { text: 'Ausgefüllt', ok: true }
              : { text: 'Fehlt', ok: false };
            const dokStatus: { text: string; ok: boolean; warn?: boolean } = !fassung?.pdfAm
              ? { text: 'Noch nicht erzeugt', ok: false }
              : fassung.veraltet
              ? { text: 'Nicht aktuell', ok: false, warn: true }
              : { text: 'PDF erstellt', ok: true };
            const eintraege = [
              { key: 'esf', nr: letzteNr + 1, titel: 'ESF-Fragebogen', status: esfStatus, ref: esfRef },
              { key: 'dokumente', nr: letzteNr + 2, titel: 'Dokumente', status: dokStatus, ref: exportRef },
            ];
            return (
              <div className={`mt-3 pt-3 border-t space-y-1 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                {eintraege.map(e => (
                  <button
                    key={e.key}
                    onClick={() => scrollToRef(e.ref.current)}
                    className={`w-full text-left px-2 py-2 rounded-lg transition-colors border border-transparent ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="relative flex-shrink-0 mt-0.5">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                          e.status.ok
                            ? 'bg-green-500 text-white'
                            : 'warn' in e.status && e.status.warn
                            ? 'bg-amber-500 text-white'
                            : darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {e.nr}
                        </div>
                        {e.status.ok && (
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-slate-800">
                            <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{e.titel}</div>
                        <div className={`text-[12px] mt-0.5 ${
                          e.status.ok
                            ? 'text-green-600'
                            : 'warn' in e.status && e.status.warn
                            ? 'text-amber-600'
                            : darkMode ? 'text-slate-500' : 'text-slate-400'
                        }`}>
                          {e.status.text}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div ref={scrollContainerRef} className={`flex-1 min-w-0 overflow-y-auto overflow-x-hidden pt-8 md:pt-8 ${darkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
        <div className="max-w-7xl mx-auto p-4 md:p-8 pt-0 lg:pt-0 pl-8 lg:pl-4 md:pl-8 pb-28 min-w-0">

        {/* Intro banner */}
        {showIntroBanner && (
          <div className={`flex items-start gap-3 mb-6 px-4 py-3 rounded-xl border text-sm ${
            darkMode
              ? 'bg-blue-900/20 border-blue-800 text-blue-300'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            <span className="text-base mt-0.5">💡</span>
            <p className="flex-1 leading-relaxed">
              Fülle die Felder aus und nutze <strong>„Text generieren"</strong> oder <strong>„Vorschläge generieren"</strong>, um KI-Texte zu erstellen. Einzelne Texte lassen sich danach per KI verfeinern. Der Bericht wird <strong>automatisch gespeichert</strong>.
            </p>
            <button
              onClick={dismissIntroBanner}
              className={`flex-shrink-0 p-1 rounded-md transition-colors ${
                darkMode ? 'hover:bg-blue-800 text-blue-400' : 'hover:bg-blue-100 text-blue-600'
              }`}
              aria-label="Schließen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Nicht an dataLoaded koppeln: sonst mountet die Karte erst nach dem
            Async-Load und schiebt die Sektionen nach unten ("springt dazu").
            Von Anfang an rendern → fester Platz, kein Layout-Sprung. */}
        <PruefDokumentKarte
          reportId={reportId}
          darkMode={darkMode}
          onApply={handleApplyExtraction}
        />

        {/* Render ALL sections sequentially */}
        <div className="space-y-12 md:space-y-16 min-w-0">
          {sections.map((section, index) => {
            const config = sectionConfigs[section.id];
            if (!config) return null;
            // Adjust display number when Section 6 is excluded
            const displayNumber = !includePrinciples && section.number > 6 ? section.number - 1 : section.number;
            const displayTitle = displayNumber !== section.number
              ? config.title.replace(/^\d+\./, `${displayNumber}.`)
              : config.title;

            const isCollapsed = !!collapsedSections[section.id];
            const isMultiCard = MULTI_CARD_SECTIONS.has(section.id);
            const totalSections = includePrinciples ? 7 : 6;
            const strippedTitle = config.title.replace(/^\d+\.\s*/, '');
            const isHiddenPrinciples = section.id === 'principles' && !includePrinciples;

            return (
              <div key={section.id}>
              <div className={isHiddenPrinciples ? 'grid grid-rows-[0fr]' : undefined}>
              <div className={isHiddenPrinciples ? 'overflow-hidden' : undefined}>
                <div
                  id={`section-${section.id}`}
                  data-section-id={section.id}
                  ref={(el) => (sectionRefs.current[section.id] = el)}
                  className="scroll-mt-24 md:scroll-mt-32"
                  onBlur={isMultiCard ? undefined : (e) => {
                    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                    // If focus moved to a specific other card/section the user
                    // is navigating themselves — don't auto-advance/scroll.
                    const r = e.relatedTarget as HTMLElement | null;
                    if (r && (r.closest('[data-card-id]') || r.closest('[data-section-id]'))) return;
                    // relatedTarget is null for many in-card mouse interactions
                    // (e.g. a plain <button> click on WebKit/macOS doesn't move
                    // keyboard focus there). Re-check activeElement a tick later
                    // instead of trusting the synchronous relatedTarget.
                    const container = e.currentTarget;
                    setTimeout(() => {
                      if (container.contains(document.activeElement)) return;
                      handleCardComplete(section.id);
                    }, 0);
                  }}
                >
                  {/* Section Header — pure heading, NOT a collapse trigger */}
                  <div className="flex items-center justify-between gap-4 py-3 px-3 -mx-3">
                    <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                      {/* Step number circle — matches sidebar stepper */}
                      <div className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center text-sm md:text-base font-semibold flex-shrink-0 ${
                        section.id === 'principles' && !includePrinciples
                          ? darkMode ? 'bg-slate-700 text-slate-500' : 'bg-slate-200 text-slate-400'
                          : getSectionProgress(section.id) === 100
                          ? 'bg-green-500 text-white'
                          : darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {displayNumber}
                      </div>
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <h1 className={`text-xl sm:text-2xl md:text-3xl font-semibold break-words transition-colors ${
                          section.id === 'principles' && !includePrinciples
                            ? darkMode ? 'text-slate-600 line-through' : 'text-slate-300 line-through'
                            : darkMode ? 'text-white' : 'text-slate-900'
                        }`}>
                          {section.title}
                        </h1>
                        <p className={`text-sm break-words leading-snug ${
                          section.id === 'principles' && !includePrinciples
                            ? darkMode ? 'text-slate-600 line-through' : 'text-slate-300 line-through'
                            : darkMode ? 'text-slate-400' : 'text-slate-500'
                        }`}>
                          {strippedTitle}
                        </p>
                      </div>
                    </div>
                    {section.id === 'principles' && (
                      <label className={`flex items-center gap-2 cursor-pointer select-none text-sm font-medium shrink-0 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        <input
                          translate="no"
                          type="checkbox"
                          checked={includePrinciples}
                          onChange={(e) => setIncludePrinciples(e.target.checked)}
                          className="w-4 h-4 rounded accent-blue-600 cursor-pointer"
                        />
                        Im Bericht einschließen
                      </label>
                    )}
                  </div>

                  {/* Helper text — always visible (outside any collapse) */}
                  <div className={`mb-6 md:mb-10 mt-4 min-w-0`}>
                    <div className={`flex items-start gap-2.5 px-4 py-2.5 rounded-xl border text-sm ${
                      darkMode
                        ? 'bg-blue-900/20 border-blue-800 text-blue-300'
                        : 'bg-blue-50 border-blue-200 text-blue-800'
                    }`}>
                      <span className="mt-0.5">💡</span>
                      <p className="leading-relaxed">{config.helperText}</p>
                    </div>
                  </div>

                  {/* Inner-card wrapper: for single sections, gives a clickable
                      header bar (chevron) that toggles `collapsedSections[id]`.
                      Multi-card sections render their content directly — the
                      inner cards handle their own collapse. */}
                  {!isMultiCard && (
                    <div
                      className={`flex items-center justify-between px-6 md:px-8 py-4 cursor-pointer select-none transition-colors border shadow-sm ${
                        isCollapsed ? 'rounded-lg' : 'rounded-t-lg border-b-0'
                      } ${
                        darkMode ? 'bg-slate-800/50 border-slate-700 hover:bg-slate-700/60' : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                      onClick={() => toggleSection(section.id)}
                    >
                      <h3 className={`text-base md:text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {strippedTitle}
                      </h3>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        {isCollapsed && isCardComplete(section.id) && (
                          <span
                            title="Inhalt vorhanden"
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[12px] font-medium ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            Inhalt
                          </span>
                        )}
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''} ${darkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                      </div>
                    </div>
                  )}
                  <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${(!isMultiCard && isCollapsed) ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'}`}>
                  <div className="overflow-hidden">
                  <div className={!isMultiCard
                    ? `px-6 md:px-8 pb-6 md:pb-8 pt-2 ${isCollapsed ? '' : `rounded-b-lg border border-t-0 shadow-sm ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}`
                    : ''}>

                  {/* Special handling for section1 with 6 cards */}
                  {section.id === 'section1' && config.cards ? (
                    <>
                      {/* Customer Selector at the top */}
                      <div className={`rounded-lg shadow-sm mb-8 ${
                        darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                      } border`}>
                        <div className="mx-6 my-6 space-y-6">
                          {/* Row 1: Kunde + Aktenzeichen side by side on desktop */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <CustomerSelector
                              darkMode={darkMode}
                              onCustomerSelect={handleCustomerSelect}
                              initialCustomer={initialReport?.customer || null}
                            />

                            {/* Vorgangsnummer (intern: aktenzeichen) */}
                            <div>
                              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                Vorgangsnummer
                              </label>
                              <input
                                translate="no"
                                type="text"
                                value={formData['aktenzeichen'] || ''}
                                onChange={(e) => handleFieldChange('aktenzeichen', e.target.value)}
                                placeholder="z.B. BAFA-2024-1234"
                                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                                  darkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300 text-slate-900'
                                }`}
                              />
                            </div>
                          </div>

                          {/* Row 2: Berichtstitel — full width */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              Berichtstitel *
                            </label>
                            <div className="flex items-center gap-5 mb-3">
                              <label className={`flex items-center gap-2 cursor-pointer text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                <input
                                  translate="no"
                                  type="radio"
                                  name="titleMode"
                                  checked={titleMode === 'custom'}
                                  onChange={() => setTitleMode('custom')}
                                  className="accent-blue-600"
                                />
                                Eigener Titel
                              </label>
                              <label className={`flex items-center gap-2 cursor-pointer text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                <input
                                  translate="no"
                                  type="radio"
                                  name="titleMode"
                                  checked={titleMode === 'template'}
                                  onChange={() => setTitleMode('template')}
                                  className="accent-blue-600"
                                />
                                Vorlage wählen
                              </label>
                            </div>

                            {titleMode === 'template' ? (
                              templatesActive ? (
                                <TemplateSelector
                                  darkMode={darkMode}
                                  hideLabel
                                  onTemplateSelect={(tpl) => {
                                    setSelectedTemplate(tpl);
                                    if (tpl) {
                                      handleFieldChange('berichtstitel', tpl.name);
                                      // Expand all sections when a template is applied
                                      setCollapsedSections(prev =>
                                        Object.fromEntries(Object.keys(prev).map(k => [k, false]))
                                      );
                                      if (!reportId) return;
                                      setApplyTemplateDialog(tpl);
                                    }
                                  }}
                                />
                              ) : (
                                <div className={`text-sm italic ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  Vorlagen sind in Ihrem aktuellen Plan nicht verfügbar.
                                </div>
                              )
                            ) : (
                              <input
                                translate="no"
                                type="text"
                                value={formData['berichtstitel'] || ''}
                                onChange={(e) => handleFieldChange('berichtstitel', e.target.value)}
                                placeholder="z.B. Marketing- und Vertriebsstrategie"
                                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                                  darkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300 text-slate-900'
                                }`}
                              />
                            )}
                          </div>

                          {/* Beratungszeitraum */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              Beratungszeitraum
                            </label>
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <DateTextInput
                                  value={formData['startDate'] || ''}
                                  onChange={(val) => {
                                    handleFieldChange('startDate', val);
                                    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
                                      const d = new Date(val);
                                      d.setMonth(d.getMonth() + 6);
                                      handleFieldChange('endDate', d.toISOString().split('T')[0]);
                                    }
                                  }}
                                  darkMode={darkMode}
                                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                                    darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'
                                  }`}
                                />
                              </div>
                              <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>bis</span>
                              <div className="flex-1">
                                <DateTextInput
                                  value={formData['endDate'] || ''}
                                  onChange={(val) => handleFieldChange('endDate', val)}
                                  darkMode={darkMode}
                                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                                    darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'
                                  }`}
                                />
                              </div>
                            </div>
                            <p className={`text-xs mt-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Max. 6 Monate ab Beratungsbeginn
                            </p>
                          </div>

                          {/* Honorar & Förderberechnung */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              Honorar &amp; Förderung
                            </label>
                            {(() => {
                              const brutto = parseFloat(preis) || 0;
                              const netto = mwstEnabled ? brutto / 1.19 : brutto;
                              const foerder = netto * (foerderRate / 100);
                              const fmt = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
                              const sel = `w-full py-2 pl-3 pr-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`;
                              return (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                                  {/* Betrag */}
                                  <div className="relative">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={preis}
                                      onChange={(e) => setPreis(e.target.value)}
                                      placeholder="0,00"
                                      className={`w-full pl-3 pr-7 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${darkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300 text-slate-900'}`}
                                    />
                                    <span className={`absolute right-2.5 top-1/2 -translate-y-1/2 text-xs pointer-events-none ${darkMode ? 'text-slate-400' : 'text-slate-400'}`}>€</span>
                                  </div>
                                  {/* Netto / Brutto */}
                                  <select
                                    value={mwstEnabled ? 'brutto' : 'netto'}
                                    onChange={(e) => setMwstEnabled(e.target.value === 'brutto')}
                                    className={sel}
                                  >
                                    <option value="netto">Netto / zzgl. MwSt.</option>
                                    <option value="brutto">Brutto / inkl. MwSt.</option>
                                  </select>
                                  {/* 50 / 80 % */}
                                  <select
                                    value={foerderRate}
                                    onChange={(e) => setFoerderRate(Number(e.target.value) as 50 | 80)}
                                    className={sel}
                                  >
                                    <option value={50}>50% – Alte Bundesländer</option>
                                    <option value={80}>80% – Neue Bundesländer</option>
                                  </select>
                                  {/* Cashback */}
                                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-slate-700/60 border-slate-600' : 'bg-green-50 border-green-200'}`}>
                                    <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Zuschuss</span>
                                    <span className={`font-bold ${brutto > 0 ? (darkMode ? 'text-green-400' : 'text-green-700') : (darkMode ? 'text-slate-500' : 'text-slate-400')}`}>
                                      {brutto > 0 ? fmt(foerder) : '–'}
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Neuen Bericht speichern - only when no reportId */}
                          {reportId === null && (
                            <button
                              onClick={handleCreateReport}
                              disabled={saveStatus === 'saving'}
                              className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors shadow-sm ${
                                saveStatus === 'saving'
                                  ? 'bg-blue-400 text-white cursor-not-allowed'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                              {saveStatus === 'saving' ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Wird gespeichert...</>
                              ) : (
                                <><Save className="w-4 h-4" /> Neuen Bericht speichern</>
                              )}
                            </button>
                          )}

                        </div>
                      </div>

                      {/* Render the 6 cards */}
                      <Section1Cards
                        darkMode={darkMode}
                        cards={config.cards}
                        formData={formData}
                        onFieldChange={handleFieldChange}
                        cardTexts={section1CardTexts}
                        onUpdateCardText={handleUpdateSection1CardText}
                        onGenerateCard={handleGenerateSection1Card}
                        onSaveCard={handleSaveSection1Card}
                        consultingDays={consultingDays}
                        onAddConsultingDay={addConsultingDay}
                        onRemoveConsultingDay={removeConsultingDay}
                        onUpdateConsultingDay={updateConsultingDay}
                        consultants={consultants}
                        onAddConsultant={addConsultant}
                        onRemoveConsultant={removeConsultant}
                        onUpdateConsultant={updateConsultant}
                        externalDayBookings={externalDayBookings}
                        collapsedCards={collapsedSection1Cards}
                        onToggleCard={(id) => {
                          if (wasJustAutoOpened(id)) return;
                          setCollapsedSection1Cards(prev => ({ ...prev, [id]: !prev[id] }));
                        }}
                        onCardComplete={handleCardComplete}
                        isCardComplete={isCardComplete}
                      />

                      {/* FileManagement moved to bottom of editor */}
                    </>
                  ) : (section.id === 'analysis' && config.cards) ? (
                    /* Card Layout for Section 2 */
                    dataLoaded ? <Section2Cards
                      darkMode={darkMode}
                      cards={config.cards || []}
                      cardTexts={section2CardTexts}
                      onUpdateCardText={(cardId, field, value) => {
                        setSection2CardTexts(prev => ({
                          ...prev,
                          [cardId]: { ...prev[cardId], [field]: value }
                        }));
                      }}
                      onGenerateCard={(cardId) => handleGenerateWithPopulate('analysis', cardId)}
                      entries={{
                        istZustand: currentStateEntries,
                        staerken: keyFindingsEntries,
                        schwaechen: dataSourcesEntries,
                      }}
                      onEntriesChange={(listKey, newEntries) => {
                        if (listKey === 'istZustand') setCurrentStateEntries(newEntries);
                        if (listKey === 'staerken') setKeyFindingsEntries(newEntries);
                        if (listKey === 'schwaechen') setDataSourcesEntries(newEntries);
                      }}
                      onSuggestCard={(cardId) => {
                        handleCardSuggest('analysis', cardId);
                      }}
                      suggestLoadingCardId={cardSuggestLoading}
                      collapsedCards={collapsedSection2Cards}
                      onToggleCard={(id) => {
                        if (wasJustAutoOpened(id)) return;
                        setCollapsedSection2Cards(prev => ({ ...prev, [id]: !prev[id] }));
                      }}
                      onCardComplete={handleCardComplete}
                      isCardComplete={isCardComplete}
                    /> : null
                  ) : (section.id === 'weaknesses' && config.cards) ? (
                    /* Card Layout for Section 4 */
                    dataLoaded ? <Section4Cards
                      darkMode={darkMode}
                      cards={config.cards || []}
                      cardTexts={section4CardTexts}
                      onUpdateCardText={(cardId, field, value) => {
                        setSection4CardTexts(prev => ({
                          ...prev,
                          [cardId]: { ...prev[cardId], [field]: value }
                        }));
                      }}
                      onGenerateCard={(cardId) => handleGenerateWithPopulate('weaknesses', cardId)}
                      entries={{
                        schwachstellen: schwachstellenEntries,
                        fragestellungen: fragestellungenEntries,
                      }}
                      onEntriesChange={(listKey, newEntries) => {
                        if (listKey === 'schwachstellen') {
                          setSchwachstellenEntries(newEntries);
                          setSchwachstellenVersion(v => v + 1);
                        }
                        if (listKey === 'fragestellungen') {
                          setFragestellungenEntries(newEntries);
                          sectionDataRef.current['weaknesses'] = {
                            ...sectionDataRef.current['weaknesses'],
                            fragestellungen: newEntries,
                          };
                        }
                      }}
                      onSuggestCard={(cardId) => {
                        handleCardSuggest('weaknesses', cardId);
                      }}
                      suggestLoadingCardId={cardSuggestLoading}
                      collapsedCards={collapsedSection4Cards}
                      onToggleCard={(id) => {
                        if (wasJustAutoOpened(id)) return;
                        setCollapsedSection4Cards(prev => ({ ...prev, [id]: !prev[id] }));
                      }}
                      onCardComplete={handleCardComplete}
                      isCardComplete={isCardComplete}
                    /> : null
                  ) : (
                  /* Standard Two-Column Layout for all other sections */
                  <div className="flex flex-col lg:flex-row gap-6 md:gap-8 items-stretch min-w-0">
                    {/* LEFT COLUMN - Eingaben */}
                    <div className={`flex flex-col ${(section.id === 'confirmation' || section.id === 'principles' || section.id === 'analysis' || section.id === 'weaknesses') ? 'lg:w-full' : 'lg:w-[50%]'}`}>
                      <h2 className={`text-lg md:text-xl font-semibold mb-4 md:mb-6 ${darkMode ? 'text-white' : 'text-slate-900'}`}>{(section.id === 'confirmation' || section.id === 'principles' || section.id === 'analysis' || section.id === 'weaknesses') ? '' : 'Eingaben'}</h2>
                      
                      {/* Customer Selector - Only show in section1 */}
                      {section.id === 'section1' && (
                        <div className={`rounded-lg shadow-sm mb-6 ${
                          darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                        } border`}>
                          <div className="mx-6 my-6">
                            <CustomerSelector
                              darkMode={darkMode}
                              onCustomerSelect={handleCustomerSelect}
                              initialCustomer={initialReport?.customer || null}
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Structured Input Fields - Container that stretches */}
                      {config.subsections ? (
                        // Render subsections in a stretched container
                        <div className={`rounded-lg shadow-sm flex-1 ${
                          darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                        } border`}>
                          <div className="mx-6 my-6">
                            <div className="space-y-5">
                            {config.subsections.map((subsection) => (
                              <div key={subsection.title} className={`rounded-lg p-5 ${
                                darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50/50 border-slate-200'
                              } border`}>
                                <h3 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                  {subsection.title}
                                </h3>
                                
                                {/* If subsection has nested subsections */}
                                {subsection.subsections ? (
                                  <div className="space-y-5">
                                    {subsection.subsections.map((nestedSubsection) => (
                                      <div key={nestedSubsection.title}>
                                        <h4 className={`text-xs font-medium uppercase tracking-wide mb-2.5 ${
                                          darkMode ? 'text-slate-400' : 'text-slate-500'
                                        }`}>
                                          {nestedSubsection.title}
                                        </h4>
                                        <div className={`grid grid-cols-1 gap-x-3 gap-y-2.5 ${
                                          nestedSubsection.title === 'Ansprechperson' ? 'md:grid-cols-3' : 'md:grid-cols-2'
                                        }`}>
                                          {nestedSubsection.fields.map((field) => {
                                            const isFullWidth = field.fullWidth || field.type === 'textarea';
                                            const gridColumnClass = isFullWidth 
                                              ? (nestedSubsection.title === 'Ansprechperson' ? 'md:col-span-3' : 'md:col-span-2')
                                              : '';
                                            
                                            return (
                                              <div key={field.id} className={gridColumnClass}>
                                                <label className={`block text-xs font-medium mb-1 ${
                                                  darkMode ? 'text-slate-300' : 'text-slate-700'
                                                }`}>
                                                  {field.label}
                                                </label>
                                                {field.type === 'select' ? (
                                                  <select className={`w-full px-2.5 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                                                    darkMode ? 'bg-slate-600 border-slate-500 text-white' : 'bg-white border-slate-300'
                                                  }`}>
                                                    <option value="">Bitte wählen</option>
                                                    {field.options?.map((option) => (
                                                      <option key={option} value={option}>
                                                        {option}
                                                      </option>
                                                    ))}
                                                  </select>
                                                ) : field.type === 'divider' ? (
                                                  <div key={field.id} className="col-span-full">
                                                    <div className={`border-t pt-3 mt-2 ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                                                      <span className={`text-xs font-semibold tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                        {field.label.toUpperCase()}
                                                      </span>
                                                    </div>
                                                  </div>
                                                ) : field.type === 'wz-search' ? (
                                                  <WzSearch
                                                    value={formData[field.id] || ''}
                                                    onChange={(val) => handleFieldChange(field.id, val)}
                                                    darkMode={darkMode}
                                                  />
                                                ) : field.type === 'textarea' ? (
                                                  <textarea
                                                    translate="no"                                                    value={formData[field.id] || ''}
                                                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                                    placeholder={field.placeholder}
                                                    rows={field.rows || 3}
                                                    className={`w-full px-2.5 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm ${
                                                      darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'border-slate-300'
                                                    }`}
                                                  />
                                                ) : (
                                                  <input
                                                    translate="no"                                                    value={formData[field.id] || ''}
                                                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                                    type={field.type}
                                                    placeholder={field.placeholder}
                                                    className={`w-full px-2.5 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                                                      darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'border-slate-300'
                                                    }`}
                                                  />
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  /* Regular subsection with fields */
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-2.5">
                                    {subsection.fields?.map((field) => {
                                      const isFullWidth = field.fullWidth || field.type === 'textarea';
                                      const gridColumnClass = isFullWidth ? 'md:col-span-2' : '';
                                      
                                      return (
                                        <div key={field.id} className={gridColumnClass}>
                                          <label className={`block text-xs font-medium mb-1 ${
                                            darkMode ? 'text-slate-300' : 'text-slate-700'
                                          }`}>
                                            {field.label}
                                          </label>
                                          {field.type === 'select' ? (
                                            <select className={`w-full px-2.5 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                                              darkMode ? 'bg-slate-600 border-slate-500 text-white' : 'bg-white border-slate-300'
                                            }`}>
                                              <option value="">Bitte wählen</option>
                                              {field.options?.map((option) => (
                                                <option key={option} value={option}>
                                                  {option}
                                                </option>
                                              ))}
                                            </select>
                                          ) : field.type === 'divider' ? (
                                            <div key={field.id} className="col-span-full">
                                              <div className={`border-t pt-3 mt-2 ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                                                <span className={`text-xs font-semibold tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                  {field.label.toUpperCase()}
                                                </span>
                                              </div>
                                            </div>
                                          ) : field.type === 'wz-search' ? (
                                            <WzSearch
                                              value={formData[field.id] || ''}
                                              onChange={(val) => handleFieldChange(field.id, val)}
                                              darkMode={darkMode}
                                            />
                                          ) : field.type === 'textarea' ? (
                                            <textarea
                                              translate="no"                                              value={formData[field.id] || ''}
                                              onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                              placeholder={field.placeholder}
                                              rows={field.rows || 3}
                                              className={`w-full px-2.5 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm ${
                                                darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'border-slate-300'
                                              }`}
                                            />
                                          ) : (
                                            <input
                                              translate="no"                                              value={formData[field.id] || ''}
                                              onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                              type={field.type}
                                              placeholder={field.placeholder}
                                              className={`w-full px-2.5 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                                                darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'border-slate-300'
                                              }`}
                                            />
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            ))}
                            </div>
                          
                            {/* Action Bar */}
                            <div className={`flex justify-end pt-6 mt-6 border-t ${
                              darkMode ? 'border-slate-600' : 'border-slate-200'
                            }`}>
                            <button
                              onClick={() => handleGenerateWithPopulate()}
                              className={`px-4 py-2 rounded-lg border transition-colors text-sm font-medium shadow-sm ${
                              darkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                            }`}>
                              ✨ Text generieren
                            </button>
                          </div>
                          </div>
                        </div>
                      ) : section.id === 'execution' ? (
                        dataLoaded ? <Section3Execution
                          darkMode={darkMode}
                          onDataChange={(data) => {
                            sectionDataRef.current['execution'] = data;
                            if (data.durchfuehrungsschritte) {
                              setExecutionStepsEntries(data.durchfuehrungsschritte);
                            }
                          }}
                          onGenerate={() => handleGenerateWithPopulate('execution')}
                          customerData={selectedCustomer ? { name: selectedCustomer.name, wirtschaftszweig: selectedCustomer.wirtschaftszweig, geschaeftsgegenstand: (selectedCustomer as any).geschaeftsgegenstand, ort: selectedCustomer.ort } : undefined}
                          formData={formData}
                          initialData={{ durchfuehrungsschritte: executionStepsEntries }}
                          analyseText={sectionStates['analysis']?.finalText || ''}
                          isAdmin={user?.isAdmin === true}
                        /> : null
                      ) : section.id === 'action-plan' ? (
                        dataLoaded ? <Section5ActionPlan
                          darkMode={darkMode}
                          onDataChange={(data) => {
                            sectionDataRef.current['action-plan'] = data;
                            if (data.massnahmen) {
                              setRecommendationsEntries(data.massnahmen);
                            }
                          }}
                          onGenerate={() => handleGenerateWithPopulate('action-plan')}
                          customerData={selectedCustomer ? { name: selectedCustomer.name, wirtschaftszweig: selectedCustomer.wirtschaftszweig, geschaeftsgegenstand: (selectedCustomer as any).geschaeftsgegenstand, ort: selectedCustomer.ort } : undefined}
                          formData={formData}
                          initialData={{ massnahmen: recommendationsEntries }}
                          schwachstellenText={sectionStates['weaknesses']?.finalText || ''}
                          schwachstellenData={schwachstellenEntries}
                          schwachstellenVersion={schwachstellenVersion}
                          isAdmin={user?.isAdmin === true}
                        /> : null
                      ) : section.id === 'principles' ? (
                        !includePrinciples ? (
                          <div className={`rounded-lg border border-dashed p-8 text-center ${darkMode ? 'border-slate-600 text-slate-500' : 'border-slate-300 text-slate-400'}`}>
                            Dieser Abschnitt ist deaktiviert und wird nicht im Bericht eingeschlossen.
                          </div>
                        ) : !dataLoaded ? null :
                        <Section6Principles
                          darkMode={darkMode}
                          initialData={{
                            principlesText: sectionStates['principles']?.finalText || '',
                            confirmed: !!sectionStates['principles']?.confirmed,
                          }}
                          onDataChange={(data) => {
                            sectionDataRef.current['principles'] = data;
                            // Sync to sectionStates so auto-save picks it up
                            // Only update if value actually changed to prevent infinite loops
                            setSectionStates(prev => {
                              if (prev.principles?.finalText === data.principlesText
                                && !!prev.principles?.confirmed === !!data.confirmed) return prev;
                              return { ...prev, principles: { ...prev.principles, finalText: data.principlesText, confirmed: !!data.confirmed } };
                            });
                          }}
                          onGenerate={() => handleGenerateWithPopulate('principles')}
                        />
                      ) : section.id === 'confirmation' ? (
                        !dataLoaded ? null :
                        <Section7Confirmation
                          darkMode={darkMode}
                          initialData={{
                            confirmationText: sectionStates['confirmation']?.finalText || '',
                            confirmed: !!sectionStates['confirmation']?.confirmed,
                          }}
                          onDataChange={(data) => {
                            sectionDataRef.current['confirmation'] = data;
                            // Sync to sectionStates so auto-save picks it up
                            // Only update if value actually changed to prevent infinite loops
                            setSectionStates(prev => {
                              if (prev.confirmation?.finalText === data.confirmationText
                                && !!prev.confirmation?.confirmed === !!data.confirmed) return prev;
                              return { ...prev, confirmation: { ...prev.confirmation, finalText: data.confirmationText, confirmed: !!data.confirmed } };
                            });
                          }}
                          onGenerate={() => {}}
                          customerData={selectedCustomer ? { name: selectedCustomer.name, ort: selectedCustomer.ort } : undefined}
                          beraterName={formData['berater'] || (sectionDataRef.current?.['zeitangaben'] as any)?.berater?.[0]?.name}
                        />
                      ) : null}
                    </div>

                    {/* RIGHT COLUMN - Finaler Inhalt (nicht für Confirmation/Principles) */}
                    {section.id !== 'confirmation' && section.id !== 'principles' && section.id !== 'analysis' && section.id !== 'weaknesses' && (
                    <div className="flex flex-col lg:w-[50%]">
                      <h2 className={`text-lg md:text-xl font-semibold mb-4 md:mb-6 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Finaler Inhalt</h2>
                      
                      <div className={`rounded-lg shadow-sm flex-1 flex flex-col ${
                        darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                      } border`}>
                        <div className="mx-6 my-6 flex-1 flex flex-col">
                          <RichTextEditor
                            content={sectionStates[section.id]?.finalText || ''}
                            onChange={(html) => handleUpdateSectionState(section.id, 'finalText', html)}
                            darkMode={darkMode}
                            minHeight="200px"
                            onRefineItem={async (itemHtml, instruction, mode) => {
                              const result = await refineItem({ itemHtml, instruction: instruction || undefined, mode });
                              if (!result.ok) throw new Error(result.error.message);
                              return result.data.text;
                            }}
                          />
                        </div>

                      </div>
                    </div>
                    )}
                  </div>
                  )}
                  </div>{/* inner-card padding */}
                  </div>{/* overflow-hidden */}
                  </div>{/* grid collapse wrapper */}
                </div>
              </div>{/* includePrinciples overflow-hidden */}
              </div>{/* includePrinciples grid collapse wrapper */}

                {/* Section Divider (except for last section, or a hidden Grundsätze-Karte —
                    sonst stünde eine Trennlinie direkt vor einem unsichtbaren Block). */}
                {index < sections.length - 1 && !isHiddenPrinciples && (
                  <div className={`mt-12 md:mt-16 pt-0 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`} />
                )}
              </div>
            );
          })}

          {/* ESF Fragebogen */}
          <div ref={esfRef} className="mt-12 md:mt-16 scroll-mt-4">
            <ESFQuestionnaire
              darkMode={darkMode}
              reportId={reportId ?? undefined}
              onFileChange={(f) => { setEsfFileId(f?.id ?? null); setFileRefreshKey(k => k + 1); }}
              onEnabledChange={setEsfEnabled}
            />
          </div>

          {/* Export-Bereich */}
          <div ref={exportRef} className={`mt-12 md:mt-16 pt-8 border-t scroll-mt-4 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 flex-wrap">
              {/* Erst der Schritt im Ablauf, dann das Ansehen: „generieren" legt die Datei am
                  Vorgang ab und ist Voraussetzung für Unterschrift, VN und BAFA-Einreichung.
                  Die Vorschau lädt dasselbe PDF nur lokal herunter. */}
              <button
                onClick={handleSavePdf}
                disabled={exportingFormat !== null || !reportId}
                translate="no"
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                title="Erzeugt das Bericht-PDF und legt es beim Vorgang ab — Grundlage für Unterschrift und Einreichung"
              >
                {exportingFormat === 'pdf' ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                <span>Bericht PDF generieren</span>
              </button>
              <button
                onClick={handleExportPdf}
                disabled={exportingFormat !== null}
                translate="no"
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                title="Lädt den Bericht als PDF herunter — wird nicht beim Vorgang abgelegt"
              >
                {exportingFormat === 'pdf' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                <span>PDF herunterladen</span>
              </button>
              {/* Word-Export vorerst deaktiviert: jsPDF und docx sind getrennte Renderer und nicht
                  pixel-identisch. Nur das PDF gilt als maßgeblicher Bericht. handleExportDocx und
                  exportDocx.ts bleiben erhalten — Button bei Bedarf wieder einkommentieren.
              <button
                onClick={handleExportDocx}
                disabled={exportingFormat !== null}
                translate="no"
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportingFormat === 'docx' ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                <span>Word (DOCX) Export</span>
              </button>
              */}
              <button
                onClick={handleExportTxt}
                disabled={exportingFormat !== null}
                translate="no"
                className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors font-medium shadow-sm border cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                darkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}>
                {exportingFormat === 'txt' ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                <span>TXT Export</span>
              </button>
              {templatesActive && (
                <button
                  type="button"
                  onClick={() => setSaveTemplateOpen(true)}
                  disabled={!reportId || !hasTemplateQuellinhalt}
                  className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors font-medium shadow-sm border cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                  title={
                    !reportId
                      ? 'Erst verfügbar, sobald der Bericht angelegt ist'
                      : !hasTemplateQuellinhalt
                        ? 'Noch keine Inhalte in Analyse, Schwachstellen, Maßnahmen oder Durchführung — zuerst befüllen'
                        : 'Aktuellen Bericht als Vorlage speichern'
                  }
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Als Vorlage speichern</span>
                </button>
              )}
            </div>
            {!reportId && (
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                Hinweis: „Bericht PDF generieren" wird verfügbar, sobald der Bericht angelegt ist — bitte Kunde, Berichtstitel und Vorgangsnummer ausfüllen und oben „Neuen Bericht speichern".
              </p>
            )}
          </div>

          {/* Dokumentenanhänge - nach allen Sektionen */}
          <div className="mt-6">
            {/* Workflow-Schritte */}
            {reportId && (
              <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Schritt 1: BAFA-Formulare */}
                <div className={`relative rounded-xl border p-4 transition-colors flex flex-col h-full ${
                  bafaGenerated && !bafaError
                    ? 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                }`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      bafaGenerated && !bafaError
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                    }`}>1</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">Dauer-Dokumente</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Vollmacht, GRC und De-Minimis automatisch befüllen und als PDF speichern</p>
                    </div>
                    {bafaGenerated && !bafaError && (
                      <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                        Erledigt
                      </span>
                    )}
                  </div>
                  {signedDauerDocs.length > 0 && (
                    <p className="mb-2 text-xs text-green-700 dark:text-green-400 flex items-start gap-1">
                      <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                      <span>Bereits unterschrieben vorhanden: <strong>{signedDauerDocs.join(', ')}</strong> — wird nicht erneut zur Unterschrift angefordert.</span>
                    </p>
                  )}
                  <button
                    onClick={handleGenerateBafaForms}
                    disabled={bafaGenerating}
                    className="mt-auto self-start flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {bafaGenerating ? (
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )}
                    Dauer-Dokumente generieren
                  </button>
                  {bafaError && (
                    <p className="mt-2 text-xs text-red-600 dark:text-red-400">{bafaError}</p>
                  )}
                </div>

                {/* Schritt 2: Unterschreiben */}
                <div className={`relative rounded-xl border p-4 transition-colors flex flex-col h-full ${
                  signingStatus === 'completed'
                    ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10'
                    : signingStatus === 'pending'
                    ? 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                }`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      signingStatus === 'completed'
                        ? 'bg-emerald-600 text-white'
                        : signingStatus === 'pending'
                        ? 'bg-amber-500 text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                    }`}>2</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">Unterschreiben</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Unterschriftsanfrage per E-Mail an den Kunden senden</p>
                    </div>
                    {signingStatus === 'completed' && (
                      <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                        Unterschrieben
                      </span>
                    )}
                    {signingStatus === 'pending' && (
                      <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                        Ausstehend
                      </span>
                    )}
                  </div>

                  {/* Versand-Info: Sendedatum + angeforderte Dokumente mit Eingangs-Häkchen */}
                  {signingInfo && signingStatus !== 'none' && (
                    <div className="mb-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/30 p-2.5">
                      {(signingInfo.emailSentAt ?? signingInfo.createdAt) && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1.5">
                          Gesendet am {new Date((signingInfo.emailSentAt ?? signingInfo.createdAt)!).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                      {signingInfo.docs && signingInfo.docs.length > 0 && (
                        <ul className="space-y-1">
                          {signingInfo.docs.map((doc) => (
                            <li key={doc.type} className="flex items-center gap-2 text-xs">
                              {doc.received ? (
                                <svg className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                              ) : (
                                <span className="w-3.5 h-3.5 rounded-full border border-slate-300 dark:border-slate-600 flex-shrink-0" />
                              )}
                              <span className={doc.received ? 'text-slate-700 dark:text-slate-200' : 'text-slate-500 dark:text-slate-400'}>
                                {doc.label}
                                {doc.received ? ' – eingegangen' : ' – ausstehend'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  <button
                    onClick={() => oeffneBerichtVersand(selectedCustomer?.email ?? '')}
                    disabled={!reportId || berichtSpeichert}
                    className="mt-auto self-stretch flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Bericht zur Unterschrift senden
                  </button>

                  <button
                    onClick={handleVnUnterschrift}
                    disabled={vnSending || vnPruefung || !reportId}
                    className="mt-2 self-stretch flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {vnSending ? 'Sende…' : vnPruefung ? 'Prüfe…' : 'Verwendungsnachweis zur Unterschrift senden'}
                  </button>
                  {vnMeldung && <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">{vnMeldung}</p>}
                </div>
              </div>
            )}
            {reportId != null && <EditorDocuments reportId={reportId} refreshKey={fileRefreshKey} signingStatus={signingStatus} onChanged={() => setFileRefreshKey(k => k + 1)} />}
          </div>

          {/* Schließen-Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onNavigateBack}
              translate="no"
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors font-medium shadow-sm border cursor-pointer ${
                darkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Schließen</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>

    {/* Card Suggest Popup */}
    {cardSuggestPopup && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={!cardSuggestLoading ? () => setCardSuggestPopup(null) : undefined} />
        <div className={`relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-xl shadow-2xl ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} border`}>
          <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Vorschläge generieren</h2>
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Prompt prüfen und bei Bedarf anpassen</p>
              </div>
            </div>
            <button onClick={() => setCardSuggestPopup(null)} disabled={!!cardSuggestLoading} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'} disabled:opacity-50`}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <textarea
              translate="no"              value={cardSuggestPopup.prompt}
              onChange={(e) => setCardSuggestPopup({ ...cardSuggestPopup, prompt: e.target.value })}
              disabled={!!cardSuggestLoading}
              rows={16}
              className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-y font-mono leading-relaxed ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-slate-50 border-slate-300 text-slate-900'} disabled:opacity-60`}
            />
            {cardSuggestLoading && (
              <div className={`mt-4 p-3 rounded-lg flex items-center gap-3 ${darkMode ? 'bg-purple-900/20 border-purple-800' : 'bg-purple-50 border-purple-200'} border`}>
                <Loader2 className={`w-4 h-4 animate-spin ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                <p className={`text-sm ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>Vorschläge werden generiert...</p>
              </div>
            )}
          </div>
          <div className={`flex items-center justify-between gap-3 p-4 sm:p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <button
              onClick={() => {
                const fresh = buildCardSuggestPrompt(cardSuggestPopup.sectionId, cardSuggestPopup.cardId);
                setCardSuggestPopup({ ...cardSuggestPopup, prompt: fresh });
              }}
              disabled={!!cardSuggestLoading}
              className={`text-xs px-3 py-1.5 rounded transition-colors ${darkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'} disabled:opacity-50`}
            >
              ↺ Prompt zurücksetzen
            </button>
            <button
              onClick={handleCardSuggestSubmit}
              disabled={!!cardSuggestLoading || !cardSuggestPopup.prompt.trim()}
              className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="inline-flex">
                {cardSuggestLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </span>
              <span>{cardSuggestLoading ? 'Wird generiert...' : 'Abschicken'}</span>
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Prompt Popup */}
    {promptPopupOpen && promptPopupData && (
      <PromptPopup
        isOpen={promptPopupOpen}
        onClose={() => setPromptPopupOpen(false)}
        initialPrompt={promptPopupData.prompt}
        contextPreview={promptPopupData.contextPreview}
        sectionKey={promptPopupData.cardId}
        cardId={promptPopupData.cardId}
        darkMode={darkMode}
        onGenerated={handlePromptGenerated}
        formData={promptPopupData.formData}
        customerData={promptPopupData.customerData}
        warnings={promptPopupData.warnings}
        customerId={selectedCustomer?.id}
        isAdmin={user?.isAdmin === true}
      />
    )}

    {/* Tips Popup (non-admin generating) */}
    <GeneratingTipsPopup
      isOpen={tipsPopupOpen}
      darkMode={darkMode}
      error={tipsPopupError}
      onReset={tipsPopupError ? async () => {
        // Hängende Generierung freigeben (alle Locks dieses Users), dann Popup
        // schließen. Der Nutzer kann danach sofort erneut „Generieren" klicken.
        await cancelGeneration();
        setTipsPopupError(null);
        setTipsPopupOpen(false);
      } : undefined}
    />

    {/* Direct Render Info Popup (Admin with Prompt-Editor active) */}
    {directRenderInfo.open && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className={`rounded-xl border shadow-xl p-6 max-w-md mx-4 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
              <Sparkles className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Direktes Rendering
              </h3>
              <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {DIRECT_RENDER_LABELS[directRenderInfo.cardId] || directRenderInfo.cardId}
              </p>
            </div>
          </div>
          <p className={`text-sm mb-5 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            Dieser Abschnitt wird <strong>direkt aus den Formulardaten</strong> gerendert – ohne KI-Aufruf. Die Daten werden strukturiert als HTML ausgegeben.
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setDirectRenderInfo({ open: false, cardId: '' })}
              className={`px-4 py-2 text-sm rounded-lg border ${darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
            >
              Abbrechen
            </button>
            <button
              onClick={handleDirectRenderConfirm}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Rendern
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Bericht-komplett-erstellen — Sicherheitsabfrage als Pop-up */}
    {/* Schwebender Hauptknopf — immer genau EINE naheliegende Aktion:
        noch nichts geschrieben → erstellen, während des Laufs → Fortschritt,
        alles gefüllt → prüfen. Die Knöpfe in der Seitenleiste bleiben bestehen,
        treten aber optisch zurück. Liegt unter den Dialogen (z-30 < z-50). */}
    <div className="fixed bottom-5 right-5 z-30 print:hidden">
      {batchGenerating ? (
        <div className={`flex items-center gap-2 pl-4 pr-2 py-2 rounded-full shadow-lg border ${darkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'}`}>
          <Loader2 className="w-4 h-4 animate-spin text-blue-600 shrink-0" />
          <span className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            {batchProgress ? `${batchProgress.i} von ${batchProgress.total}` : 'läuft…'}
          </span>
          <button
            type="button"
            onClick={stopBatchGeneration}
            title="Erstellung stoppen"
            className="ml-1 p-1.5 rounded-full text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : berichtGefuellt ? (
        <button
          type="button"
          onClick={() => setAnalysisOpen(true)}
          disabled={!reportId}
          title="Analysiert den fertigen Bericht (Förderfähigkeit & Qualität) und schlägt gezielte Verbesserungen vor"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full shadow-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-semibold transition-colors"
        >
          <Sparkles className="w-4 h-4" />
          Bericht analysieren
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setBatchConfirm(true)}
          disabled={!reportId}
          title="Generiert alle Abschnitte des Berichts nacheinander automatisch"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full shadow-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold transition-colors"
        >
          <Sparkles className="w-4 h-4" />
          Bericht komplett erstellen
        </button>
      )}
    </div>

    {batchConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setBatchConfirm(false)}>
        <div className={`rounded-xl border shadow-xl p-6 max-w-md mx-4 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`} onClick={e => e.stopPropagation()}>
          <div className="flex items-start gap-3 mb-4">
            <div className={`p-2 rounded-lg flex-shrink-0 ${berichtGefuellt ? 'bg-red-100' : 'bg-blue-100'}`}>
              {berichtGefuellt
                ? <AlertTriangle className="w-5 h-5 text-red-600" />
                : <Sparkles className="w-5 h-5 text-blue-600" />}
            </div>
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              {berichtGefuellt ? 'Bericht wirklich neu erstellen?' : 'Bericht komplett erstellen?'}
            </h3>
          </div>
          {berichtGefuellt ? (
            <div className="mb-5 space-y-2">
              <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                <p><strong>Alle vorhandenen Texte werden ersetzt</strong> — auch die, die Sie von Hand überarbeitet haben. Rückgängig machen lässt sich das nicht.</p>
              </div>
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Dieser Bericht ist bereits vollständig. Zum Verbessern einzelner Stellen ist
                <strong> „Bericht analysieren"</strong> der richtige Weg — dort bleiben Ihre Texte erhalten
                und Sie entscheiden je Vorschlag.
              </p>
            </div>
          ) : (
            <p className={`text-sm mb-5 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Alle Abschnitte werden automatisch vollständig generiert.
              {hasExistingGeneratedText() && <> <strong>Bereits generierte Texte werden dabei überschrieben.</strong></>}
            </p>
          )}
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setBatchConfirm(false)}
              className={`px-4 py-2 text-sm rounded-lg border ${darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
            >
              Abbrechen
            </button>
            {berichtGefuellt && (
              <button
                onClick={() => { setBatchConfirm(false); setAnalysisOpen(true); }}
                className="px-4 py-2 text-sm rounded-lg border border-purple-400 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 font-semibold"
              >
                Lieber analysieren
              </button>
            )}
            <button
              onClick={() => { setBatchConfirm(false); generateAllSections(); }}
              className={`px-4 py-2 text-sm rounded-lg text-white font-semibold ${berichtGefuellt ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {berichtGefuellt ? 'Ja, alles ersetzen' : 'Ja, erstellen'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Bericht-Generierung — Zusammenfassung als Pop-up */}
    {!batchGenerating && batchSummary && batchSummary.length > 0 && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setBatchSummary(null)}>
        <div className={`rounded-xl border shadow-xl p-6 max-w-md w-full mx-4 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`} onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Bericht generiert
            </h3>
            <button type="button" onClick={() => setBatchSummary(null)}
              className={`p-1 rounded ${darkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-200'}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className={`text-sm font-medium mb-3 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            {batchSummary.filter(r => r.ok).length} generiert · {batchSummary.filter(r => !r.ok).length} übersprungen
          </p>
          <ul className="space-y-1 max-h-72 overflow-y-auto text-sm">
            {batchSummary.map((r, idx) => (
              <li key={idx} className="flex items-start gap-2">
                {r.ok
                  ? <Check className="w-4 h-4 mt-0.5 shrink-0 text-green-500" />
                  : <X className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />}
                <span className={`min-w-0 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {r.label}
                  {!r.ok && r.reason && <span className="text-amber-600 dark:text-amber-400"> — {r.reason}</span>}
                </span>
              </li>
            ))}
          </ul>
          <p className={`text-xs mt-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Die Texte wurden im Bericht gespeichert. „Bericht anzeigen" lädt sie in den Editor.
          </p>
          <div className="flex justify-end mt-3">
            <button onClick={() => window.location.reload()}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-semibold">
              Bericht anzeigen
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Missing Fields Error Modal */}
    {missingFieldsError && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className={`rounded-xl border shadow-xl p-6 max-w-md mx-4 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Fehlende Angaben
              </h3>
              <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Bitte füllen Sie folgende Felder aus, bevor der Text generiert werden kann:
              </p>
            </div>
          </div>
          <ul className={`ml-12 mb-6 space-y-1 text-sm ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
            {missingFieldsError.warnings.map((w, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                {w}
              </li>
            ))}
          </ul>
          <div className="flex justify-end">
            <button
              onClick={() => setMissingFieldsError(null)}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Verstanden
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Auto-Save Floating Toast — bottom-right, blue, semi-transparent */}
    {saveStatus !== 'idle' && (
      <div className="fixed bottom-6 right-6 z-50 pointer-events-none max-w-sm">
        <div
          className={`flex items-start gap-2 px-3 py-2 rounded-lg shadow-lg backdrop-blur-sm text-xs font-medium text-white transition-all duration-200 ${
            saveStatus === 'error' ? 'bg-red-600/90' : 'bg-blue-600/80'
          }`}
        >
          <Save className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${saveStatus === 'saving' ? 'animate-pulse' : ''}`} />
          <span>
            {saveStatus === 'saving' ? 'Speichert…' : saveStatus === 'saved' ? 'Gespeichert' : (
              <>Fehler beim Speichern{saveErrorMsg ? <span className="block opacity-90 font-normal mt-0.5">{saveErrorMsg}</span> : null}</>
            )}
          </span>
        </div>
      </div>
    )}

    {/* Apply Template Dialog — Vorschau + bullet-weise Auswahl */}
    {applyTemplateDialog && reportId && (
      <ApplyTemplateDialog
        template={applyTemplateDialog}
        reportId={Number(reportId)}
        darkMode={darkMode}
        open
        onClose={() => setApplyTemplateDialog(null)}
        onApplied={() => {
          setApplyTemplateDialog(null);
          window.location.reload();
        }}
      />
    )}

    {/* Save As Template Modal */}
    {saveTemplateOpen && reportId && (
      <SaveAsTemplateModal
        reportId={Number(reportId)}
        isAdmin={!!user?.isAdmin}
        open={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
        onSaved={() => setSaveTemplateOpen(false)}
      />
    )}

    {/* Signing Modal */}
    {/* BAFA Warn-Modal bei fehlenden Feldern */}
    {bafaWarnFields.length > 0 && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
        <div className={`w-full max-w-md rounded-xl shadow-xl p-6 ${darkMode ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'}`}>
          <h3 className="text-base font-semibold mb-2">Fehlende Kundendaten</h3>
          <p className={`text-sm mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Folgende Felder sind nicht ausgefüllt — die Formulare werden unvollständig generiert:
          </p>
          <ul className={`text-sm mb-5 space-y-1 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
            {bafaWarnFields.map(f => (
              <li key={f} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                {f}
              </li>
            ))}
          </ul>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setBafaWarnFields([])}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
            >
              Abbrechen
            </button>
            <button
              onClick={doGenerateBafaForms}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white transition-colors"
            >
              Trotzdem generieren
            </button>
          </div>
        </div>
      </div>
    )}

    {berichtSpeichernFrage && (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
        onClick={() => !berichtSpeichert && setBerichtSpeichernFrage(null)}>
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
          <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Bericht in aktueller Fassung speichern?</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Zum Vorgang liegt noch keine Bericht-PDF. Ohne sie enthält die Mail nichts zum
            Unterschreiben — der Kunde könnte nur seinen Kontoauszug hochladen. Mit „Ja" wird
            der Bericht aus dem aktuellen Stand erzeugt, gespeichert und anschließend geht die
            E-Mail-Vorschau auf.
          </p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setBerichtSpeichernFrage(null)} disabled={berichtSpeichert}
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">
              Abbrechen
            </button>
            <button
              onClick={async () => {
                const email = berichtSpeichernFrage.email;
                setBerichtSpeichert(true);
                try {
                  await doSavePdf();
                  setBerichtSpeichernFrage(null);
                  setSigningEmail(email);
                  handleOpenSigningPreview(email);
                } finally {
                  setBerichtSpeichert(false);
                }
              }}
              disabled={berichtSpeichert}
              className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50">
              {berichtSpeichert ? 'Speichert…' : 'Ja, speichern'}
            </button>
          </div>
        </div>
      </div>
    )}

    {vnFehlt && (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={() => setVnFehlt(null)}>
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
          <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Noch kein Verwendungsnachweis vorhanden</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Zum Unterschreiben braucht es zuerst das Formular der BAFA. Es wird über den
            Vorgangs-Zugang abgeholt und liegt danach beim Vorgang — anschließend können Sie es
            dem Kunden zur Unterschrift schicken.
          </p>
          <div className="mb-4">
            <VnButton vorgangId={vnFehlt.vorgangId} customerId={vnFehlt.customerId} active />
          </div>
          <div className="flex justify-end">
            <button onClick={() => setVnFehlt(null)}
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
              Schließen
            </button>
          </div>
        </div>
      </div>
    )}

    <SigningEmailReview
      open={emailReviewOpen}
      reportId={reportId!}
      email={signingEmail || selectedCustomer?.email || ''}
      defaultCc={user?.email}
      context={{ bitteKontoauszug: emailReviewContext.bitteKontoauszug }}
      flow={emailReviewContext.mode === 'vn' ? 'vn' : 'bericht'}
      error={emailReviewContext.mode === 'vn' ? vnMeldung : signingError}
      // Beim Bericht steckt die Dokumentauswahl im selben Dialog (früher ein eigenes Fenster davor).
      docAuswahl={emailReviewDocAuswahl}
      prepare={(email, auswahl) => emailReviewContext.mode === 'vn'
        ? prepareSigningVn(reportId!, email)
        : prepareSigningReport(reportId!, {
            customerEmail: email,
            forceTypes: auswahl.forceTypes,
            excludeTypes: auswahl.excludeTypes,
            bitteKontoauszug: auswahl.bitteKontoauszug,
          })}
      onCancel={() => { setEmailReviewOpen(false); setSigningError(null); setVnMeldung(null); }}
      onSent={() => {
        setEmailReviewOpen(false);
        if (emailReviewContext.mode === 'vn') {
          setVnMeldung('VN wurde zur Unterschrift an den Kunden gesendet.');
        } else {
          setSigningStatus('pending');
              setSigningPreview(null);
        }
      }}
    />

    <ConfirmDialog
      open={esfWarnOpen}
      title="ESF-Fragebogen fehlt"
      message="Der ESF-Fragebogen wurde noch nicht ausgefüllt und gespeichert. Für einen vollständigen Bericht sollte er vorhanden sein."
      confirmLabel="Trotzdem speichern"
      onConfirm={() => { setEsfWarnOpen(false); checkSignedThenSave(); }}
      onCancel={() => setEsfWarnOpen(false)}
    />

    <ConfirmDialog
      open={signedUpdateConfirmOpen}
      title="Bereits unterschrieben"
      message="Dieser Bericht wurde bereits unterschrieben. Die unterschriebene Fassung im Vorgang kann mit dem aktuellen Inhalt aktualisiert werden — die Unterschriftsseite bleibt dabei unverändert. Bei 'Abbrechen' wird nur im Editor gespeichert, die unterschriebene Fassung im Vorgang bleibt wie sie ist."
      confirmLabel="Auch im Vorgang aktualisieren"
      onConfirm={() => { setSignedUpdateConfirmOpen(false); doSavePdf({ alsoRefreshSigned: true }); }}
      onCancel={() => { setSignedUpdateConfirmOpen(false); doSavePdf({ alsoRefreshSigned: false }); }}
    />

    <ConfirmDialog
      open={signedRefreshMismatch}
      title="Unterschriebene Fassung nicht aktualisiert"
      message="Die Seitenzahl des Berichts hat sich durch die Änderung verschoben — die unterschriebene Fassung im Vorgang konnte deshalb nicht automatisch aktualisiert werden (die Unterschrift könnte sonst auf der falschen Seite landen). Der Editor-Entwurf wurde trotzdem gespeichert. Bitte eine neue Signaturrunde starten, wenn die unterschriebene Fassung ebenfalls aktuell sein soll."
      confirmLabel="Verstanden"
      hideCancel
      onConfirm={() => setSignedRefreshMismatch(false)}
      onCancel={() => setSignedRefreshMismatch(false)}
    />

    <ConfirmDialog
      open={overwriteConfirm}
      title="Dauer-Dokumente überschreiben"
      message="Dauer-Dokumente bereits generiert. Neu generieren und überschreiben?"
      confirmLabel="Überschreiben"
      onConfirm={async () => {
        setOverwriteConfirm(false);
        await doGenerateBafaForms();
      }}
      onCancel={() => setOverwriteConfirm(false)}
    />

    <ConfirmDialog
      open={signingSentWarnOpen}
      title="Unterlagen bereits versendet"
      message={signingStatus === 'completed'
        ? 'Die Unterlagen wurden bereits unterschrieben zurückgemeldet. Möchten Sie den Vorgang wirklich wiederholen und die Dauer-Dokumente neu erzeugen? Anschließend müssten Sie erneut zur Unterschrift senden.'
        : 'Die Dokumente wurden bereits zur Unterschrift an den Kunden versendet. Möchten Sie den Vorgang wiederholen und die Dauer-Dokumente neu erzeugen? Anschließend müssten Sie erneut zur Unterschrift senden.'}
      confirmLabel="Wiederholen"
      onConfirm={async () => {
        setSigningSentWarnOpen(false);
        // Vorgang wiederholen: Versand zurücksetzen (Anfrage löschen), damit die
        // "zum Unterzeichnen"-Liste wieder erscheint, dann Formulare neu erzeugen.
        if (reportId) { try { await api.delete(`/api/reports/${reportId}/signing-request`); } catch { /* ignore */ } }
        setSigningStatus('none');
        await doGenerateBafaForms();
      }}
      onCancel={() => setSigningSentWarnOpen(false)}
    />

    <ConfirmDialog
      open={alertMsg !== null}
      title="Hinweis"
      message={alertMsg || ''}
      confirmLabel="OK"
      hideCancel
      onConfirm={() => setAlertMsg(null)}
      onCancel={() => setAlertMsg(null)}
    />

    <ReportAnalysisModal
      open={analysisOpen}
      onClose={() => setAnalysisOpen(false)}
      reportId={reportId}
      darkMode={darkMode}
      onAccepted={handleRevisionAccepted}
    />
    </>
  );
}
